import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Package,
  Plus,
  Activity,
  ShieldAlert,
  Warehouse,
  AlertTriangle,
  Users,
  RefreshCw,
  Search,
  Filter,
  ClipboardList,
  Truck,
  Store,
  BarChart3,
  ThermometerSun,
  Droplets,
  MapPin,
  Scale,
  Phone,
  Mail,
  Star,
  ArrowLeft,
  Edit,
  Trash2,
  Power,
  CreditCard,
  X,
  Download,
  ChefHat,
  Link2,
  Eye,
  DollarSign,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { showToast } from "../utils/toast";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";

const currencyFormatter = {
  format: (value = 0) =>
    `${new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(value) || 0)} FCFA`,
};

const preciseCurrencyFormatter = {
  format: (value = 0) =>
    `${new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0)} FCFA`,
};

const numberFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const tabs = [
  { id: "overview", label: "Vue d'ensemble", icon: BarChart3 },
  { id: "inventory", label: "Produits & Inventaire", icon: Warehouse },
  { id: "consumption", label: "Consommation", icon: Activity },
  { id: "entries", label: "Entrées & commandes", icon: Truck },
  { id: "suppliers", label: "Fournisseurs", icon: Users },
  { id: "warehouses", label: "Entrepôts", icon: Store },
  { id: "settings", label: "Paramètres", icon: ClipboardList },
];

const PURCHASE_ORDER_STATUSES = [
  { id: "pending", label: "En attente", pill: "bg-amber-500/15 text-amber-300" },
  { id: "confirmed", label: "Confirmé", pill: "bg-cyan-500/15 text-cyan-300" },
  { id: "on_the_way", label: "En chemin", pill: "bg-blue-500/15 text-blue-300" },
  { id: "delivered", label: "Livré", pill: "bg-emerald-500/15 text-emerald-300" },
  { id: "stored", label: "Stocké", pill: "bg-purple-500/15 text-purple-300" },
];

const EMPTY_PURCHASE_COUNTS = {
  all: 0,
  pending: 0,
  confirmed: 0,
  on_the_way: 0,
  delivered: 0,
  stored: 0,
  trash: 0,
};

const PURCHASE_STATUS_LOOKUP = PURCHASE_ORDER_STATUSES.reduce((acc, status) => {
  acc[status.id] = status;
  return acc;
}, {});

// Must match the SQLite CHECK constraint in `warehouses.type`.
// "Magasin principal" uniqueness is enforced by the UI + server-side guardrails.
const WAREHOUSE_TYPE_OPTIONS = [
  { value: "primary", label: "Magasin principal" },
  { value: "dry_storage", label: "Réserve sèche" },
  { value: "cold_storage", label: "Réfrigérateur / chambre froide" },
  { value: "freezer", label: "Congélateur" },
  { value: "secondary", label: "Dépôt secondaire" },
];

const PACKAGING_UNIT_OPTIONS = [
  { value: "piece", label: "Pièce" },
  { value: "carton", label: "Carton" },
  { value: "caissier", label: "Caissier" },
  { value: "pack", label: "Pack" },
  { value: "sachet", label: "Sachet" },
  { value: "bouteille", label: "Bouteille" },
  { value: "fut", label: "Fût" },
  { value: "palette", label: "Palette" },
  { value: "other", label: "Autre" },
];

const SUPPLIER_FORM_DEFAULT = {
  entity_type: "person",
  civility: "M.",
  first_name: "",
  last_name: "",
  company_name: "",
  manager_name: "",
  supplier_category: "ingredient",
  city: "",
  address: "",
  country: "Côte d'Ivoire",
  phone: "",
  email: "",
  payment_mode: "mobile_money",
  account_number: "",
  tax_number: "",
  bank_details: "",
  description: "",
  payment_terms: 30,
  rating: 4,
  is_active: true,
};

const STOCK_ITEM_FORM_DEFAULT = {
  name: "",
  sku: "",
  category: "",
  custom_category: "",
  category_mode: "existing",
  description: "",
  unit_id: "",
  storage_unit_id: "",
  consumption_unit_id: "",
  warehouse_id: "",
  minimum_stock: "",
  maximum_stock: "",
  reorder_point: "",
  reorder_quantity: "",
  cost_price: "",
  selling_price: "",
  supplier_id: "",
  supplier_ids: [],
  has_multiple_suppliers: false,
  shelf_life_value: "",
  shelf_life_unit: "days",
  is_perishable: false,
  track_expiry: false,
};

const buildPurchaseOrderFormDefault = () => ({
  supplier_id: "",
  created_at: new Date().toISOString(),
  status: "pending",
  scheduled_date: "",
  scheduled_time: "",
  arrival_date: "",
  arrival_time: "",
  description: "",
  warehouse_id: "",
});

const WAREHOUSE_FORM_DEFAULT = {
  name: "",
  code: "",
  type: "primary",
  type_mode: "preset",
  custom_type: "",
  location: "",
  location_mode: "preset",
  custom_location: "",
  description: "",
  is_active: true,
  temperature_target: "",
  capacity_max: "",
  parent_id: "",
};

const PAYMENT_MODE_LABELS = {
  mobile_money: "Mobile Money",
  cash: "Espèces",
  transfer: "Virement",
  cheque: "Chèque",
};

const OVERVIEW_PERIOD_OPTIONS = [
  { id: "today", label: "Aujourd'hui" },
  { id: "yesterday", label: "Hier" },
  { id: "last7", label: "7 jours" },
  { id: "month", label: "Ce mois" },
  { id: "custom", label: "Personnalisé" },
];

const normalizeOverviewPeriod = (value) => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (OVERVIEW_PERIOD_OPTIONS.some((option) => option.id === raw)) return raw;
  if (raw === "7days" || raw === "7_days" || raw === "7d") return "last7";
  if (raw === "this_month") return "month";
  return "today";
};

const toDateInputValue = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const WaiseInterface = () => {
  const getPreferredDark = () => {
    if (typeof document === "undefined") return false;
    const docHasDark = document.documentElement.classList.contains("dark");
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem("theme") : null;
    const media = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false;
    return stored ? stored === "dark" : docHasDark || media;
  };

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshingDashboard, setIsRefreshingDashboard] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const [warehousesState, setWarehousesState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [suppliersState, setSuppliersState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [settingsState, setSettingsState] = useState({
    data: null,
    loading: false,
    error: null,
    loaded: false,
  });
  const [inventorySection, setInventorySection] = useState(null);
  const [catalogTab, setCatalogTab] = useState("products");
  const [isDarkMode, setIsDarkMode] = useState(getPreferredDark);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showProductSupplierModal, setShowProductSupplierModal] = useState(false);
  const [productSupplierTarget, setProductSupplierTarget] = useState(null);
  const [productSupplierSelection, setProductSupplierSelection] = useState([]);
  const [productSupplierLoading, setProductSupplierLoading] = useState(false);
  const [productSupplierSaving, setProductSupplierSaving] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ ...SUPPLIER_FORM_DEFAULT });
  const [isSavingSupplier, setIsSavingSupplier] = useState(false);
  const [showPurchaseOrderModal, setShowPurchaseOrderModal] = useState(false);
  const [purchaseOrderForm, setPurchaseOrderForm] = useState(buildPurchaseOrderFormDefault());
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState("");
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [purchaseOrdersState, setPurchaseOrdersState] = useState({
    items: [],
    counts: { ...EMPTY_PURCHASE_COUNTS },
    loading: false,
    error: null,
  });
  const [purchaseOrdersFilter, setPurchaseOrdersFilter] = useState("all");
  const [purchaseOrdersSearch, setPurchaseOrdersSearch] = useState("");
  const [isSavingPurchaseOrder, setIsSavingPurchaseOrder] = useState(false);
  const [updatingPurchaseOrderId, setUpdatingPurchaseOrderId] = useState(null);
  const [supplierCatalogState, setSupplierCatalogState] = useState({
    items: [],
    loading: false,
    error: null,
  });
  const [showSupplierCatalog, setShowSupplierCatalog] = useState(false);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageModalOrder, setStorageModalOrder] = useState(null);
  const [storageMode, setStorageMode] = useState("auto");
  const [storageItemsForm, setStorageItemsForm] = useState([]);
  const [isSavingStorage, setIsSavingStorage] = useState(false);
  const [storageModalLoading, setStorageModalLoading] = useState(false);
  const [purchasePriceConfirmation, setPurchasePriceConfirmation] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [recipeTarget, setRecipeTarget] = useState(null);
  const [recipeForm, setRecipeForm] = useState({ name: "", sku: "", produced_quantity: "1" });
  const [recipeItems, setRecipeItems] = useState([]);
  const [isSavingRecipe, setIsSavingRecipe] = useState(false);
  const [isDeletingRecipe, setIsDeletingRecipe] = useState(false);
  const [recipeHasExisting, setRecipeHasExisting] = useState(false);
  const [recipeModalLoading, setRecipeModalLoading] = useState(false);
  const [currentRecipeId, setCurrentRecipeId] = useState(null);
  const [recipeModalReadOnly, setRecipeModalReadOnly] = useState(false);
  const [deletingRecipeId, setDeletingRecipeId] = useState(null);
  const [editingPurchaseOrder, setEditingPurchaseOrder] = useState(null);
  const [deletingPurchaseOrderId, setDeletingPurchaseOrderId] = useState(null);
  const [ingredientsState, setIngredientsState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [consumablesState, setConsumablesState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [resoldStockState, setResoldStockState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [unitsState, setUnitsState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [inventoriesState, setInventoriesState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [valuationState, setValuationState] = useState({
    data: null,
    loading: false,
    error: null,
    loaded: false,
  });
  const [valuationWarehouseFilter, setValuationWarehouseFilter] = useState("");
  const [lossTypesState, setLossTypesState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [lossCausesState, setLossCausesState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [lossesState, setLossesState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [lossWarehouseFilter, setLossWarehouseFilter] = useState("");
  const [lossForm, setLossForm] = useState({
    item_key: "",
    loss_type_id: "",
    loss_cause_id: "",
    quantity: "",
    notes: "",
  });
  const [isSubmittingLoss, setIsSubmittingLoss] = useState(false);
  const [reconciliationView, setReconciliationView] = useState("list");
  const [newInventoryWarehouseId, setNewInventoryWarehouseId] = useState("");
  const [isCreatingInventory, setIsCreatingInventory] = useState(false);
  const [activeInventoryId, setActiveInventoryId] = useState(null);
  const [activeInventoryDetail, setActiveInventoryDetail] = useState(null);
  const [activeInventoryLoading, setActiveInventoryLoading] = useState(false);
  const [countableItemsState, setCountableItemsState] = useState({
    data: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const [countInputs, setCountInputs] = useState({});
  const [isFinalizingInventory, setIsFinalizingInventory] = useState(false);
  const [isCancellingInventory, setIsCancellingInventory] = useState(false);
  const defaultAlertSettings = {
    enableLowStock: true,
    defaultMinThreshold: 10,
    enableSaleCapacity: true,
    saleCapacityThreshold: 5,
    enableNearZeroCapacity: true,
    enableRecipeUnusable: true,
    enableNegativeMargin: true,
    dormantProductDays: 10,
  };
  const [alertSettings, setAlertSettings] = useState({ ...defaultAlertSettings });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsSection, setSettingsSection] = useState(null);
  const [showMinThresholdModal, setShowMinThresholdModal] = useState(false);
  const [showSaleCapacityModal, setShowSaleCapacityModal] = useState(false);
  const [showDormantDaysModal, setShowDormantDaysModal] = useState(false);
  const [minThresholdInput, setMinThresholdInput] = useState(10);
  const [saleCapacityInput, setSaleCapacityInput] = useState(5);
  const [dormantDaysInput, setDormantDaysInput] = useState(10);
  const [overviewDefaultPeriod, setOverviewDefaultPeriod] = useState("today");
  const [overviewDefaultPeriodInput, setOverviewDefaultPeriodInput] = useState("today");
  const [resoldPeriodFilter, setResoldPeriodFilter] = useState("today");
  const [kitchenPeriodFilter, setKitchenPeriodFilter] = useState("today");
  const [consumablesPeriodFilter, setConsumablesPeriodFilter] = useState("today");
  const [resoldCustomStart, setResoldCustomStart] = useState("");
  const [resoldCustomEnd, setResoldCustomEnd] = useState("");
  const [kitchenCustomStart, setKitchenCustomStart] = useState("");
  const [kitchenCustomEnd, setKitchenCustomEnd] = useState("");
  const [consumablesCustomStart, setConsumablesCustomStart] = useState("");
  const [consumablesCustomEnd, setConsumablesCustomEnd] = useState("");
  const [overviewPeriodsInitialized, setOverviewPeriodsInitialized] = useState(false);
  const [lastAlertsError, setLastAlertsError] = useState(null);
  const [showSimulatorModal, setShowSimulatorModal] = useState(false);
  const [simulatorMode, setSimulatorMode] = useState("existing");
  const [simulatorForm, setSimulatorForm] = useState({ recipeId: "", quantity: "1", produced_quantity: "1", name: "" });
  const [simulatorItems, setSimulatorItems] = useState([]);
  const [simulatorResult, setSimulatorResult] = useState(null);
  const [showStockItemModal, setShowStockItemModal] = useState(false);
  const [stockModalType, setStockModalType] = useState("ingredient");
  const [editingStockItem, setEditingStockItem] = useState(null);
  const [stockItemForm, setStockItemForm] = useState({ ...STOCK_ITEM_FORM_DEFAULT });
  const [isSavingStockItem, setIsSavingStockItem] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [warehouseForm, setWarehouseForm] = useState({ ...WAREHOUSE_FORM_DEFAULT });
  const [isSavingWarehouse, setIsSavingWarehouse] = useState(false);
  const [recipesState, setRecipesState] = useState({
    items: [],
    loading: false,
    error: null,
    loaded: false,
  });
  const hasLoadedDashboardRef = useRef(false);
  const lastWaiseCloudSyncRef = useRef(0);

  const openProductSupplierModal = async (item) => {
    if (!item?.productId) return;
    setProductSupplierTarget(item);
    setShowProductSupplierModal(true);
    setProductSupplierSelection([]);
    setProductSupplierLoading(true);
    try {
      if (!suppliersState.loaded && !suppliersState.loading) {
        await fetchSuppliers();
      }
      const res = await fetch(`/api/waise/product-suppliers?product_id=${item.productId}`);
      if (res.ok) {
        const data = await res.json();
        const ids = Array.isArray(data?.supplier_ids) ? data.supplier_ids.map(String) : [];
        setProductSupplierSelection(ids);
      }
    } catch (error) {
      console.error("❌ Erreur fournisseurs produit:", error);
      showToast("error", "Impossible de charger les fournisseurs liés.");
    } finally {
      setProductSupplierLoading(false);
    }
  };

  const toggleProductSupplierSelection = (supplierId) => {
    setProductSupplierSelection((prev) => {
      const exists = prev.includes(supplierId);
      return exists ? prev.filter((id) => id !== supplierId) : [...prev, supplierId];
    });
  };

  const handleSaveProductSuppliers = async () => {
    if (!productSupplierTarget?.productId) return;
    setProductSupplierSaving(true);
    try {
      const res = await fetch(`/api/waise/product-suppliers/${productSupplierTarget.productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ supplier_ids: productSupplierSelection }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Impossible d'enregistrer les fournisseurs.");
      }
      showToast("success", "Fournisseurs liés au produit.");
      setShowProductSupplierModal(false);
    } catch (error) {
      console.error("❌ Erreur liaison fournisseurs produit:", error);
      showToast("error", error.message || "Erreur lors de la liaison.");
    } finally {
      setProductSupplierSaving(false);
    }
  };
  const stockCategories = useMemo(() => {
    const items = [...(ingredientsState.data || []), ...(consumablesState.data || [])];
    const categories = new Set(
      items
        .map((item) => (item.category || "").trim())
        .filter((cat) => cat.length > 0),
    );
    return Array.from(categories).sort((a, b) => a.localeCompare(b, "fr"));
  }, [ingredientsState.data, consumablesState.data]);

  const warehouseLocations = useMemo(() => {
    const entries = (warehousesState.data || []).map((w) => (w.location || "").trim()).filter(Boolean);
    return Array.from(new Set(entries)).sort((a, b) => a.localeCompare(b, "fr"));
  }, [warehousesState.data]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(getPreferredDark());
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    syncTheme();
    const media = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    media?.addEventListener("change", syncTheme);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setBusinessType(extractBusinessType(data));
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          }
          return;
        }
      } catch (_error) {
        // ignore and fallback to cache
      }
      try {
        const cached = localStorage.getItem("restaurant-info");
        if (cached) {
          const parsed = JSON.parse(cached);
          setBusinessType(extractBusinessType(parsed));
        }
      } catch (_error) {
        // ignore
      }
    };
    loadBusinessType();
  }, []);

  useEffect(() => {
    if (!isBoutique) return;
    if (inventorySection === "ingredients" || inventorySection === "consumables" || inventorySection === "resold") {
      setInventorySection("catalog");
    }
    if (catalogTab !== "products") {
      setCatalogTab("products");
    }
  }, [isBoutique, inventorySection, catalogTab]);

  const fetchDashboard = useCallback(async () => {
    const showBlockingLoader = !hasLoadedDashboardRef.current;
    try {
      if (showBlockingLoader) {
        setLoading(true);
      } else {
        setIsRefreshingDashboard(true);
      }
      setError(null);
      const now = Date.now();
      if (now - lastWaiseCloudSyncRef.current > 30000) {
        lastWaiseCloudSyncRef.current = now;
        fetch("/api/cloud/sync/manual", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scope: "waise" }),
        }).catch(() => {
          // Sync cloud best-effort: Waise local ne doit jamais etre bloque.
        });
      }
      const params = new URLSearchParams();
      params.set("resoldPeriod", resoldPeriodFilter);
      params.set("kitchenPeriod", kitchenPeriodFilter);
      params.set("consumablesPeriod", consumablesPeriodFilter);
      if (resoldPeriodFilter === "custom") {
        if (resoldCustomStart) params.set("resoldStart", resoldCustomStart);
        if (resoldCustomEnd) params.set("resoldEnd", resoldCustomEnd);
      }
      if (kitchenPeriodFilter === "custom") {
        if (kitchenCustomStart) params.set("kitchenStart", kitchenCustomStart);
        if (kitchenCustomEnd) params.set("kitchenEnd", kitchenCustomEnd);
      }
      if (consumablesPeriodFilter === "custom") {
        if (consumablesCustomStart) {
          params.set("consumablesStart", consumablesCustomStart);
        }
        if (consumablesCustomEnd) params.set("consumablesEnd", consumablesCustomEnd);
      }
      const query = params.toString();
      const response = await fetch(`/api/waise/dashboard${query ? `?${query}` : ""}`);
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(
          errorPayload.error || "Impossible de charger les données Waise",
        );
      }
      const data = await response.json();
      setDashboard(data);
      hasLoadedDashboardRef.current = true;
    } catch (err) {
      console.error("❌ Erreur Waise:", err);
      setError(err.message);
      showToast("error", err.message || "Erreur inconnue");
    } finally {
      if (showBlockingLoader) {
        setLoading(false);
      } else {
        setIsRefreshingDashboard(false);
      }
    }
  }, [
    resoldPeriodFilter,
    kitchenPeriodFilter,
    consumablesPeriodFilter,
    resoldCustomStart,
    resoldCustomEnd,
    kitchenCustomStart,
    kitchenCustomEnd,
    consumablesCustomStart,
    consumablesCustomEnd,
  ]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const fetchWarehouses = useCallback(async () => {
    setWarehousesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/warehouses");
      if (!response.ok) {
        throw new Error("Impossible de charger les entrepôts");
      }
      const data = await response.json();
      setWarehousesState({
        data,
        loading: false,
        error: null,
        loaded: true,
      });
    } catch (err) {
      console.error("❌ Erreur entrepôts:", err);
      setWarehousesState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger les entrepôts");
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    setSuppliersState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/suppliers");
      if (!response.ok) {
        throw new Error("Impossible de charger les fournisseurs");
      }
      const data = await response.json();
      setSuppliersState({
        data,
        loading: false,
        error: null,
        loaded: true,
      });
    } catch (err) {
      console.error("❌ Erreur fournisseurs:", err);
      setSuppliersState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger les fournisseurs");
    }
  }, []);

  useEffect(() => {
    if (!showStockItemModal || editingStockItem || stockItemForm.supplier_id) {
      return;
    }
    const general = (suppliersState.data || []).find(
      (supplier) =>
        supplier?.is_general_supplier ||
        ["général", "general"].includes(
          String(supplier?.name || "").trim().toLowerCase(),
        ),
    );
    if (!general?.id) return;
    setStockItemForm((previous) => ({
      ...previous,
      supplier_id: String(general.id),
    }));
  }, [
    editingStockItem,
    showStockItemModal,
    stockItemForm.supplier_id,
    suppliersState.data,
  ]);

  const fetchRecipesList = useCallback(async () => {
    setRecipesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/recipes");
      if (!response.ok) {
        throw new Error("Impossible de charger les recettes");
      }
      const data = await response.json();
      setRecipesState({ items: data, loading: false, error: null, loaded: true });
    } catch (err) {
      console.error("❌ Erreur recettes:", err);
      setRecipesState((prev) => ({ ...prev, loading: false, error: err.message || "Erreur inconnue", loaded: true }));
      showToast("error", err.message || "Impossible de charger les recettes");
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    setUnitsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/units");
      if (!response.ok) {
        throw new Error("Impossible de charger les unités de mesure");
      }
      const data = await response.json();
      setUnitsState({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      console.error("❌ Erreur unités:", err);
      setUnitsState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger les unités");
    }
  }, []);

  const fetchStockItems = useCallback(async (type) => {
    const setter = type === "consumable" ? setConsumablesState : setIngredientsState;
    setter((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch(`/api/waise/stock-items?type=${type}`);
      if (!response.ok) {
        throw new Error("Impossible de charger les articles de stock");
      }
      const data = await response.json();
      setter({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      console.error("❌ Erreur articles de stock:", err);
      setter((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger les articles");
    }
  }, []);

  const fetchResoldStockItems = useCallback(async () => {
    setResoldStockState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/stock-items?inventory_kind=resold");
      if (!response.ok) {
        throw new Error("Impossible de charger les produits revendus");
      }
      const data = await response.json();
      setResoldStockState({
        data: Array.isArray(data) ? data : [],
        loading: false,
        error: null,
        loaded: true,
      });
    } catch (err) {
      console.error("❌ Erreur produits revendus:", err);
      setResoldStockState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger les produits revendus");
    }
  }, []);

  const fetchInventories = useCallback(async () => {
    setInventoriesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/inventories");
      if (!response.ok) {
        throw new Error("Impossible de charger les inventaires");
      }
      const data = await response.json();
      setInventoriesState({
        data: Array.isArray(data) ? data : [],
        loading: false,
        error: null,
        loaded: true,
      });
    } catch (err) {
      console.error("❌ Erreur inventaires:", err);
      setInventoriesState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger les inventaires");
    }
  }, []);

  const fetchStockValuation = useCallback(async (warehouseId) => {
    setValuationState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const query = warehouseId ? `?warehouse_id=${warehouseId}` : "";
      const response = await fetch(`/api/waise/stock-valuation${query}`);
      if (!response.ok) {
        throw new Error("Impossible de charger la valorisation du stock");
      }
      const data = await response.json();
      setValuationState({
        data,
        loading: false,
        error: null,
        loaded: true,
      });
    } catch (err) {
      console.error("❌ Erreur valorisation stock:", err);
      setValuationState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger la valorisation du stock");
    }
  }, []);

  const fetchLossTypes = useCallback(async () => {
    setLossTypesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/loss-types");
      if (!response.ok) {
        throw new Error("Impossible de charger les types de perte");
      }
      const data = await response.json();
      setLossTypesState({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      console.error("❌ Erreur types de perte:", err);
      setLossTypesState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
    }
  }, []);

  const fetchLossCauses = useCallback(async () => {
    setLossCausesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/loss-causes");
      if (!response.ok) {
        throw new Error("Impossible de charger les causes de perte");
      }
      const data = await response.json();
      setLossCausesState({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      console.error("❌ Erreur causes de perte:", err);
      setLossCausesState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
    }
  }, []);

  const fetchLosses = useCallback(async (warehouseId) => {
    setLossesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const query = warehouseId ? `?warehouse_id=${warehouseId}` : "";
      const response = await fetch(
        `/api/waise/stock-movements?movement_type=loss${query ? `&${query.slice(1)}` : ""}`,
      );
      if (!response.ok) {
        throw new Error("Impossible de charger l'historique des pertes");
      }
      const data = await response.json();
      setLossesState({ data, loading: false, error: null, loaded: true });
    } catch (err) {
      console.error("❌ Erreur historique pertes:", err);
      setLossesState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger l'historique des pertes");
    }
  }, []);

  const submitLossDeclaration = useCallback(async () => {
    const [stockItemIdRaw, warehouseIdRaw] = (lossForm.item_key || "").split("::");
    const stockItemId = parseInt(stockItemIdRaw, 10);
    const warehouseId = parseInt(warehouseIdRaw, 10);
    const quantity = parseFloat(lossForm.quantity);

    if (!stockItemId || !warehouseId) {
      showToast("error", "Sélectionnez un article et un entrepôt.");
      return;
    }
    if (!lossForm.loss_type_id) {
      showToast("error", "Sélectionnez un type de perte.");
      return;
    }
    if (!quantity || quantity <= 0) {
      showToast("error", "Indiquez une quantité valide.");
      return;
    }

    const valuationItems = valuationState.data?.items || [];
    const matchedItem = valuationItems.find(
      (row) => row.stock_item_id === stockItemId && row.warehouse_id === warehouseId,
    );
    if (!matchedItem) {
      showToast("error", "Article introuvable dans le stock actuel. Actualisez et réessayez.");
      return;
    }
    if (quantity > matchedItem.quantity + 0.0001) {
      showToast(
        "error",
        `Quantité supérieure au stock disponible (${matchedItem.quantity} ${matchedItem.unit_symbol || ""}).`,
      );
      return;
    }
    const unitCost = matchedItem.unit_cost;
    const lossType = (lossTypesState.data || []).find(
      (t) => String(t.id) === String(lossForm.loss_type_id),
    );
    const lossCause = (lossCausesState.data || []).find(
      (c) => String(c.id) === String(lossForm.loss_cause_id),
    );

    const notesParts = [];
    if (lossCause) notesParts.push(`Cause : ${lossCause.name}`);
    if (lossForm.notes && lossForm.notes.trim()) notesParts.push(lossForm.notes.trim());

    let currentUserId = null;
    try {
      const storedUser = JSON.parse(localStorage.getItem("moo_user") || "{}");
      currentUserId = storedUser?.id || null;
    } catch {
      currentUserId = null;
    }
    if (!currentUserId) {
      showToast("error", "Utilisateur non identifié. Veuillez vous reconnecter.");
      return;
    }

    setIsSubmittingLoss(true);
    try {
      const response = await fetch("/api/waise/stock-movements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stock_item_id: stockItemId,
          warehouse_id: warehouseId,
          movement_type: "loss",
          quantity,
          unit_cost: unitCost,
          reference_type: "loss",
          reason: lossType ? lossType.name : "Perte",
          notes: notesParts.join(" — ") || null,
          performed_by: currentUserId,
        }),
      });
      if (!response.ok) {
        throw new Error("Impossible d'enregistrer la perte");
      }
      showToast("success", "Perte enregistrée");
      setLossForm({
        item_key: "",
        loss_type_id: "",
        loss_cause_id: "",
        quantity: "",
        notes: "",
      });
      await fetchLosses(lossWarehouseFilter || null);
      await fetchStockValuation(valuationWarehouseFilter || null);
    } catch (err) {
      console.error("❌ Erreur déclaration perte:", err);
      showToast("error", err.message || "Impossible d'enregistrer la perte");
    } finally {
      setIsSubmittingLoss(false);
    }
  }, [
    lossForm,
    valuationState.data,
    lossTypesState.data,
    lossCausesState.data,
    lossWarehouseFilter,
    valuationWarehouseFilter,
    fetchLosses,
    fetchStockValuation,
  ]);

  const fetchInventoryDetail = useCallback(async (inventoryId) => {
    setActiveInventoryLoading(true);
    try {
      const response = await fetch(`/api/waise/inventories/${inventoryId}`);
      if (!response.ok) {
        throw new Error("Impossible de charger l'inventaire");
      }
      const data = await response.json();
      setActiveInventoryDetail(data);
    } catch (err) {
      console.error("❌ Erreur détail inventaire:", err);
      showToast("error", err.message || "Impossible de charger l'inventaire");
    } finally {
      setActiveInventoryLoading(false);
    }
  }, []);

  const fetchCountableItems = useCallback(async (warehouseId) => {
    if (!warehouseId) return;
    setCountableItemsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      // Endpoint dédié au comptage à l'aveugle : ne renvoie jamais la quantité
      // théorique ni la valeur, contrairement à /stock-levels.
      const response = await fetch(
        `/api/waise/inventories/countable-items?warehouse_id=${warehouseId}`,
      );
      if (!response.ok) {
        throw new Error("Impossible de charger les articles à compter");
      }
      const data = await response.json();
      setCountableItemsState({
        data: Array.isArray(data) ? data : [],
        loading: false,
        error: null,
        loaded: true,
      });
    } catch (err) {
      console.error("❌ Erreur articles à compter:", err);
      setCountableItemsState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
      }));
      showToast("error", err.message || "Impossible de charger les articles à compter");
    }
  }, []);

  const handleCreateInventory = async () => {
    if (!newInventoryWarehouseId) {
      showToast("error", "Choisissez un entrepôt pour démarrer l'inventaire.");
      return;
    }
    setIsCreatingInventory(true);
    try {
      const response = await fetch("/api/waise/inventories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ warehouse_id: Number(newInventoryWarehouseId) }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Impossible de créer l'inventaire");
      }
      const result = await response.json();
      showToast("success", "Inventaire démarré.");
      setCountInputs({});
      setActiveInventoryId(result.id);
      setReconciliationView("count");
      await Promise.all([
        fetchInventoryDetail(result.id),
        fetchCountableItems(newInventoryWarehouseId),
        fetchInventories(),
      ]);
    } catch (err) {
      console.error("❌ Erreur création inventaire:", err);
      showToast("error", err.message || "Impossible de créer l'inventaire");
    } finally {
      setIsCreatingInventory(false);
    }
  };

  const handleOpenInventory = async (inventory) => {
    setActiveInventoryId(inventory.id);
    setCountInputs({});
    await fetchInventoryDetail(inventory.id);
    if (inventory.status === "completed") {
      setReconciliationView("detail");
    } else {
      await fetchCountableItems(inventory.warehouse_id);
      setReconciliationView("count");
    }
  };

  const handleSaveCount = async (stockItemId, value, notes) => {
    if (!activeInventoryId) return;
    const numericValue = Number(value);
    if (value === "" || value === null || !Number.isFinite(numericValue)) {
      showToast("error", "Saisissez une quantité comptée valide.");
      return;
    }
    if (numericValue < 0) {
      showToast("error", "La quantité comptée doit être positive ou nulle.");
      return;
    }
    setCountInputs((prev) => ({
      ...prev,
      [stockItemId]: { ...(prev[stockItemId] || {}), value, notes, saving: true },
    }));
    try {
      const response = await fetch(`/api/waise/inventories/${activeInventoryId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stock_item_id: stockItemId,
          counted_quantity: numericValue,
          notes: notes || null,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Impossible d'enregistrer le comptage");
      }
      setCountInputs((prev) => ({
        ...prev,
        [stockItemId]: { ...(prev[stockItemId] || {}), value, notes, saving: false, saved: true },
      }));
      await fetchInventoryDetail(activeInventoryId);
    } catch (err) {
      console.error("❌ Erreur comptage:", err);
      showToast("error", err.message || "Impossible d'enregistrer le comptage");
      setCountInputs((prev) => ({
        ...prev,
        [stockItemId]: { ...(prev[stockItemId] || {}), saving: false },
      }));
    }
  };

  const handleDeleteCountedItem = async (stockItemId) => {
    if (!activeInventoryId) return;
    try {
      const response = await fetch(
        `/api/waise/inventories/${activeInventoryId}/items/${stockItemId}`,
        { method: "DELETE" },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Impossible de retirer cette ligne");
      }
      setCountInputs((prev) => {
        const next = { ...prev };
        delete next[stockItemId];
        return next;
      });
      await fetchInventoryDetail(activeInventoryId);
      showToast("success", "Ligne retirée du comptage.");
    } catch (err) {
      console.error("❌ Erreur suppression ligne comptage:", err);
      showToast("error", err.message || "Impossible de retirer cette ligne");
    }
  };

  const handleFinalizeInventory = async () => {
    if (!activeInventoryId) return;
    setIsFinalizingInventory(true);
    try {
      const response = await fetch(`/api/waise/inventories/${activeInventoryId}/finalize`, {
        method: "POST",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Impossible de finaliser l'inventaire");
      }
      const result = await response.json();
      showToast(
        "success",
        `Inventaire finalisé : ${result.adjustments_created} ajustement(s) sur ${result.counted_items ?? result.total_items} article(s) compté(s).`,
      );
      await fetchInventoryDetail(activeInventoryId);
      await fetchInventories();
      setReconciliationView("detail");
    } catch (err) {
      console.error("❌ Erreur finalisation inventaire:", err);
      showToast("error", err.message || "Impossible de finaliser l'inventaire");
    } finally {
      setIsFinalizingInventory(false);
    }
  };

  const handleCancelInventory = async () => {
    if (!activeInventoryId) return;
    if (!window.confirm("Annuler cet inventaire ? Le comptage en cours sera abandonné.")) {
      return;
    }
    setIsCancellingInventory(true);
    try {
      const response = await fetch(`/api/waise/inventories/${activeInventoryId}/cancel`, {
        method: "POST",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Impossible d'annuler l'inventaire");
      }
      showToast("success", "Inventaire annulé.");
      setActiveInventoryId(null);
      setActiveInventoryDetail(null);
      setReconciliationView("list");
      await fetchInventories();
    } catch (err) {
      console.error("❌ Erreur annulation inventaire:", err);
      showToast("error", err.message || "Impossible d'annuler l'inventaire");
    } finally {
      setIsCancellingInventory(false);
    }
  };

  const updateRecipePresence = useCallback(
    (targetId, type, hasRecipe) => {
      if (targetId === null || targetId === undefined) {
        return;
      }
      const normalizedId = String(targetId);
      setDashboard((prev) => {
        if (!prev) return prev;
        let catalogChanged = false;
        let extrasChanged = false;
        let nextTrackedCatalog = prev.trackedCatalog;
        let nextTrackedExtras = prev.trackedExtras;

        if (type === "product" && prev.trackedCatalog?.items?.length) {
          const updatedItems = prev.trackedCatalog.items.map((item) => {
            const matchesProduct =
              (item.productId !== undefined && String(item.productId) === normalizedId) ||
              (typeof item.id === "string" && item.id.endsWith(`-${normalizedId}`));
            if (matchesProduct && item.hasRecipe !== hasRecipe) {
              catalogChanged = true;
              return { ...item, hasRecipe };
            }
            return item;
          });
          if (catalogChanged) {
            nextTrackedCatalog = { ...prev.trackedCatalog, items: updatedItems };
          }
        }

        if (type === "extra" && Array.isArray(prev.trackedExtras)) {
          const updatedExtras = prev.trackedExtras.map((extra) => {
            if (String(extra.id) === normalizedId && extra.hasRecipe !== hasRecipe) {
              extrasChanged = true;
              return { ...extra, hasRecipe };
            }
            return extra;
          });
          if (extrasChanged) {
            nextTrackedExtras = updatedExtras;
          }
        }

        if (!catalogChanged && !extrasChanged) {
          return prev;
        }

        return {
          ...prev,
          trackedCatalog: nextTrackedCatalog,
          trackedExtras: nextTrackedExtras,
        };
      });
    },
    [setDashboard],
  );

  const fetchSettings = useCallback(async () => {
    setSettingsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch("/api/waise/stock-settings");
      if (!response.ok) {
        throw new Error("Impossible de charger les paramètres");
      }
      const data = await response.json();
      setSettingsState({
        data,
        loading: false,
        error: null,
        loaded: true,
      });
    } catch (err) {
      console.error("❌ Erreur paramètres stock:", err);
      setSettingsState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
        loaded: true,
        data: prev.data || null,
      }));
      // Pas de popup : on affiche juste le message en entête
    }
  }, []);

  const persistAlerts = useCallback(
    async (nextState) => {
      setIsSavingSettings(true);
      setLastAlertsError(null);
      const payload = [
        { key: "alert_enable_low_stock", value: String(nextState.enableLowStock), description: "Activer l'alerte seuil minimum" },
        { key: "default_min_threshold", value: String(nextState.defaultMinThreshold ?? 0), description: "Seuil minimum global" },
        { key: "alert_enable_sale_capacity", value: String(nextState.enableSaleCapacity), description: "Alerte capacité de vente atteinte" },
        { key: "alert_sale_capacity_threshold", value: String(nextState.saleCapacityThreshold ?? 0), description: "Seuil global de capacité de vente" },
        { key: "alert_enable_near_zero_capacity", value: String(nextState.enableNearZeroCapacity), description: "Alerte capacité proche de zéro" },
        { key: "alert_enable_recipe_unusable", value: String(nextState.enableRecipeUnusable), description: "Alerte recette inutilisable" },
        { key: "alert_enable_negative_margin", value: String(nextState.enableNegativeMargin), description: "Alerte marge négative" },
        { key: "alert_dormant_product_days", value: String(nextState.dormantProductDays ?? 10), description: "Seuil de jours sans vente pour produits dormants" },
      ];
      try {
        const response = await fetch("/api/waise/stock-settings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "general", data: payload }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || "Impossible d'enregistrer les paramètres.");
        }
        showToast("success", "Paramètre d'alerte mis à jour.");
      } catch (err) {
        console.error("❌ Sauvegarde alertes:", err);
        setLastAlertsError(err.message || "Impossible d'enregistrer les paramètres.");
        showToast("error", err.message || "Impossible d'enregistrer les paramètres.");
        return false;
      } finally {
        setIsSavingSettings(false);
      }
      return true;
    },
    [],
  );

  const persistOverviewDefaults = useCallback(async (periodKey) => {
    const normalizedPeriod = normalizeOverviewPeriod(periodKey);
    setIsSavingSettings(true);
    try {
      const payload = [
        {
          key: "overview_default_period",
          value: normalizedPeriod,
          description: "Période par défaut de la vue d'ensemble Waise",
        },
      ];
      const response = await fetch("/api/waise/stock-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "general", data: payload }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible d'enregistrer la période par défaut.");
      }
      setOverviewDefaultPeriod(normalizedPeriod);
      setOverviewDefaultPeriodInput(normalizedPeriod);
      setResoldPeriodFilter(normalizedPeriod);
      setKitchenPeriodFilter(normalizedPeriod);
      setConsumablesPeriodFilter(normalizedPeriod);
      setResoldCustomStart("");
      setResoldCustomEnd("");
      setKitchenCustomStart("");
      setKitchenCustomEnd("");
      setConsumablesCustomStart("");
      setConsumablesCustomEnd("");
      setDashboard((prev) =>
        prev
          ? {
              ...prev,
              overviewV2: {
                ...(prev.overviewV2 || {}),
                filters: {
                  ...(prev.overviewV2?.filters || {}),
                  defaultPeriod: normalizedPeriod,
                },
              },
            }
          : prev,
      );
      showToast("success", "Période par défaut enregistrée.");
      return true;
    } catch (err) {
      console.error("❌ Sauvegarde période par défaut:", err);
      showToast("error", err.message || "Impossible d'enregistrer la période par défaut.");
      return false;
    } finally {
      setIsSavingSettings(false);
    }
  }, []);

  useEffect(() => {
    if (!settingsState.data) return;
    const general = settingsState.data.general || [];
    const map = new Map(general.map((entry) => [entry.setting_key, entry.setting_value]));
    const parsedOverviewDefaultPeriod = normalizeOverviewPeriod(
      map.get("overview_default_period") || "today",
    );
    setAlertSettings((prev) => ({
      ...prev,
      enableLowStock: map.get("alert_enable_low_stock") === "false" ? false : true,
      defaultMinThreshold: map.has("default_min_threshold")
        ? Number(map.get("default_min_threshold")) || 0
        : prev.defaultMinThreshold,
      enableSaleCapacity: map.get("alert_enable_sale_capacity") === "false" ? false : true,
      saleCapacityThreshold: map.has("alert_sale_capacity_threshold")
        ? Number(map.get("alert_sale_capacity_threshold")) || 0
        : prev.saleCapacityThreshold,
      enableNearZeroCapacity: map.get("alert_enable_near_zero_capacity") === "false" ? false : true,
      enableRecipeUnusable: map.get("alert_enable_recipe_unusable") === "false" ? false : true,
      enableNegativeMargin: map.get("alert_enable_negative_margin") === "false" ? false : true,
      dormantProductDays: map.has("alert_dormant_product_days")
        ? Math.max(1, Number(map.get("alert_dormant_product_days")) || 10)
        : prev.dormantProductDays,
    }));
    setMinThresholdInput(
      map.has("default_min_threshold")
        ? Number(map.get("default_min_threshold")) || 0
        : defaultAlertSettings.defaultMinThreshold,
    );
    setSaleCapacityInput(
      map.has("alert_sale_capacity_threshold")
        ? Number(map.get("alert_sale_capacity_threshold")) || 0
        : defaultAlertSettings.saleCapacityThreshold,
    );
    setDormantDaysInput(
      map.has("alert_dormant_product_days")
        ? Math.max(1, Number(map.get("alert_dormant_product_days")) || 10)
        : defaultAlertSettings.dormantProductDays,
    );
    setOverviewDefaultPeriod(parsedOverviewDefaultPeriod);
    setOverviewDefaultPeriodInput(parsedOverviewDefaultPeriod);
  }, [settingsState.data]);

  useEffect(() => {
    const serverDefaultPeriod = normalizeOverviewPeriod(
      dashboard?.overviewV2?.filters?.defaultPeriod || "today",
    );
    if (overviewPeriodsInitialized) return;
    setOverviewDefaultPeriod(serverDefaultPeriod);
    setOverviewDefaultPeriodInput(serverDefaultPeriod);
    setResoldPeriodFilter(serverDefaultPeriod);
    setKitchenPeriodFilter(serverDefaultPeriod);
    setConsumablesPeriodFilter(serverDefaultPeriod);
    setOverviewPeriodsInitialized(true);
  }, [dashboard?.overviewV2?.filters?.defaultPeriod, overviewPeriodsInitialized]);

  const computeSimulation = useCallback((items, producedQty, targetQty) => {
    const results = [];
    let blocking = [];
    items.forEach((item) => {
      const baseQty = Number(item.quantity) || 0;
      const yieldPercent = item.yield_percent !== undefined ? Number(item.yield_percent) || 100 : 100;
      const safeYield = yieldPercent > 0 ? yieldPercent : 100;
      const effectiveQty = safeYield >= 100 ? baseQty : baseQty / (safeYield / 100);
      const perUnitRequirement = producedQty > 0 ? effectiveQty / producedQty : 0;
      const needed = perUnitRequirement * targetQty;
      const stockLevels = Number(item.stock_quantity) || 0;
      const stockStorage = Number(item.storage_stock_quantity) || 0;
      const available = stockLevels + stockStorage;
      const remaining = available - needed;
      const isBlocking = remaining < 0;
      if (isBlocking) {
        blocking.push({
          name: item.name || item.stock_name,
          available,
          needed,
          missing: Math.abs(remaining),
          unit: item.unit_symbol || "",
        });
      }
      results.push({
        name: item.name || item.stock_name,
        available,
        used: needed,
        remaining,
        unit: item.unit_symbol || "",
      });
    });
    return { results, blocking };
  }, []);

  const resetSimulatorModal = () => {
    setSimulatorMode("existing");
    setSimulatorForm({ recipeId: "", quantity: "1", produced_quantity: "1", name: "" });
    setSimulatorItems([]);
    setSimulatorResult(null);
    setShowSimulatorModal(false);
  };

  const prepareSimulatorItemsFromRecipe = (recipe) =>
    (recipe?.items || []).map((item) => ({
      id: `recipe-${item.id}`,
      stock_item_id: item.stock_item_id,
      name: item.stock_name,
      quantity: item.quantity ? String(item.quantity) : "1",
      yield_percent: item.yield_percent ? String(item.yield_percent) : "100",
      unit_symbol: item.unit_symbol || "",
      stock_quantity: item.stock_quantity,
      storage_stock_quantity: item.storage_stock_quantity,
      categoryType:
        item.linked_inventory_kind === "resold"
          ? "resold"
          : item.stock_type === "consumable"
            ? "consumable"
            : "ingredient",
    }));

  const openSimulatorWithRecipe = (recipe) => {
    if (!recipe) return;
    const producedQty = recipe.produced_quantity ? Number(recipe.produced_quantity) : 1;
    const preparedItems = prepareSimulatorItemsFromRecipe(recipe);
    setSimulatorMode("existing");
    setSimulatorForm({
      recipeId: String(recipe.id),
      name: recipe.name || "",
      quantity: producedQty ? String(producedQty) : "1",
      produced_quantity: producedQty ? String(producedQty) : "1",
    });
    setSimulatorItems(preparedItems);
    setSimulatorResult(computeSimulation(preparedItems, producedQty || 1, producedQty || 1));
    setShowSimulatorModal(true);
  };

  const openSimulatorNew = () => {
    setSimulatorMode("new");
    setSimulatorForm({ recipeId: "", quantity: "1", produced_quantity: "1", name: "" });
    setSimulatorItems([]);
    setSimulatorResult(null);
    setShowSimulatorModal(true);
  };

  const handleSimulatorRecipeSelect = (recipeId) => {
    const recipe = recipesState.items.find((r) => String(r.id) === String(recipeId));
    if (recipe) {
      openSimulatorWithRecipe(recipe);
    }
  };

  const handleSimulatorFormChange = (field, value) => {
    setSimulatorForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSimulatorItem = (type, stockItemId) => {
    const source =
      type === "consumable"
        ? consumablesState.data || []
        : type === "resold"
          ? resoldStockState.data || []
          : ingredientsState.data || [];
    const stockItem = source.find((entry) => String(entry.id) === String(stockItemId));
    if (!stockItem) return;
    const exists = simulatorItems.some((item) => item.stock_item_id === stockItem.id);
    if (exists) {
      showToast("info", "Cet article est déjà dans la simulation.");
      return;
    }
    setSimulatorItems((prev) => [
      ...prev,
      {
        id: `${type}-${stockItem.id}`,
        stock_item_id: stockItem.id,
        name: stockItem.name,
        quantity: "1",
        yield_percent: "100",
        unit_symbol: stockItem.unit_symbol || stockItem.unit_name || "",
        stock_quantity: stockItem.stock_quantity,
        storage_stock_quantity: stockItem.storage_stock_quantity,
        categoryType: type,
      },
    ]);
  };

  const handleSimulatorItemChange = (itemId, field, value) => {
    setSimulatorItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, [field]: value } : item)),
    );
  };

  const removeSimulatorItem = (itemId) => {
    setSimulatorItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleRunSimulation = () => {
    const producedQty = parseDecimalInput(simulatorForm.produced_quantity) || 1;
    const targetQty = parseDecimalInput(simulatorForm.quantity) || 0;
    const { results, blocking } = computeSimulation(simulatorItems, producedQty, targetQty);
    setSimulatorResult({ results, blocking });
  };

  useEffect(() => {
    if (activeTab === "warehouses" && !warehousesState.loaded && !warehousesState.loading) {
      fetchWarehouses();
    }
    if (activeTab === "suppliers" && !suppliersState.loaded && !suppliersState.loading) {
      fetchSuppliers();
    }
    if (activeTab === "settings" && !settingsState.loaded && !settingsState.loading) {
      fetchSettings();
    }
    if (
      activeTab === "inventory" &&
      inventorySection === "catalog" &&
      catalogTab === "recipes" &&
      !recipesState.loaded &&
      !recipesState.loading
    ) {
      fetchRecipesList();
    }
    if (
      activeTab === "inventory" &&
      inventorySection === "catalog" &&
      catalogTab === "simulator"
    ) {
      if (!recipesState.loaded && !recipesState.loading) {
        fetchRecipesList();
      }
      if (!ingredientsState.loaded && !ingredientsState.loading) {
        fetchStockItems("ingredient");
      }
      if (!consumablesState.loaded && !consumablesState.loading) {
        fetchStockItems("consumable");
      }
    }
  }, [
    activeTab,
    fetchSuppliers,
    fetchWarehouses,
    fetchSettings,
    fetchRecipesList,
    fetchStockItems,
    suppliersState.loaded,
    suppliersState.loading,
    warehousesState.loaded,
    warehousesState.loading,
    settingsState.loaded,
    settingsState.loading,
    inventorySection,
    catalogTab,
    recipesState.loaded,
    recipesState.loading,
    ingredientsState.loaded,
    ingredientsState.loading,
    consumablesState.loaded,
    consumablesState.loading,
  ]);

  useEffect(() => {
    if (
      (activeTab === "entries" || showPurchaseOrderModal) &&
      !suppliersState.loaded &&
      !suppliersState.loading
    ) {
      fetchSuppliers();
    }
  }, [activeTab, showPurchaseOrderModal, suppliersState.loaded, suppliersState.loading, fetchSuppliers]);

  useEffect(() => {
    if (activeTab !== "inventory" || inventorySection !== "resold") return;
    if (!resoldStockState.loaded && !resoldStockState.loading) {
      fetchResoldStockItems();
    }
    if (!suppliersState.loaded && !suppliersState.loading) {
      fetchSuppliers();
    }
  }, [
    activeTab,
    inventorySection,
    resoldStockState.loaded,
    resoldStockState.loading,
    suppliersState.loaded,
    suppliersState.loading,
    fetchResoldStockItems,
    fetchSuppliers,
  ]);


  const trackedCatalogItems = useMemo(() => {
    if (dashboard?.trackedCatalog?.items) {
      return dashboard.trackedCatalog.items;
    }
    const fallback =
      dashboard?.menuProducts
        ?.filter(
          (product) => product.stockQuantity !== null && product.stockQuantity !== undefined,
        )
        .map((product) => ({
          id: `product-${product.id}`,
          type: "product",
          productId: product.id,
          baseName: product.name,
          name: product.name,
          category: product.category || "Sans catégorie",
          destination: product.destination || "cuisine",
          stockQuantity: product.stockQuantity,
          stockValue: product.stockValue || 0,
          unitSymbol: product.unitSymbol || null,
          price: product.price || 0,
          // Pas de données de recette dans ce chemin de repli (menuProducts) : on
          // affiche la quantité/valeur de stock connue plutôt qu'un "—" qui laisse
          // croire que la donnée n'existe pas du tout.
          saleCapacity: product.stockQuantity ?? null,
          recipeUnitCost: product.stockValue ?? null,
        })) || [];
    return fallback;
  }, [dashboard]);
  const trackedProductsCount =
    dashboard?.trackedCatalog?.totalCount || trackedCatalogItems.length;
  const trackedExtras = dashboard?.trackedExtras || [];
  const trackedExtrasCount = trackedExtras.length;
  const trackedRecipes = recipesState.items || [];
  const trackedRecipesCount = trackedRecipes.length;

  const summaryCards = useMemo(() => {
    if (!dashboard?.summary) return [];
    return [
      {
        label: "Valeur du stock",
        value: currencyFormatter.format(
          dashboard.summary.stockValueReal ?? dashboard.summary.totalValue ?? 0,
        ),
        caption: "Coût d'achat stocké + frais de livraison (bons livrés/storés)",
        icon: Package,
      },
      {
        label: "Produits suivis",
        value: numberFormatter.format(
          dashboard.summary.trackedCountX ?? dashboard.summary.trackedProducts ?? 0,
        ),
        subValue: `sur ${numberFormatter.format(dashboard.summary.trackedGlobalY || 0)}`,
        caption: `${dashboard.summary.alertCountZ ?? dashboard.summary.lowStockItems ?? 0} en alerte`,
        icon: ShieldAlert,
      },
      {
        label: "Produits dormants",
        value: numberFormatter.format(dashboard.summary.dormantProductsCount || 0),
        caption: `>= ${numberFormatter.format(dashboard.summary.dormantDays || 10)} jours sans vente`,
        icon: Activity,
      },
      {
        label: "Livraison en attente",
        value: numberFormatter.format(dashboard.summary.pendingDeliveriesCount || 0),
        caption: "Statuts avant livré",
        icon: Truck,
      },
    ];
  }, [dashboard]);

  const overviewV2 = dashboard?.overviewV2 || null;

  const handleResoldPeriodChange = useCallback((nextValue) => {
    const normalized = normalizeOverviewPeriod(nextValue);
    setResoldPeriodFilter(normalized);
    if (normalized === "custom") {
      const today = toDateInputValue(new Date());
      setResoldCustomStart((prev) => prev || today);
      setResoldCustomEnd((prev) => prev || today);
    } else {
      setResoldCustomStart("");
      setResoldCustomEnd("");
    }
  }, []);

  const handleKitchenPeriodChange = useCallback((nextValue) => {
    const normalized = normalizeOverviewPeriod(nextValue);
    setKitchenPeriodFilter(normalized);
    if (normalized === "custom") {
      const today = toDateInputValue(new Date());
      setKitchenCustomStart((prev) => prev || today);
      setKitchenCustomEnd((prev) => prev || today);
    } else {
      setKitchenCustomStart("");
      setKitchenCustomEnd("");
    }
  }, []);

  const handleConsumablesPeriodChange = useCallback((nextValue) => {
    const normalized = normalizeOverviewPeriod(nextValue);
    setConsumablesPeriodFilter(normalized);
    if (normalized === "custom") {
      const today = toDateInputValue(new Date());
      setConsumablesCustomStart((prev) => prev || today);
      setConsumablesCustomEnd((prev) => prev || today);
    } else {
      setConsumablesCustomStart("");
      setConsumablesCustomEnd("");
    }
  }, []);

  const warehouseStats = useMemo(() => {
    const entries = warehousesState.data || [];
    if (!entries.length) {
      return { total: 0, cold: 0, volume: 0, weight: 0 };
    }
    return entries.reduce(
      (acc, warehouse) => {
        const type = warehouse?.type || "primary";
        if (["cold_storage", "freezer"].includes(type)) {
          acc.cold += 1;
        }
        acc.volume += Number(warehouse?.capacity_volume || 0);
        acc.weight += Number(warehouse?.capacity_weight || 0);
        acc.total += 1;
        return acc;
      },
      { total: 0, cold: 0, volume: 0, weight: 0 },
    );
  }, [warehousesState.data]);

  const supplierStats = useMemo(() => {
    const entries = suppliersState.data || [];
    if (!entries.length) {
      return { total: 0, avgPayment: 0, avgRating: 0, cities: 0 };
    }

    const activeEntries = entries.filter(
      (supplier) => supplier.is_active && !supplier.is_general_supplier,
    );
    const paymentEntries = activeEntries.filter((supplier) => supplier?.payment_terms);
    const ratingEntries = activeEntries.filter((supplier) => supplier?.rating);
    const cities = new Set(
      activeEntries
        .map((supplier) => (supplier.city || "").trim())
        .filter((city) => city.length > 0),
    );

    return {
      total: activeEntries.length,
      avgPayment: paymentEntries.length
        ? Math.round(
            paymentEntries.reduce(
              (sum, supplier) => sum + Number(supplier.payment_terms || 0),
              0,
            ) / paymentEntries.length,
          )
        : 0,
      avgRating: ratingEntries.length
        ? ratingEntries.reduce(
            (sum, supplier) => sum + Number(supplier.rating || 0),
            0,
          ) / ratingEntries.length
        : 0,
      cities: cities.size,
    };
  }, [suppliersState.data]);

  const supplierTypeOptions = [
    { value: "ingredient", label: "Ingrédients" },
    { value: "consumable", label: "Consommables" },
    { value: "other", label: "Autre" },
  ];

  const paymentModeOptions = [
    { value: "mobile_money", label: PAYMENT_MODE_LABELS.mobile_money },
    { value: "cash", label: PAYMENT_MODE_LABELS.cash },
    { value: "transfer", label: PAYMENT_MODE_LABELS.transfer },
    { value: "cheque", label: PAYMENT_MODE_LABELS.cheque },
  ];

  const mapSupplierToForm = useCallback(
    (supplier) => {
      if (!supplier) return { ...SUPPLIER_FORM_DEFAULT };
      return {
        ...SUPPLIER_FORM_DEFAULT,
        entity_type:
          supplier.entity_type ||
          (supplier.company_name || supplier.name ? "company" : "person"),
        civility: supplier.civility || "M.",
        first_name: supplier.first_name || "",
        last_name: supplier.last_name || "",
        company_name: supplier.company_name || supplier.name || "",
        manager_name: supplier.manager_name || supplier.contact_person || "",
        supplier_category: supplier.supplier_category || "ingredient",
        city: supplier.city || "",
        address: supplier.address || "",
        country: supplier.country || "Côte d'Ivoire",
        phone: supplier.phone || "",
        email: supplier.email || "",
        payment_mode: supplier.payment_mode || "mobile_money",
        account_number: supplier.account_number || supplier.bank_details || "",
        tax_number: supplier.tax_number || "",
        bank_details: supplier.bank_details || "",
        description: supplier.description || supplier.notes || "",
        payment_terms: supplier.payment_terms || 30,
        rating: supplier.rating || 0,
        is_active: supplier.is_active ? true : false,
      };
    },
    [],
  );

  const mapStockItemToForm = useCallback((item) => {
    if (!item) return { ...STOCK_ITEM_FORM_DEFAULT };
    return {
      ...STOCK_ITEM_FORM_DEFAULT,
      name: item.name || "",
      sku: item.sku || "",
      category: item.category || "",
      category_mode: item.category ? "existing" : "custom",
      description: item.description || "",
      unit_id: item.unit_id ? String(item.unit_id) : "",
      storage_unit_id: item.storage_unit_id
        ? String(item.storage_unit_id)
        : item.unit_id
          ? String(item.unit_id)
          : "",
      consumption_unit_id: item.consumption_unit_id
        ? String(item.consumption_unit_id)
        : item.unit_id
          ? String(item.unit_id)
          : "",
      warehouse_id: item.warehouse_id ? String(item.warehouse_id) : "",
      minimum_stock:
        item.minimum_stock !== null && item.minimum_stock !== undefined
          ? String(item.minimum_stock)
          : "",
      maximum_stock:
        item.maximum_stock !== null && item.maximum_stock !== undefined
          ? String(item.maximum_stock)
          : "",
      reorder_point:
        item.reorder_point !== null && item.reorder_point !== undefined
          ? String(item.reorder_point)
          : "",
      reorder_quantity:
        item.reorder_quantity !== null && item.reorder_quantity !== undefined
          ? String(item.reorder_quantity)
          : "",
      cost_price:
        item.cost_price !== null && item.cost_price !== undefined ? String(item.cost_price) : "",
      selling_price:
        item.selling_price !== null && item.selling_price !== undefined
          ? String(item.selling_price)
          : "",
      supplier_id: item.supplier_id ? String(item.supplier_id) : "",
      supplier_ids: Array.isArray(item.supplier_ids)
        ? item.supplier_ids.map((id) => String(id))
        : item.supplier_id
          ? [String(item.supplier_id)]
          : [],
      has_multiple_suppliers: Array.isArray(item.supplier_ids)
        ? item.supplier_ids.length > 1
        : false,
      is_perishable: item.is_perishable ? true : false,
      track_expiry: item.track_expiry ? true : false,
      shelf_life_value:
        item.shelf_life_days && item.shelf_life_days > 0
          ? String(item.shelf_life_days)
          : "",
      shelf_life_unit: "days",
    };
  }, []);

  const mapWarehouseToForm = useCallback((warehouse) => {
    if (!warehouse) {
      return {
        ...WAREHOUSE_FORM_DEFAULT,
        type_mode: "preset",
        location_mode: warehouseLocations.length ? "preset" : "custom",
      };
    }
    const knownTypes = new Set(WAREHOUSE_TYPE_OPTIONS.map((opt) => opt.value));
    const normalizedType = knownTypes.has(warehouse.type) ? warehouse.type : "secondary";
    const existingLocation = (warehouse.location || "").trim();
    const useCustomLocation = existingLocation && !warehouseLocations.includes(existingLocation);
    return {
      ...WAREHOUSE_FORM_DEFAULT,
      name: warehouse.name || "",
      code: warehouse.code || "",
      type: normalizedType || "primary",
      type_mode: "preset",
      custom_type: "",
      location: useCustomLocation ? "custom" : existingLocation,
      location_mode: useCustomLocation ? "custom" : "preset",
      custom_location: useCustomLocation ? existingLocation : "",
      description: warehouse.description || "",
      is_active: warehouse.is_active ? true : false,
      temperature_target:
        warehouse.temperature_min !== null && warehouse.temperature_min !== undefined
          ? String(warehouse.temperature_min)
          : "",
      capacity_max:
        warehouse.capacity_volume !== null && warehouse.capacity_volume !== undefined
          ? String(warehouse.capacity_volume)
          : "",
      parent_id: warehouse.parent_id ? String(warehouse.parent_id) : "",
    };
  }, [warehouseLocations]);

  const openSupplierModal = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierForm(mapSupplierToForm(supplier));
    } else {
      setEditingSupplier(null);
      setSupplierForm({ ...SUPPLIER_FORM_DEFAULT });
    }
    setShowSupplierModal(true);
  };

  const closeSupplierModal = () => {
    setShowSupplierModal(false);
    setEditingSupplier(null);
    setSupplierForm({ ...SUPPLIER_FORM_DEFAULT });
  };

  const handleSupplierInputChange = (field, value) => {
    setSupplierForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSupplierSubmit = async (e) => {
    if (e) e.preventDefault();
    const form = supplierForm;
    if (form.entity_type === "person") {
      if (!form.first_name.trim() || !form.last_name.trim()) {
        showToast("error", "Prénom et nom sont obligatoires pour une personne.");
        return;
      }
    } else if (!form.company_name.trim()) {
      showToast("error", "Le nom de l'entreprise est obligatoire.");
      return;
    }

    if (!form.phone.trim()) {
      showToast("error", "Le numéro de téléphone est obligatoire.");
      return;
    }

    const fullName = `${form.first_name} ${form.last_name}`.trim();
    const payload = {
      entity_type: form.entity_type,
      civility: form.civility,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company_name: form.company_name.trim(),
      manager_name: form.manager_name.trim(),
      supplier_category: form.supplier_category,
      city: form.city.trim(),
      address: form.address.trim(),
      country: form.country || "Côte d'Ivoire",
      phone: form.phone.trim(),
      email: form.email.trim(),
      payment_mode: form.payment_mode,
      account_number: form.account_number.trim(),
      tax_number: form.tax_number.trim(),
      bank_details: form.bank_details.trim() || form.account_number.trim(),
      description: form.description.trim(),
      notes: form.description.trim(),
      payment_terms: Number(form.payment_terms) || 0,
      rating: form.rating ? Number(form.rating) : null,
      is_active: form.is_active ? 1 : 0,
    };

    payload.name =
      form.entity_type === "company"
        ? payload.company_name || "Entreprise"
        : fullName || form.phone.trim();
    payload.contact_person =
      form.entity_type === "company"
        ? payload.manager_name || fullName || payload.company_name
        : `${form.civility ? `${form.civility} ` : ""}${fullName}`.trim();

    setIsSavingSupplier(true);
    try {
      const url = editingSupplier
        ? `/api/waise/suppliers/${editingSupplier.id}`
        : "/api/waise/suppliers";
      const method = editingSupplier ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible d'enregistrer le fournisseur.");
      }
      showToast(
        "success",
        editingSupplier ? "Fournisseur mis à jour." : "Fournisseur ajouté.",
      );
      await fetchSuppliers();
      closeSupplierModal();
    } catch (err) {
      console.error("❌ Erreur formulaire fournisseur:", err);
      showToast("error", err.message || "Erreur lors de l'enregistrement.");
    } finally {
      setIsSavingSupplier(false);
    }
  };

  const handleToggleSupplier = async (supplier) => {
    if (!supplier) return;
    try {
      const response = await fetch(`/api/waise/suppliers/${supplier.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...supplier, is_active: supplier.is_active ? 0 : 1 }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de changer le statut.");
      }
      showToast(
        "success",
        supplier.is_active ? "Fournisseur désactivé." : "Fournisseur activé.",
      );
      await fetchSuppliers();
    } catch (err) {
      console.error("❌ Erreur changement statut fournisseur:", err);
      showToast("error", err.message || "Erreur lors du changement de statut.");
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!supplier) return;
    if (
      !window.confirm(
        `Supprimer définitivement ${supplier.name || "ce fournisseur"} ?`,
      )
    ) {
      return;
    }
    try {
      const response = await fetch(`/api/waise/suppliers/${supplier.id}`, {
        method: "DELETE",
      });
      if (!response.ok && response.status !== 204) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de supprimer ce fournisseur.");
      }
      showToast("success", "Fournisseur supprimé.");
      await fetchSuppliers();
    } catch (err) {
      console.error("❌ Erreur suppression fournisseur:", err);
      showToast("error", err.message || "Erreur lors de la suppression.");
    }
  };

  const openStockItemModal = (type, stockItem = null) => {
    const normalizedType =
      type === "ingredients" ? "ingredient" : type === "consumables" ? "consumable" : type || "ingredient";
    if (!unitsState.loaded && !unitsState.loading) {
      fetchUnits();
    }
    if (!warehousesState.loaded && !warehousesState.loading) {
      fetchWarehouses();
    }
    if (!suppliersState.loaded && !suppliersState.loading) {
      fetchSuppliers();
    }
    setStockModalType(normalizedType);
    if (stockItem) {
      setEditingStockItem(stockItem);
      setStockItemForm(mapStockItemToForm(stockItem));
    } else {
      setEditingStockItem(null);
      setStockItemForm({
        ...STOCK_ITEM_FORM_DEFAULT,
        category_mode: stockCategories.length ? "existing" : "custom",
      });
    }
    setShowStockItemModal(true);
  };

  const closeStockItemModal = () => {
    setShowStockItemModal(false);
    setEditingStockItem(null);
    setStockModalType("ingredient");
    setStockItemForm({
      ...STOCK_ITEM_FORM_DEFAULT,
      category_mode: stockCategories.length ? "existing" : "custom",
    });
  };

  const handleStockItemInputChange = (field, value) => {
    setStockItemForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "unit_id") {
        if (!prev.storage_unit_id) {
          next.storage_unit_id = value;
        }
        if (!prev.consumption_unit_id) {
          next.consumption_unit_id = value;
        }
      }
      return next;
    });
  };

  const handleCategorySelection = (value) => {
    if (value === "__new_category__") {
      setStockItemForm((prev) => ({
        ...prev,
        category_mode: "custom",
        category: "",
        custom_category: "",
      }));
    } else {
      setStockItemForm((prev) => ({
        ...prev,
        category_mode: "existing",
        category: value,
        custom_category: "",
      }));
    }
  };

  const toggleLinkedSupplier = (supplierId) => {
    setStockItemForm((prev) => {
      const exists = prev.supplier_ids.includes(supplierId);
      return {
        ...prev,
        supplier_ids: exists
          ? prev.supplier_ids.filter((id) => id !== supplierId)
          : [...prev.supplier_ids, supplierId],
      };
    });
  };

  const handleSupplierModeToggle = (enabled) => {
    setStockItemForm((prev) => ({
      ...prev,
      has_multiple_suppliers: enabled,
      supplier_ids: enabled
        ? prev.supplier_ids.length
          ? prev.supplier_ids
          : prev.supplier_id
            ? [prev.supplier_id]
            : []
        : [],
      supplier_id: enabled
        ? prev.supplier_id
        : prev.supplier_ids[0] || prev.supplier_id || "",
    }));
  };

  const openWarehouseModal = (warehouse = null) => {
    if (!warehousesState.loaded && !warehousesState.loading) {
      fetchWarehouses();
    }
    if (warehouse) {
      setEditingWarehouse(warehouse);
      setWarehouseForm(mapWarehouseToForm(warehouse));
    } else {
      setEditingWarehouse(null);
      setWarehouseForm({
        ...WAREHOUSE_FORM_DEFAULT,
        location_mode: warehouseLocations.length ? "preset" : "custom",
      });
    }
    setShowWarehouseModal(true);
  };

  const closeWarehouseModal = () => {
    setShowWarehouseModal(false);
    setEditingWarehouse(null);
    setWarehouseForm({
      ...WAREHOUSE_FORM_DEFAULT,
      location_mode: warehouseLocations.length ? "preset" : "custom",
    });
  };

  const handleWarehouseInputChange = (field, value) => {
    setWarehouseForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWarehouseTypeChange = (value) => {
    // We keep a fixed set of types to match the SQLite CHECK constraint.
    setWarehouseForm((prev) => ({
      ...prev,
      type_mode: "preset",
      type: value,
      custom_type: "",
    }));
  };

  const handleWarehouseLocationChange = (value) => {
    if (value === "__custom_location__") {
      setWarehouseForm((prev) => ({
        ...prev,
        location_mode: "custom",
        location: "custom",
        custom_location: "",
      }));
    } else {
      setWarehouseForm((prev) => ({
        ...prev,
        location_mode: "preset",
        location: value,
        custom_location: "",
      }));
    }
  };

  const handleWarehouseSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!warehouseForm.name.trim()) {
      showToast("error", "Le nom de l'entrepôt est obligatoire.");
      return;
    }

    const numericOrNull = (value) => {
      if (value === "" || value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const allowedTypes = new Set(WAREHOUSE_TYPE_OPTIONS.map((opt) => opt.value));
    const normalizedTypeRaw =
      warehouseForm.type_mode === "custom" ? warehouseForm.custom_type.trim() : warehouseForm.type;
    const normalizedName = normalizeWarehouseName(warehouseForm.name);
    const isMainWarehouse =
      editingWarehouse?.is_main_pos_store === 1 || normalizedName === "entrepot principal";
    let normalizedType = allowedTypes.has(normalizedTypeRaw) ? normalizedTypeRaw : "secondary";
    if (isMainWarehouse) {
      normalizedType = "primary";
    }
    if (!isMainWarehouse && normalizedType === "primary") {
      normalizedType = "secondary";
    }
    const normalizedLocation =
      warehouseForm.location_mode === "custom"
        ? warehouseForm.custom_location.trim()
        : warehouseForm.location;
    const isMainPosStore = normalizedType === "primary" ? 1 : 0;

    const payload = {
      name: warehouseForm.name.trim(),
      description: warehouseForm.description.trim(),
      location: normalizedLocation || null,
      type: normalizedType,
      temperature_min: numericOrNull(warehouseForm.temperature_target),
      temperature_max: numericOrNull(warehouseForm.temperature_target),
      humidity_min: null,
      humidity_max: null,
      capacity_volume: numericOrNull(warehouseForm.capacity_max),
      capacity_weight: null,
      manager_id: warehouseForm.parent_id ? Number(warehouseForm.parent_id) : null,
      is_active: warehouseForm.is_active ? 1 : 0,
      is_main_pos_store: isMainPosStore,
    };

    setIsSavingWarehouse(true);
    try {
      const url = editingWarehouse
        ? `/api/waise/warehouses/${editingWarehouse.id}`
        : "/api/waise/warehouses";
      const method = editingWarehouse ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible d'enregistrer l'entrepôt.");
      }
      showToast(
        "success",
        editingWarehouse ? "Entrepôt mis à jour." : "Nouvel entrepôt ajouté.",
      );
      await fetchWarehouses();
      closeWarehouseModal();
    } catch (err) {
      console.error("❌ Erreur entrepôt:", err);
      showToast("error", err.message || "Erreur lors de l'enregistrement de l'entrepôt.");
    } finally {
      setIsSavingWarehouse(false);
    }
  };

  const handleDeleteWarehouse = async (warehouse) => {
    if (!warehouse) return;
    const normalizedName = normalizeWarehouseName(warehouse.name);
    const isMainWarehouse =
      warehouse.is_main_pos_store === 1 ||
      warehouse.type === "primary" ||
      normalizedName === "entrepot principal";
    if (isMainWarehouse) {
      showToast("error", "L'Entrepot Principal ne peut pas etre supprime.");
      return;
    }
    if (
      !window.confirm(
        `Supprimer ${warehouse.name || "cet entrepot"} ? Cette action le desactive dans Waise sans supprimer l'historique.`,
      )
    ) {
      return;
    }
    try {
      const response = await fetch(`/api/waise/warehouses/${warehouse.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de supprimer cet entrepot.");
      }
      showToast("success", "Entrepot supprime.");
      await fetchWarehouses();
    } catch (err) {
      console.error("[waise] Erreur suppression entrepot:", err);
      showToast("error", err.message || "Erreur lors de la suppression.");
    }
  };

  const handleStockItemSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!stockItemForm.name.trim()) {
      showToast("error", "Le nom de l'article est obligatoire.");
      return;
    }
    if (!stockItemForm.unit_id) {
      showToast("error", "Sélectionnez une unité de mesure.");
      return;
    }
    if (!stockItemForm.warehouse_id) {
      showToast("error", "Sélectionnez l'entrepôt où sera suivi cet article.");
      return;
    }

    const numericOrNull = (value) => {
      if (value === "" || value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const shelfLifeDays = (() => {
      if (!stockItemForm.is_perishable || !stockItemForm.shelf_life_value) return null;
      const value = Number(stockItemForm.shelf_life_value);
      if (Number.isNaN(value) || value <= 0) return null;
      const unit = stockItemForm.shelf_life_unit || "days";
      const factor = unit === "months" ? 30 : unit === "years" ? 365 : 1;
      return value * factor;
    })();

  const payload = {
    type: stockModalType,
      name: stockItemForm.name.trim(),
      sku: stockItemForm.sku.trim() || null,
      category:
        stockItemForm.category_mode === "custom"
          ? stockItemForm.custom_category.trim() || null
          : stockItemForm.category.trim() || null,
      description: stockItemForm.description.trim(),
      unit_id: Number(stockItemForm.unit_id),
      minimum_stock: numericOrNull(stockItemForm.minimum_stock) ?? 0,
      maximum_stock: numericOrNull(stockItemForm.maximum_stock),
      reorder_point: numericOrNull(stockItemForm.reorder_point),
      reorder_quantity: numericOrNull(stockItemForm.reorder_quantity),
      cost_price: numericOrNull(stockItemForm.cost_price),
      selling_price: numericOrNull(stockItemForm.selling_price),
      supplier_id: stockItemForm.has_multiple_suppliers
        ? stockItemForm.supplier_ids.length > 0
          ? Number(stockItemForm.supplier_ids[0])
          : null
        : stockItemForm.supplier_id
          ? Number(stockItemForm.supplier_id)
          : null,
      supplier_ids: stockItemForm.has_multiple_suppliers ? stockItemForm.supplier_ids : [],
      storage_unit_id: stockItemForm.storage_unit_id
        ? Number(stockItemForm.storage_unit_id)
        : stockItemForm.unit_id
          ? Number(stockItemForm.unit_id)
          : null,
      consumption_unit_id: stockItemForm.consumption_unit_id
        ? Number(stockItemForm.consumption_unit_id)
        : stockItemForm.unit_id
          ? Number(stockItemForm.unit_id)
          : null,
      warehouse_id: stockItemForm.warehouse_id
        ? Number(stockItemForm.warehouse_id)
        : null,
      is_perishable: stockItemForm.is_perishable ? 1 : 0,
      track_expiry: stockItemForm.is_perishable ? 1 : 0,
      shelf_life_days: shelfLifeDays,
      is_active: 1,
    };

    setIsSavingStockItem(true);
    try {
      const url = editingStockItem
        ? `/api/waise/stock-items/${editingStockItem.id}`
        : "/api/waise/stock-items";
      const method = editingStockItem ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible d'enregistrer cet article.");
      }
      showToast(
        "success",
        editingStockItem ? "Article de stock mis à jour." : "Nouvel article de stock ajouté.",
      );
      await fetchStockItems(stockModalType);
      closeStockItemModal();
    } catch (err) {
      console.error("❌ Erreur article stock:", err);
      showToast("error", err.message || "Erreur lors de l'enregistrement de l'article.");
    } finally {
      setIsSavingStockItem(false);
    }
  };

  useEffect(() => {
    if (inventorySection === "ingredients" && !ingredientsState.loaded && !ingredientsState.loading) {
      fetchStockItems("ingredient");
    }
    if (inventorySection === "consumables" && !consumablesState.loaded && !consumablesState.loading) {
      fetchStockItems("consumable");
    }
    if (
      (inventorySection === "ingredients" || inventorySection === "consumables") &&
      !unitsState.loaded &&
      !unitsState.loading
    ) {
      fetchUnits();
    }
  }, [
    inventorySection,
    ingredientsState.loaded,
    ingredientsState.loading,
    consumablesState.loaded,
    consumablesState.loading,
    unitsState.loaded,
    unitsState.loading,
    fetchStockItems,
    fetchUnits,
  ]);

  useEffect(() => {
    if (
      inventorySection === "reconciliation" &&
      reconciliationView === "list" &&
      !inventoriesState.loaded &&
      !inventoriesState.loading
    ) {
      fetchInventories();
    }
    if (
      inventorySection === "reconciliation" &&
      !warehousesState.loaded &&
      !warehousesState.loading
    ) {
      fetchWarehouses();
    }
  }, [
    inventorySection,
    reconciliationView,
    inventoriesState.loaded,
    inventoriesState.loading,
    warehousesState.loaded,
    warehousesState.loading,
    fetchInventories,
    fetchWarehouses,
  ]);

  useEffect(() => {
    if (
      inventorySection === "overview" &&
      !valuationState.loaded &&
      !valuationState.loading
    ) {
      fetchStockValuation();
    }
    if (
      inventorySection === "overview" &&
      !warehousesState.loaded &&
      !warehousesState.loading
    ) {
      fetchWarehouses();
    }
  }, [
    inventorySection,
    valuationState.loaded,
    valuationState.loading,
    warehousesState.loaded,
    warehousesState.loading,
    fetchStockValuation,
    fetchWarehouses,
  ]);

  useEffect(() => {
    if (inventorySection !== "losses") return;
    if (!lossTypesState.loaded && !lossTypesState.loading) {
      fetchLossTypes();
    }
    if (!lossCausesState.loaded && !lossCausesState.loading) {
      fetchLossCauses();
    }
    if (!valuationState.loaded && !valuationState.loading) {
      fetchStockValuation();
    }
    if (!warehousesState.loaded && !warehousesState.loading) {
      fetchWarehouses();
    }
    if (!lossesState.loaded && !lossesState.loading) {
      fetchLosses();
    }
  }, [
    inventorySection,
    lossTypesState.loaded,
    lossTypesState.loading,
    lossCausesState.loaded,
    lossCausesState.loading,
    valuationState.loaded,
    valuationState.loading,
    warehousesState.loaded,
    warehousesState.loading,
    lossesState.loaded,
    lossesState.loading,
    fetchLossTypes,
    fetchLossCauses,
    fetchStockValuation,
    fetchWarehouses,
    fetchLosses,
  ]);

  const renderOverview = () => {
    const estimation = overviewV2?.estimation || {};
    const resoldStock = overviewV2?.resold?.stockNow || {};
    const resoldActivity = overviewV2?.resold?.activity || {};
    const kitchenIngredientsNow = overviewV2?.kitchen?.ingredientsNow || {};
    const kitchenComposedActivity = overviewV2?.kitchen?.composedActivity || {};
    const kitchenLossesActivity = overviewV2?.kitchen?.lossesActivity || {};
    const consumablesStockNow = overviewV2?.consumables?.stockNow || {};
    const consumablesActivity = overviewV2?.consumables?.activity || {};
    const configuredProducts = Number(estimation.configuredProducts) || 0;
    const totalProducts = Number(estimation.totalProducts) || 0;
    const showEstimationConfigBadge = Boolean(estimation.hasPartialConfig);
    const estimationConfigBadge = `Basé sur ${numberFormatter.format(configuredProducts)}/${numberFormatter.format(totalProducts)} produits configurés`;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {summaryCards.map((card) => (
            <SummaryCard key={card.label} {...card} isDarkMode={isDarkMode} />
          ))}
        </div>

        <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-4`}>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
              Ce que le stock peut rapporter - estimation
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={`rounded-2xl border p-5 ${isDarkMode ? "border-white/15 bg-white/5" : "border-slate-200 bg-white"}`}>
              <p className="text-sm font-semibold">Ventes possibles</p>
              <p className="text-2xl font-bold mt-2">
                {currencyFormatter.format(estimation.salesPossible || 0)}
              </p>
              <p className={`text-xs mt-2 ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
                Si tout le stock est vendu (estimation basse)
              </p>
              {showEstimationConfigBadge && (
                <p className="text-xs mt-3 inline-flex rounded-full px-3 py-1 bg-amber-500/15 text-amber-300">
                  {estimationConfigBadge}
                </p>
              )}
            </div>
            <div className={`rounded-2xl border p-5 ${isDarkMode ? "border-white/15 bg-white/5" : "border-slate-200 bg-white"}`}>
              <p className="text-sm font-semibold">Bénéfice possible</p>
              <p className={`text-2xl font-bold mt-2 ${Number(estimation.profitPossible) < 0 ? "text-amber-300" : ""}`}>
                {currencyFormatter.format(estimation.profitPossible || 0)}
              </p>
              <p className={`text-xs mt-2 ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
                Estimation prudente - marge sur ventes possibles
              </p>
              {showEstimationConfigBadge && (
                <p className="text-xs mt-3 inline-flex rounded-full px-3 py-1 bg-amber-500/15 text-amber-300">
                  {estimationConfigBadge}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-4`}>
          <div className="flex items-center gap-3">
            <div className={`rounded-xl p-2 ${isDarkMode ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
              <Package className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-semibold">Revendus</h2>
          </div>

          <div>
            <p className={`text-sm mb-2 ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>Stock actuel</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                label="Stock en valeur"
                value={currencyFormatter.format(resoldStock.stockValue || 0)}
                helper="Coût d'achat des revendus en stock"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Articles disponibles"
                value={`${numberFormatter.format(resoldStock.availableUnits || 0)} articles`}
                helper="Quantité encore en stock, prête à vendre"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Coût estimé pour vendre"
                value={currencyFormatter.format(resoldStock.estimatedCostToSell || 0)}
                helper="Ce que ce stock vous a coûté"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <p className={`text-sm ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
                Activité sur la période
              </p>
              <PeriodFilterControl
                value={resoldPeriodFilter}
                onChange={handleResoldPeriodChange}
                startDate={resoldCustomStart}
                endDate={resoldCustomEnd}
                onStartDateChange={setResoldCustomStart}
                onEndDateChange={setResoldCustomEnd}
                isDarkMode={isDarkMode}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <MetricCard
                label="Ce qui a été vendu"
                value={currencyFormatter.format(resoldActivity.soldCost || 0)}
                helper="Coût d'achat des articles vendus"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Articles vendus"
                value={numberFormatter.format(resoldActivity.soldQuantity || 0)}
                helper="Quantité totale vendue sur la période"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Bénéfice réalisé"
                value={currencyFormatter.format(resoldActivity.realizedProfit || 0)}
                rawValue={resoldActivity.realizedProfit || 0}
                negativeTone
                helper="Ce que vous avez gagné"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Pertes enregistrées"
                value={currencyFormatter.format(resoldActivity.lossValue || 0)}
                helper="Péremption, dégâts, annulations"
                badge={`${numberFormatter.format(resoldActivity.lossCount || 0)} articles`}
                badgeTone="warning"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </section>

        <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-4`}>
          <div className="flex items-center gap-3">
            <div className={`rounded-xl p-2 ${isDarkMode ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
              <ChefHat className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-semibold">Cuisine</h2>
          </div>

          <div className={`rounded-2xl p-4 ${isDarkMode ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-200"}`}>
            <p className="text-sm font-semibold mb-3">Ingrédients en stock</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                label="Stock en valeur"
                value={currencyFormatter.format(kitchenIngredientsNow.stockValue || 0)}
                helper="Coût d'achat des ingrédients en stock"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Plats différents faisables"
                value={`${numberFormatter.format(kitchenIngredientsNow.feasibleDishesX || 0)} sur ${numberFormatter.format(kitchenIngredientsNow.feasibleDishesY || 0)}`}
                helper="Plats dont tous les ingrédients sont disponibles"
                subHelper={`${numberFormatter.format(kitchenIngredientsNow.excludedIncompleteRecipes || 0)} recettes incomplètes exclues`}
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Portions produisables"
                value={`~${numberFormatter.format(kitchenIngredientsNow.produciblePortions || 0)} portions`}
                helper="Estimation totale sur les plats faisables"
                subHelper="Estimation - recettes partielles exclues"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          <div className={`rounded-2xl p-4 ${isDarkMode ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-200"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <p className="text-sm font-semibold">Activité composés sur la période</p>
              <PeriodFilterControl
                value={kitchenPeriodFilter}
                onChange={handleKitchenPeriodChange}
                startDate={kitchenCustomStart}
                endDate={kitchenCustomEnd}
                onStartDateChange={setKitchenCustomStart}
                onEndDateChange={setKitchenCustomEnd}
                isDarkMode={isDarkMode}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <MetricCard
                label="Ce qui a été vendu"
                value={currencyFormatter.format(kitchenComposedActivity.soldRecipeCost || 0)}
                helper="Coût recette des plats vendus"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Plats vendus"
                value={numberFormatter.format(kitchenComposedActivity.soldQuantity || 0)}
                helper="Quantité totale de plats vendus sur la période"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Bénéfice réalisé"
                value={currencyFormatter.format(kitchenComposedActivity.realizedProfit || 0)}
                rawValue={kitchenComposedActivity.realizedProfit || 0}
                negativeTone
                helper="Ce que vous avez gagné sur les plats"
                isDarkMode={isDarkMode}
              />
            </div>
            <p className={`text-sm mb-2 ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
              Pertes enregistrées sur la période
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MetricCard
                label="Ingrédients perdus"
                value={currencyFormatter.format(kitchenLossesActivity.ingredientsLossValue || 0)}
                helper="Péremption, dégâts sur matières premières"
                badge={`${numberFormatter.format(kitchenLossesActivity.ingredientsLossCount || 0)} articles`}
                badgeTone="warning"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Plats perdus"
                value={currencyFormatter.format(kitchenLossesActivity.dishesLossValue || 0)}
                helper="Plats annulés ou jetés après préparation"
                badge={`${numberFormatter.format(kitchenLossesActivity.dishesLossCount || 0)} plats`}
                badgeTone="warning"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </section>

        <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-4`}>
          <div className="flex items-center gap-3">
            <div
              className={`rounded-xl p-2 ${
                isDarkMode
                  ? "bg-cyan-500/20 text-cyan-300"
                  : "bg-cyan-100 text-cyan-700"
              }`}
            >
              <Package className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-semibold">Consommables</h2>
          </div>

          <div
            className={`rounded-2xl p-4 ${
              isDarkMode
                ? "bg-white/5 border border-white/10"
                : "bg-slate-50 border border-slate-200"
            }`}
          >
            <p className="text-sm font-semibold mb-3">Stock actuel</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                label="Stock en valeur"
                value={currencyFormatter.format(consumablesStockNow.stockValue || 0)}
                helper="Coût d'achat des consommables en stock"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Articles disponibles"
                value={`${numberFormatter.format(consumablesStockNow.availableUnits || 0)} articles`}
                helper="Références avec du stock disponible"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Références surveillées"
                value={`${numberFormatter.format(consumablesStockNow.monitoredCount || 0)} sur ${numberFormatter.format(consumablesStockNow.totalProducts || 0)}`}
                helper="Celles avec un seuil d'alerte configuré"
                badge={`${numberFormatter.format(consumablesStockNow.alertCount || 0)} en alerte`}
                badgeTone="warning"
                isDarkMode={isDarkMode}
              />
            </div>
            {Boolean(consumablesStockNow.hasPartialConfig) ? (
              <p className="text-xs mt-3 inline-flex rounded-full px-3 py-1 bg-amber-500/15 text-amber-300">
                Basé sur {numberFormatter.format(consumablesStockNow.configuredProducts || 0)}/
                {numberFormatter.format(consumablesStockNow.totalProducts || 0)} références avec coût configuré
              </p>
            ) : null}
            {Number(consumablesStockNow.totalProducts || 0) === 0 ? (
              <p
                className={`text-xs mt-3 ${
                  isDarkMode ? "text-white/70" : "text-slate-500"
                }`}
              >
                Aucun consommable configuré. Ajoutez vos consommables dans Produits &amp; Inventaire.
              </p>
            ) : null}
          </div>

          <div
            className={`rounded-2xl p-4 ${
              isDarkMode
                ? "bg-white/5 border border-white/10"
                : "bg-slate-50 border border-slate-200"
            }`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <p className="text-sm font-semibold">Activité sur la période</p>
              <PeriodFilterControl
                value={consumablesPeriodFilter}
                onChange={handleConsumablesPeriodChange}
                startDate={consumablesCustomStart}
                endDate={consumablesCustomEnd}
                onStartDateChange={setConsumablesCustomStart}
                onEndDateChange={setConsumablesCustomEnd}
                isDarkMode={isDarkMode}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <MetricCard
                label="Coût consommé"
                value={currencyFormatter.format(consumablesActivity.consumedCost || 0)}
                helper="Coût d'achat des sorties internes"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Quantité consommée"
                value={`${numberFormatter.format(consumablesActivity.consumedQty || 0)} articles`}
                helper="Nombre d'unités sorties du stock"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Pertes enregistrées"
                value={currencyFormatter.format(consumablesActivity.lossValue || 0)}
                helper="Péremption, dégâts, articles inutilisables"
                badge={`${numberFormatter.format(consumablesActivity.lossCount || 0)} articles`}
                badgeTone="warning"
                isDarkMode={isDarkMode}
              />
              <MetricCard
                label="Livraisons à recevoir"
                value={numberFormatter.format(consumablesActivity.pendingDeliveries || 0)}
                helper={
                  consumablesActivity.allCategoriesFallback
                    ? "Toutes catégories confondues"
                    : "Commandes fournisseurs consommables non encore livrées"
                }
                isDarkMode={isDarkMode}
              />
            </div>
            {Number(consumablesActivity.consumedQty || 0) <= 0 &&
            Number(consumablesActivity.consumedCost || 0) <= 0 ? (
              <p
                className={`text-xs mt-3 ${
                  isDarkMode ? "text-white/70" : "text-slate-500"
                }`}
              >
                Aucune consommation enregistrée sur cette période.
              </p>
            ) : null}
          </div>
        </section>
      </div>
    );
  };

  const renderConsumption = () => (
    <div className="space-y-6">
      <ConsumptionPanel
        consumption={dashboard?.consumption}
        isDarkMode={isDarkMode}
        expanded
      />
    </div>
  );

  const renderEntries = () => {
    const entryFilters = [
      { id: "all", label: "Tous" },
      { id: "pending", label: "En attente" },
      { id: "confirmed", label: "Confirmé" },
      { id: "on_the_way", label: "En chemin" },
      { id: "delivered", label: "Livré" },
      { id: "stored", label: "Stocké" },
      { id: "trash", label: "Corbeille", isSpecial: true },
    ];
    const purchaseCounts = purchaseOrdersState.counts || EMPTY_PURCHASE_COUNTS;
    const orders = purchaseOrdersState.items || [];

    return (
      <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Logistique</p>
            <h2 className="text-lg font-semibold">Entrées & commandes</h2>
            <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
              Suivi des bons de commandes fournisseurs.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search
                className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? "text-white/40" : "text-slate-400"
                }`}
              />
              <input
                type="text"
                value={purchaseOrdersSearch}
                onChange={(e) => setPurchaseOrdersSearch(e.target.value)}
                placeholder="Rechercher par numéro ou fournisseur..."
                className={`pl-10 pr-4 py-2 rounded-2xl text-sm border ${
                  isDarkMode
                    ? "bg-white/5 border-white/10 text-white placeholder-white/40"
                    : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                }`}
              />
            </div>
            <button
              type="button"
              onClick={openNewPurchaseOrderModal}
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow"
            >
              <Plus className="w-4 h-4" />
              Nouveau bon
            </button>
          </div>
        </div>

        <div
          className={`flex flex-wrap gap-2 rounded-2xl border ${
            isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
          } p-1`}
        >
          {entryFilters.map((filter) => {
            const isActive = purchaseOrdersFilter === filter.id;
            const countValue =
              filter.id === "all"
                ? purchaseCounts.all
                : filter.id === "trash"
                  ? purchaseCounts.trash
                  : purchaseCounts[filter.id] || 0;
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => setPurchaseOrdersFilter(filter.id)}
                className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                  isActive
                    ? "bg-amber-400 text-slate-900 shadow-lg"
                    : filter.isSpecial
                      ? "text-rose-400"
                      : isDarkMode
                        ? "text-white/70"
                        : "text-slate-700"
                }`}
              >
                <span>{filter.label}</span>
                <span
                  className={`text-xs font-semibold ${
                    isActive
                      ? "text-slate-900/70"
                      : isDarkMode
                        ? "text-white/60"
                        : "text-slate-500"
                  }`}
                >
                  {numberFormatter.format(countValue || 0)}
                </span>
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
              <tr>
                {["N°", "Date", "Dernière modification", "Statut", "Fournisseur", "Date prévue", "Date d'arrivée", "Total", "Actions"].map((header) => (
                  <th
                    key={header}
                    className={`px-4 py-3 text-left text-xs uppercase tracking-wide ${
                      isDarkMode ? "text-white/60" : "text-slate-500"
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {purchaseOrdersState.loading && !orders.length ? (
                <tr>
                  <td
                    colSpan={9}
                    className={`px-4 py-10 text-center ${isDarkMode ? "text-white/60" : "text-slate-500"}`}
                  >
                    Chargement des bons de commande…
                  </td>
                </tr>
              ) : purchaseOrdersState.error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-rose-400">
                    {purchaseOrdersState.error}
                  </td>
                </tr>
              ) : orders.length ? (
                orders.map((order) => {
                  const statusMeta = PURCHASE_STATUS_LOOKUP[order.status] || PURCHASE_STATUS_LOOKUP.pending;
                  return (
                    <tr
                      key={order.id}
                      className={isDarkMode ? "border-b border-white/5" : "border-b border-slate-100"}
                    >
                      <td className="px-4 py-3 font-semibold">{order.orderNumber || "—"}</td>
                      <td className="px-4 py-3">{formatDateValue(order.orderDate)}</td>
                      <td className="px-4 py-3">
                        {formatDateValue(order.updatedAt || order.orderDate, true)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-2 max-w-[200px]">
                          <span
                            className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold ${statusMeta.pill}`}
                          >
                            {statusMeta.label}
                          </span>
                          <select
                            value={order.status}
                            onChange={(e) => handlePurchaseOrderStatusChange(order, e.target.value)}
                            disabled={updatingPurchaseOrderId === order.id}
                            className={`rounded-xl px-3 py-1 text-xs font-semibold border ${
                              isDarkMode
                                ? "bg-white/5 border-white/10 text-white"
                                : "bg-white border-slate-200 text-slate-700"
                            }`}
                          >
                            {PURCHASE_ORDER_STATUSES.map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{order.supplierName || "—"}</div>
                        {order.warehouseName && (
                          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                            {order.warehouseName}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">{formatDateValue(order.expectedDate)}</td>
                      <td className="px-4 py-3">{formatDateValue(order.arrivalDate)}</td>
                      <td className="px-4 py-3 font-semibold">{currencyFormatter.format(order.totalAmount || 0)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openEditPurchaseOrderModal(order.id)}
                            className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
                            title="Modifier"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadPurchaseOrder(order.id)}
                            className="rounded-full border border-white/10 p-2 hover:border-white/40"
                            title="Télécharger"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePurchaseOrder(order)}
                            disabled={deletingPurchaseOrderId === order.id}
                            className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60 disabled:opacity-50"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className={`px-4 py-10 text-center ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Aucun bon de commande enregistré pour le moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const openNewPurchaseOrderModal = () => {
    resetPurchaseOrderForm();
    setShowPurchaseOrderModal(true);
  };

  const closeNewPurchaseOrderModal = () => {
    resetPurchaseOrderForm();
    setShowPurchaseOrderModal(false);
  };

  const handlePurchaseOrderFieldChange = (field, value) => {
    setPurchaseOrderForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAdditionalCharge = () => {
    setAdditionalCharges((prev) => [
      ...prev,
      { id: Date.now(), label: "", amount: "" },
    ]);
  };

  const handleAdditionalChargeChange = (id, field, value) => {
    setAdditionalCharges((prev) =>
      prev.map((charge) =>
        charge.id === id ? { ...charge, [field]: value } : charge,
      ),
    );
  };

  const combineDateAndTime = (date, time) => {
    if (!date) return null;
    const isoString = `${date}T${time || "00:00"}`;
    const parsed = new Date(isoString);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed.toISOString();
  };

  const parseDecimalInput = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const normalized = typeof value === "string" ? value.replace(",", ".") : value;
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getDefaultPackaging = (stockItem) => {
    const packaging = Array.isArray(stockItem?.packaging) ? stockItem.packaging : [];
    return (
      packaging.find((row) => Number(row.is_default) === 1) ||
      packaging[0] ||
      null
    );
  };

  const getPackagingReferencePrice = (stockItem, packaging = null) => {
    const packagingQty = Math.max(1, Number(packaging?.packaging_qty) || 1);
    const packagingPrice = Number(packaging?.purchase_price);
    if (Number.isFinite(packagingPrice) && packagingPrice > 0) return packagingPrice;

    const itemPackagePrice = Number(stockItem?.purchase_price_per_unit);
    if (Number.isFinite(itemPackagePrice) && itemPackagePrice > 0) return itemPackagePrice;

    const pieceCost =
      Number(stockItem?.weighted_avg_cost) ||
      Number(stockItem?.cost_price) ||
      Number(stockItem?.purchase_price_per_piece) ||
      0;
    return Math.max(0, pieceCost * packagingQty);
  };

  const buildPurchaseItemDraft = (stockItem, existingLine = {}) => {
    const defaultPackaging = existingLine.packaging_id
      ? (stockItem?.packaging || []).find((row) => Number(row.id) === Number(existingLine.packaging_id))
      : getDefaultPackaging(stockItem);
    const packagingQty = Math.max(
      1,
      Number(existingLine.packaging_qty ?? defaultPackaging?.packaging_qty ?? 1) || 1,
    );
    const referenceUnitCost = Number(
      existingLine.reference_unit_price ?? getPackagingReferencePrice(stockItem, defaultPackaging),
    ) || 0;
    const unitCost = Number(existingLine.unit_price ?? existingLine.unit_cost ?? referenceUnitCost) || 0;
    return {
      id: stockItem.id,
      name: stockItem.name,
      unit_cost: unitCost,
      reference_unit_cost: referenceUnitCost,
      unit_price_changed:
        existingLine.unit_price_changed !== undefined
          ? Boolean(Number(existingLine.unit_price_changed))
          : Math.abs(unitCost - referenceUnitCost) > 0.001,
      storage: stockItem.warehouse_name || stockItem.storage_unit_name || existingLine.storage || "—",
      quantity:
        existingLine.quantity !== undefined && existingLine.quantity !== null
          ? String(existingLine.quantity)
          : (
              stockItem.reorder_quantity !== null &&
              stockItem.reorder_quantity !== undefined &&
              stockItem.reorder_quantity !== ""
                ? String(stockItem.reorder_quantity)
                : "1"
            ),
      unit_label:
        defaultPackaging?.packaging_unit ||
        existingLine.packaging_unit ||
        stockItem.unit_symbol ||
        stockItem.unit_name ||
        "",
      packaging_id: defaultPackaging?.id || existingLine.packaging_id || null,
      packaging_unit: defaultPackaging?.packaging_unit || existingLine.packaging_unit || null,
      packaging_qty: packagingQty,
      packaging_options: Array.isArray(stockItem?.packaging) ? stockItem.packaging : [],
      notes: existingLine.notes || null,
    };
  };

  const extractDatePart = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  };

  const extractTimePart = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(11, 16);
  };

  const resetPurchaseOrderForm = () => {
    setPurchaseOrderForm(buildPurchaseOrderFormDefault());
    setPurchaseItems([]);
    setDeliveryFee("");
    setAdditionalCharges([]);
    setEditingPurchaseOrder(null);
    setShowSupplierCatalog(false);
    setSupplierCatalogState({ items: [], loading: false, error: null });
  };

  const fetchSupplierCatalog = useCallback(async (supplierId) => {
    if (!supplierId) return;
    setSupplierCatalogState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch(`/api/waise/stock-items?supplier_id=${supplierId}`);
      if (!response.ok) {
        throw new Error("Impossible de récupérer les articles du fournisseur");
      }
      const items = await response.json();
      setSupplierCatalogState({ items, loading: false, error: null });
    } catch (err) {
      console.error("❌ Articles fournisseur:", err);
      setSupplierCatalogState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
      }));
      showToast("error", err.message || "Chargement des articles impossible.");
    }
  }, []);

  useEffect(() => {
    if (!purchaseOrderForm.supplier_id) {
      setSupplierCatalogState({ items: [], loading: false, error: null });
      setShowSupplierCatalog(false);
      return;
    }
    fetchSupplierCatalog(purchaseOrderForm.supplier_id);
  }, [purchaseOrderForm.supplier_id, fetchSupplierCatalog]);

  const handleAttachSupplierItem = (stockItem) => {
    if (!stockItem) return;
    const exists = purchaseItems.some((item) => item.id === stockItem.id);
    if (exists) return;
    setPurchaseItems((prev) => [...prev, buildPurchaseItemDraft(stockItem)]);
  };

  const handlePurchaseItemChange = (itemId, field, value) => {
    setPurchaseItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        if (field === "quantity") {
          return { ...item, quantity: value.replace(/[^0-9.,]/g, "") };
        }
        if (field === "unit_cost") {
          const cleaned = value.replace(/[^0-9.,]/g, "");
          const nextValue = parseDecimalInput(cleaned);
          const reference = Number(item.reference_unit_cost) || 0;
          return {
            ...item,
            unit_cost: cleaned,
            unit_price_changed: Math.abs(nextValue - reference) > 0.001,
          };
        }
        if (field === "packaging_id") {
          const selectedPackaging = (item.packaging_options || []).find(
            (row) => Number(row.id) === Number(value),
          );
          if (!selectedPackaging) return item;
          const referenceUnitCost = getPackagingReferencePrice(item, selectedPackaging);
          return {
            ...item,
            packaging_id: selectedPackaging.id,
            packaging_unit: selectedPackaging.packaging_unit,
            packaging_qty: Math.max(1, Number(selectedPackaging.packaging_qty) || 1),
            unit_label: selectedPackaging.packaging_unit,
            unit_cost: referenceUnitCost,
            reference_unit_cost: referenceUnitCost,
            unit_price_changed: false,
          };
        }
        return { ...item, [field]: value };
      }),
    );
  };
  const handleRemovePurchaseItem = (itemId) => {
    setPurchaseItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const hydratePurchaseOrderForm = (order) => {
    setPurchaseOrderForm({
      supplier_id: order.supplier_id ? String(order.supplier_id) : "",
      created_at: order.order_date || new Date().toISOString(),
      status: order.status || "pending",
      scheduled_date: extractDatePart(order.expected_delivery_date),
      scheduled_time: extractTimePart(order.expected_delivery_date),
      arrival_date: extractDatePart(order.actual_delivery_date),
      arrival_time: extractTimePart(order.actual_delivery_date),
      description: order.notes || "",
      warehouse_id: order.warehouse_id ? String(order.warehouse_id) : "",
    });
    setDeliveryFee(order.delivery_fee ? String(order.delivery_fee) : "");
    const otherCharges = order.other_fees ? [{ id: Date.now(), label: "Autres frais", amount: String(order.other_fees) }] : [];
    setAdditionalCharges(otherCharges);
    setPurchaseItems(
      (order.items || []).map((item) =>
        buildPurchaseItemDraft(
          {
            id: item.stock_item_id,
            name: item.name || `Article ${item.stock_item_id}`,
            unit_symbol: item.unit_symbol,
            unit_name: item.unit_name,
            warehouse_name: order.warehouse_name || "—",
            packaging: item.packaging || [],
          },
          item,
        ),
      ),
    );    setShowSupplierCatalog(false);
  };

  const openEditPurchaseOrderModal = async (orderId) => {
    try {
      const response = await fetch(`/api/waise/purchase-orders/${orderId}`);
      if (!response.ok) {
        throw new Error("Impossible de charger ce bon de commande");
      }
      const order = await response.json();
      hydratePurchaseOrderForm(order);
      setEditingPurchaseOrder({ id: order.id, orderNumber: order.order_number });
      setShowPurchaseOrderModal(true);
    } catch (err) {
      console.error("❌ Erreur ouverture bon:", err);
      showToast("error", err.message || "Impossible d'ouvrir ce bon.");
    }
  };

  const handleDownloadPurchaseOrder = async (orderId) => {
    try {
      const response = await fetch(`/api/waise/purchase-orders/${orderId}/pdf`, {
        headers: { Accept: "application/pdf" },
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de télécharger ce bon");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers.get("Content-Disposition");
      const match = disposition && /filename="([^"]+)"/i.exec(disposition);
      const filename = match && match[1] ? match[1] : "bon-de-commande.pdf";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Téléchargement bon:", err);
      showToast("error", err.message || "Téléchargement impossible.");
    }
  };

  const handleDeletePurchaseOrder = async (order) => {
    const confirmed = typeof window === "undefined"
      ? true
      : window.confirm(`Supprimer le bon ${order.orderNumber} ?`);
    if (!confirmed) return;
    setDeletingPurchaseOrderId(order.id);
    try {
      const response = await fetch(`/api/waise/purchase-orders/${order.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Suppression impossible.");
      }
      showToast("success", "Bon de commande supprimé.");
      await fetchPurchaseOrders();
    } catch (err) {
      console.error("❌ Suppression bon:", err);
      showToast("error", err.message || "Impossible de supprimer ce bon.");
    } finally {
      setDeletingPurchaseOrderId(null);
    }
  };

  const fetchPurchaseOrders = useCallback(async () => {
    if (activeTab !== "entries") return;
    setPurchaseOrdersState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = new URLSearchParams();
      if (purchaseOrdersFilter && purchaseOrdersFilter !== "all") {
        params.append("status", purchaseOrdersFilter);
      }
      if (purchaseOrdersSearch.trim()) {
        params.append("search", purchaseOrdersSearch.trim());
      }
      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await fetch(`/api/waise/purchase-orders${query}`);
      if (!response.ok) {
        throw new Error("Impossible de charger les bons de commande");
      }
      const data = await response.json();
      const nextCounts = data.counts
        ? { ...EMPTY_PURCHASE_COUNTS, ...data.counts }
        : { ...EMPTY_PURCHASE_COUNTS };
      setPurchaseOrdersState({
        items: data.orders || [],
        counts: nextCounts,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error("❌ Erreur bons de commande:", err);
      setPurchaseOrdersState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Erreur inconnue",
      }));
      showToast("error", err.message || "Impossible de charger les bons de commande");
    }
  }, [activeTab, purchaseOrdersFilter, purchaseOrdersSearch]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  const buildPurchaseOrderPayload = () => ({
    supplier_id: Number(purchaseOrderForm.supplier_id),
    warehouse_id: null,
    status: purchaseOrderForm.status,
    expected_delivery_date: combineDateAndTime(
      purchaseOrderForm.scheduled_date,
      purchaseOrderForm.scheduled_time,
    ),
    actual_delivery_date: combineDateAndTime(
      purchaseOrderForm.arrival_date,
      purchaseOrderForm.arrival_time,
    ),
    notes: purchaseOrderForm.description,
    delivery_fee: parseDecimalInput(deliveryFee),
    additional_charges: additionalCharges
      .filter((charge) => charge.label || charge.amount)
      .map((charge) => ({
        label: charge.label.trim(),
        amount: parseDecimalInput(charge.amount),
      })),
    items: purchaseItems.map((item) => ({
      stock_item_id: item.id,
      quantity: parseDecimalInput(item.quantity),
      unit_cost: parseDecimalInput(item.unit_cost ?? item.cost),
      packaging_id: item.packaging_id || null,
      packaging_unit: item.packaging_unit || item.unit_label || null,
      packaging_qty: Math.max(1, Number(item.packaging_qty) || 1),
      reference_unit_price: parseDecimalInput(item.reference_unit_cost),
      unit_price_changed: item.unit_price_changed ? 1 : 0,
      notes: item.notes || null,
    })),
    created_at: purchaseOrderForm.created_at,
  });

  const getPurchaseOrderPriceChanges = () =>
    purchaseItems
      .filter((item) => item.unit_price_changed)
      .map((item) => ({
        stock_item_id: item.id,
        name: item.name,
        previous: parseDecimalInput(item.reference_unit_cost),
        next: parseDecimalInput(item.unit_cost ?? item.cost),
        packaging_unit: item.packaging_unit || item.unit_label || item.unit,
      }))
      .filter((change) => Number.isFinite(change.next) && Math.abs(change.next - change.previous) > 0.0001);

  const submitPurchaseOrderPayload = async (payload, updateReferencePrices = false) => {
    const endpoint = editingPurchaseOrder
      ? `/api/waise/purchase-orders/${editingPurchaseOrder.id}`
      : "/api/waise/purchase-orders";
    const method = editingPurchaseOrder ? "PUT" : "POST";

    const response = await fetch(endpoint, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        update_reference_prices: Boolean(updateReferencePrices),
      }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Impossible d'enregistrer le bon.");
    }
    return response.json().catch(() => ({}));
  };

  const finishPurchaseOrderSuccess = async () => {
    showToast("success", editingPurchaseOrder ? "Bon de commande mis à jour." : "Bon de commande créé.");
    closeNewPurchaseOrderModal();
    await fetchPurchaseOrders();
    if (ingredientsState.loaded) await fetchStockItems("ingredient");
    if (consumablesState.loaded) await fetchStockItems("consumable");
    if (resoldStockState.loaded) await fetchResoldStockItems();
    fetchDashboard();
  };

  const handleSavePurchaseOrder = async () => {
    if (!purchaseOrderForm.supplier_id) {
      showToast("error", "Veuillez sélectionner un fournisseur.");
      return;
    }
    if (purchaseItems.some((item) => parseDecimalInput(item.unit_cost) <= 0)) {
      showToast("error", "Renseignez le prix d'achat de chaque article.");
      return;
    }
    setIsSavingPurchaseOrder(true);
    try {
      const payload = buildPurchaseOrderPayload();
      const priceChanges = getPurchaseOrderPriceChanges();
      if (priceChanges.length) {
        setPurchasePriceConfirmation({ mode: "purchase_order", payload, changes: priceChanges });
        setIsSavingPurchaseOrder(false);
        return;
      }
      await submitPurchaseOrderPayload(payload, false);
      await finishPurchaseOrderSuccess();
    } catch (err) {
      console.error("❌ Erreur bon de commande:", err);
      showToast("error", err.message || "Erreur lors de la création du bon.");
    } finally {
      setIsSavingPurchaseOrder(false);
    }
  };

  const handlePurchaseOrderStatusChange = async (order, nextStatus) => {
    if (order.status === nextStatus) return;
    if (nextStatus === "stored") {
      openStorageModal(order);
      return;
    }
    setUpdatingPurchaseOrderId(order.id);
    try {
      const response = await fetch(`/api/waise/purchase-orders/${order.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de mettre à jour le statut.");
      }
      showToast("success", "Statut du bon mis à jour.");
      await fetchPurchaseOrders();
      if (ingredientsState.loaded) {
        fetchStockItems("ingredient");
      }
      if (consumablesState.loaded) {
        fetchStockItems("consumable");
      }
      fetchWarehouses();
    } catch (err) {
      console.error("❌ Erreur statut bon:", err);
      showToast("error", err.message || "Mise à jour du statut impossible.");
    } finally {
      setUpdatingPurchaseOrderId(null);
    }
  };

  const closeStorageModal = () => {
    setShowStorageModal(false);
    setStorageItemsForm([]);
    setStorageMode("auto");
    setStorageModalOrder(null);
    setIsSavingStorage(false);
    setStorageModalLoading(false);
    setPurchasePriceConfirmation(null);
  };

  const STORAGE_PREF_KEY = "waise:lastWarehouseByStockItem";
  const LEGACY_STORAGE_PREF_KEY = "keassa:lastWarehouseByStockItem";
  const normalizeWarehouseName = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  const isMainWarehouseName = (value) => normalizeWarehouseName(value) === "entrepot principal";

  const getLastWarehouseSelection = (stockItemId) => {
    try {
      const raw =
        localStorage.getItem(STORAGE_PREF_KEY) ||
        localStorage.getItem(LEGACY_STORAGE_PREF_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const stored = parsed?.[String(stockItemId)];
      return stored ? Number(stored) : null;
    } catch (err) {
      console.warn("⚠️ Impossible de lire la préférence d'entrepôt:", err);
      return null;
    }
  };

  const setLastWarehouseSelection = (stockItemId, warehouseId) => {
    try {
      const raw =
        localStorage.getItem(STORAGE_PREF_KEY) ||
        localStorage.getItem(LEGACY_STORAGE_PREF_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[String(stockItemId)] = warehouseId;
      localStorage.setItem(STORAGE_PREF_KEY, JSON.stringify(parsed));
    } catch (err) {
      console.warn("⚠️ Impossible de sauvegarder la préférence d'entrepôt:", err);
    }
  };

  const createStorageLot = (quantity = "") => ({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    quantity: quantity === "" ? "" : String(quantity),
    batch_number: "",
    lot_sku: "",
    expiry_date: "",
  });

  const openStorageModal = async (order) => {
    setStorageMode("auto");
    setStorageItemsForm([]);
    setStorageModalOrder({ id: order.id, orderNumber: order.orderNumber });
    setStorageModalLoading(true);
    setShowStorageModal(true);
    try {
      const response = await fetch(`/api/waise/purchase-orders/${order.id}`);
      if (!response.ok) {
        throw new Error("Impossible de charger ce bon.");
      }
      const detail = await response.json();
      setStorageModalOrder({ id: detail.id, orderNumber: detail.order_number });
      const warehouses = warehousesState.data || [];
      const mainWarehouseId =
        warehouses.find((warehouse) => Number(warehouse.is_main_pos_store) === 1)?.id ||
        warehouses.find((warehouse) => isMainWarehouseName(warehouse.name))?.id ||
        null;
      const defaultWarehouseId =
        detail.warehouse_id ? Number(detail.warehouse_id) : mainWarehouseId;
      const formattedItems = (detail.items || []).map((item) => ({
        stock_item_id: item.stock_item_id,
        name: item.name,
        quantity_ordered: item.quantity || 0,
        packaging_qty: Math.max(1, Number(item.packaging_qty) || 1),
        packaging_unit: item.packaging_unit || item.unit_symbol || item.unit_name || "",
        pieces_ordered: (Number(item.quantity) || 0) * Math.max(1, Number(item.packaging_qty) || 1),
        unit_price: Number(item.unit_price) || 0,
        reference_unit_price: Number(item.reference_unit_price) || 0,
        unit_price_changed: Boolean(Number(item.unit_price_changed)),
        storage_quantity: item.storage_quantity || 0,
        inputValue:
          item.storage_quantity && item.storage_quantity > 0
            ? String(item.storage_quantity)
            : String((Number(item.quantity) || 0) * Math.max(1, Number(item.packaging_qty) || 1)),
        unit_symbol: item.unit_symbol || item.unit_name || "",
        storage_warehouse_id:
          item.storage_warehouse_id ??
          getLastWarehouseSelection(item.stock_item_id) ??
          defaultWarehouseId,
        lotsEnabled: false,
        lots: [],
      }));
      setStorageItemsForm(formattedItems);
    } catch (err) {
      console.error("❌ Erreur chargement stockage bon:", err);
      showToast("error", err.message || "Impossible de préparer le stockage de ce bon.");
      closeStorageModal();
    } finally {
      setStorageModalLoading(false);
    }
  };

  const handleStorageQuantityChange = (stockItemId, value) => {
    setStorageItemsForm((prev) =>
      prev.map((item) =>
        item.stock_item_id === stockItemId
          ? {
              ...item,
              inputValue: value,
              lots:
                item.lotsEnabled && item.lots.length === 1
                  ? [{ ...item.lots[0], quantity: value }]
                  : item.lots,
            }
          : item,
      ),
    );
  };

  const handleStorageWarehouseChange = (stockItemId, value) => {
    const warehouseId = value ? Number(value) : null;
    setStorageItemsForm((prev) =>
      prev.map((item) =>
        item.stock_item_id === stockItemId
          ? { ...item, storage_warehouse_id: warehouseId }
          : item,
      ),
    );
    if (warehouseId) {
      setLastWarehouseSelection(stockItemId, warehouseId);
    }
  };

  const toggleStorageLots = (stockItemId) => {
    setStorageItemsForm((prev) =>
      prev.map((item) => {
        if (item.stock_item_id !== stockItemId) return item;
        const nextEnabled = !item.lotsEnabled;
        if (!nextEnabled) {
          return { ...item, lotsEnabled: false, lots: [] };
        }
        const initialQuantity = item.inputValue || item.storage_quantity || item.quantity_ordered || "";
        const nextLots = item.lots.length ? item.lots : [createStorageLot(initialQuantity)];
        return { ...item, lotsEnabled: true, lots: nextLots };
      }),
    );
  };

  const handleStorageLotChange = (stockItemId, lotId, field, value) => {
    setStorageItemsForm((prev) =>
      prev.map((item) => {
        if (item.stock_item_id !== stockItemId) return item;
        const nextLots = item.lots.map((lot) =>
          lot.id === lotId ? { ...lot, [field]: value } : lot,
        );
        return { ...item, lots: nextLots };
      }),
    );
  };

  const addStorageLot = (stockItemId) => {
    setStorageItemsForm((prev) =>
      prev.map((item) => {
        if (item.stock_item_id !== stockItemId) return item;
        return { ...item, lots: [...item.lots, createStorageLot("")] };
      }),
    );
  };

  const removeStorageLot = (stockItemId, lotId) => {
    setStorageItemsForm((prev) =>
      prev.map((item) => {
        if (item.stock_item_id !== stockItemId) return item;
        return { ...item, lots: item.lots.filter((lot) => lot.id !== lotId) };
      }),
    );
  };

  const submitStoragePayload = async (payload, updateReferencePrices = false) => {
    const response = await fetch(
      `/api/waise/purchase-orders/${storageModalOrder.id}/store`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          update_reference_prices: updateReferencePrices,
        }),
      },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Impossible de stocker ce bon.");
    }
  };

  const finishStorageSuccess = async () => {
    showToast("success", "Stockage enregistré.");
    setPurchasePriceConfirmation(null);
    closeStorageModal();
    await fetchPurchaseOrders();
    if (ingredientsState.loaded) {
      fetchStockItems("ingredient");
    }
    if (consumablesState.loaded) {
      fetchStockItems("consumable");
    }
    if (resoldStockState.loaded) {
      fetchResoldStockItems();
    }
    fetchDashboard();
    fetchWarehouses();
  };

  const buildStoragePayload = () => {
    const payloadItems = storageItemsForm.map((item) => {
      const rawValue = parseDecimalInput(item.inputValue);
      const maxPieces = Number(item.pieces_ordered ?? item.quantity_ordered) || 0;
      const safeValue = Math.max(0, Math.min(maxPieces, rawValue));
      const lots =
        item.lotsEnabled && Array.isArray(item.lots)
          ? item.lots.map((lot) => ({
              quantity: parseDecimalInput(lot.quantity),
              batch_number: lot.batch_number,
              lot_sku: lot.lot_sku,
              expiry_date: lot.expiry_date || null,
            }))
          : [];
      return {
        stock_item_id: item.stock_item_id,
        storage_quantity: storageMode === "custom" ? safeValue : undefined,
        storage_warehouse_id: item.storage_warehouse_id || null,
        lots,
      };
    });

    return {
      mode: storageMode === "custom" ? "custom" : "auto",
      items: payloadItems,
    };
  };

  const handleStoragePriceDecision = async (updateReferencePrices) => {
    if (!purchasePriceConfirmation) return;
    if ((purchasePriceConfirmation.mode || "storage") === "purchase_order") {
      setIsSavingPurchaseOrder(true);
      try {
        await submitPurchaseOrderPayload(purchasePriceConfirmation.payload, updateReferencePrices);
        setPurchasePriceConfirmation(null);
        await finishPurchaseOrderSuccess();
      } catch (err) {
        console.error("❌ Erreur bon de commande:", err);
        showToast("error", err.message || "Erreur lors de la création du bon.");
      } finally {
        setIsSavingPurchaseOrder(false);
      }
      return;
    }
    setIsSavingStorage(true);
    try {
      await submitStoragePayload(purchasePriceConfirmation.payload, updateReferencePrices);
      await finishStorageSuccess();
    } catch (err) {
      console.error("❌ Erreur stockage bon:", err);
      showToast("error", err.message || "Impossible d'enregistrer le stockage.");
      setIsSavingStorage(false);
    }
  };

  const handleConfirmStorage = async () => {
    if (!storageModalOrder) return;
    setIsSavingStorage(true);
    try {
      const payload = buildStoragePayload();
      await submitStoragePayload(payload, false);
      await finishStorageSuccess();
    } catch (err) {
      console.error("❌ Erreur stockage bon:", err);
      showToast("error", err.message || "Impossible d'enregistrer le stockage.");
      setIsSavingStorage(false);
    }
  };

  const resetRecipeModal = () => {
    setRecipeForm({ name: "", sku: "", produced_quantity: "1" });
    setRecipeItems([]);
    setRecipeTarget(null);
    setShowRecipeModal(false);
    setIsSavingRecipe(false);
    setRecipeModalLoading(false);
    setCurrentRecipeId(null);
    setIsDeletingRecipe(false);
    setRecipeHasExisting(false);
    setRecipeModalReadOnly(false);
  };

  const openRecipeModal = async (item, type, options = {}) => {
    const readOnly = Boolean(options.readOnly);
    const baseName = item.baseName || item.name;
    setRecipeTarget({
      id: item.productId || item.extraId || item.id,
      type,
      label: baseName,
      hasRecipe: Boolean(item.hasRecipe),
      variationId: item.variationId || item.variation_id || null,
    });
    setRecipeForm({
      name: baseName || "",
      sku: "",
      produced_quantity: "1",
    });
    setRecipeItems([]);
    setCurrentRecipeId(null);
    setRecipeHasExisting(false);
    setShowRecipeModal(true);
    setRecipeModalLoading(true);
    const loaders = [];
    if (!ingredientsState.loaded && !ingredientsState.loading) {
      loaders.push(fetchStockItems("ingredient"));
    }
    if (!consumablesState.loaded && !consumablesState.loading) {
      loaders.push(fetchStockItems("consumable"));
    }
    if (!resoldStockState.loaded && !resoldStockState.loading) {
      loaders.push(fetchResoldStockItems());
    }
    try {
      if (loaders.length) {
        await Promise.all(loaders);
      }
      const queryParams = new URLSearchParams();
      if (type === "extra") {
        queryParams.set("extra_id", item.extraId || item.id);
      } else {
        queryParams.set("product_id", item.productId || item.id);
        if (item.variationId || item.variation_id) {
          queryParams.set("variation_id", item.variationId || item.variation_id);
        }
      }
      const query = queryParams.toString();
      const response = await fetch(`/api/waise/recipes?${query}`);
      if (!response.ok) {
        throw new Error("Impossible de charger les recettes existantes.");
      }
      const recipes = await response.json();
      if (recipes.length) {
        populateRecipeForm(recipes[0]);
      } else {
        setRecipeHasExisting(false);
        setCurrentRecipeId(null);
        setRecipeTarget((prev) => (prev ? { ...prev, hasRecipe: false } : prev));
      }
    } catch (err) {
      console.error("❌ Erreur chargement recette:", err);
      showToast("error", err.message || "Chargement de la recette impossible.");
    } finally {
      setRecipeModalLoading(false);
    }
  };

  const populateRecipeForm = (recipe) => {
    setCurrentRecipeId(recipe.id);
    setRecipeForm({
      name: recipe.name || "",
      sku: recipe.sku || "",
      produced_quantity: recipe.produced_quantity ? String(recipe.produced_quantity) : "1",
    });
    const preparedItems = (recipe.items || []).map((item) => ({
      id: `existing-${item.id}`,
      stock_item_id: item.stock_item_id,
      name: item.stock_name,
      quantity: item.quantity ? String(item.quantity) : "1",
      yield_percent: item.yield_percent ? String(item.yield_percent) : "100",
      unit_symbol: item.unit_symbol || "",
      unit_id: item.unit_id,
      cost_price: Number(item.cost_price) || 0,
      categoryType: item.stock_type === "consumable" ? "consumable" : "ingredient",
    }));
    setRecipeItems(preparedItems);
    setRecipeTarget((prev) => (prev ? { ...prev, hasRecipe: true } : prev));
    setRecipeHasExisting(true);
  };

  const closeRecipeModal = () => {
    if (isSavingRecipe) return;
    resetRecipeModal();
  };

  const handleRecipeFormChange = (field, value) => {
    setRecipeForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddRecipeStockItem = (type, stockItemId) => {
    if (recipeModalReadOnly) return;
    const source =
      type === "consumable"
        ? consumablesState.data || []
        : type === "resold"
          ? resoldStockState.data || []
          : ingredientsState.data || [];
    const stockItem = source.find((entry) => String(entry.id) === String(stockItemId));
    if (!stockItem) return;
    const exists = recipeItems.some((item) => item.stock_item_id === stockItem.id);
    if (exists) {
      showToast("info", "Cet article est déjà dans la recette.");
      return;
    }
    setRecipeItems((prev) => [
      ...prev,
      {
        id: `${type}-${stockItem.id}`,
        stock_item_id: stockItem.id,
        name: stockItem.name,
        quantity: "1",
        yield_percent: "100",
        unit_symbol: stockItem.unit_symbol || stockItem.unit_name || "",
        unit_id: stockItem.unit_id,
        cost_price: Number(stockItem.cost_price) || 0,
        categoryType: type,
      },
    ]);
  };

  const handleRecipeItemChange = (itemId, field, value) => {
    if (recipeModalReadOnly) return;
    setRecipeItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );
  };

  const removeRecipeItem = (itemId) => {
    if (recipeModalReadOnly) return;
    setRecipeItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const computeRecipeLineCost = (item) => {
    const quantity = parseDecimalInput(item.quantity) || 0;
    const yieldPercent = parseDecimalInput(item.yield_percent) || 100;
    const safeYield = yieldPercent > 0 ? yieldPercent : 100;
    const effectiveQuantity = safeYield >= 100 ? quantity : quantity / (safeYield / 100);
    return effectiveQuantity * (item.cost_price || 0);
  };

  const resolveCurrentRecipeItemCost = useCallback(
    (item) => {
      const stockItemId = String(item?.stock_item_id || "");
      const source = [
        ...(ingredientsState.data || []),
        ...(consumablesState.data || []),
        ...(resoldStockState.data || []),
      ].find((entry) => String(entry.id) === stockItemId);
      if (!source) return Number(item?.cost_price) || 0;
      const weightedCost = Number(source.weighted_avg_cost);
      const unitCost = weightedCost > 0 ? weightedCost : Number(source.cost_price);
      return Number.isFinite(unitCost) ? unitCost : Number(item?.cost_price) || 0;
    },
    [ingredientsState.data, consumablesState.data, resoldStockState.data],
  );

  useEffect(() => {
    if (!recipeItems.length) return;
    setRecipeItems((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        const nextCost = resolveCurrentRecipeItemCost(item);
        if (Number(item.cost_price) === nextCost) return item;
        changed = true;
        return { ...item, cost_price: nextCost };
      });
      return changed ? next : prev;
    });
  }, [recipeItems.length, resolveCurrentRecipeItemCost]);

  const recipeTotals = useMemo(() => {
    const totalCost = recipeItems.reduce((sum, item) => sum + computeRecipeLineCost(item), 0);
    const producedQty = parseDecimalInput(recipeForm.produced_quantity) || 1;
    return {
      totalCost,
      costPerUnit: totalCost / (producedQty || 1),
    };
  }, [recipeItems, recipeForm.produced_quantity]);

  const handleRecipeSubmit = async () => {
    if (!recipeTarget) return;
    if (!recipeItems.length) {
      showToast("error", "Ajoutez au moins un ingrédient ou consommable.");
      return;
    }
    setIsSavingRecipe(true);
    try {
      const isEditing = Boolean(currentRecipeId);
      const payload = {
        name: recipeForm.name.trim(),
        sku: recipeForm.sku.trim() || null,
        produced_quantity: parseDecimalInput(recipeForm.produced_quantity) || 1,
        product_id: recipeTarget.type === "product" ? recipeTarget.id : null,
        variation_id: recipeTarget.type === "product" ? recipeTarget.variationId || null : null,
        extra_id: recipeTarget.type === "extra" ? recipeTarget.id : null,
        items: recipeItems.map((item) => ({
          stock_item_id: item.stock_item_id,
          quantity: parseDecimalInput(item.quantity) || 0,
          yield_percent: parseDecimalInput(item.yield_percent) || 100,
          unit_id: item.unit_id,
        })),
      };
      const endpoint = isEditing ? `/api/waise/recipes/${currentRecipeId}` : "/api/waise/recipes";
      const method = isEditing ? "PUT" : "POST";
      const response = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible d'enregistrer la recette.");
      }
      showToast("success", isEditing ? "Recette mise à jour." : "Recette enregistrée.");
      updateRecipePresence(recipeTarget.id, recipeTarget.type, true);
      resetRecipeModal();
      fetchDashboard();
      fetchRecipesList();
    } catch (err) {
      console.error("❌ Erreur recette:", err);
      showToast("error", err.message || "Impossible d'enregistrer la recette.");
      setIsSavingRecipe(false);
    }
  };

  const handleRecipeDelete = async () => {
    if (!currentRecipeId || !recipeTarget || isDeletingRecipe) return;
    setIsDeletingRecipe(true);
    try {
      const response = await fetch(`/api/waise/recipes/${currentRecipeId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de supprimer la recette.");
      }
      updateRecipePresence(recipeTarget.id, recipeTarget.type, false);
      showToast("success", "Recette supprimée.");
      resetRecipeModal();
      fetchDashboard();
      fetchRecipesList();
    } catch (err) {
      console.error("❌ Suppression recette:", err);
      showToast("error", err.message || "Impossible de supprimer la recette.");
      setIsDeletingRecipe(false);
    }
  };

  const handleRecipeCardDelete = async (recipe) => {
    if (!recipe?.id) return;
    if (typeof window !== "undefined") {
      const confirmed = window.confirm(`Supprimer la recette "${recipe.name}" ?`);
      if (!confirmed) return;
    }
    setDeletingRecipeId(recipe.id);
    try {
      const response = await fetch(`/api/waise/recipes/${recipe.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de supprimer la recette.");
      }
      updateRecipePresence(recipe.product_id, "product", false);
      showToast("success", "Recette supprimée.");
      fetchRecipesList();
      fetchDashboard();
    } catch (err) {
      console.error("❌ Suppression recette (carte):", err);
      showToast("error", err.message || "Suppression impossible.");
    } finally {
      setDeletingRecipeId(null);
    }
  };

  const formatDateValue = (value, withTime = false) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    const datePart = date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    if (!withTime) {
      return datePart;
    }
    const timePart = date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart} · ${timePart}`;
  };

  const renderPurchaseOrderModal = () => {
    const supplierOptions = suppliersState.data || [];
    const createdDate = purchaseOrderForm.created_at
      ? new Date(purchaseOrderForm.created_at)
      : null;
    const formattedCreated = createdDate
      ? `${createdDate.toLocaleDateString("fr-FR")} • ${createdDate.toLocaleTimeString("fr-FR")}`
      : "—";
    const numericDeliveryFee = parseDecimalInput(deliveryFee);
    const additionalFeesTotal = additionalCharges.reduce(
      (sum, charge) => sum + parseDecimalInput(charge.amount),
      0,
    );
    const purchaseItemsSubtotal = purchaseItems.reduce((sum, item) => {
      const quantity = parseDecimalInput(item.quantity);
      const unitCost = parseDecimalInput(item.unit_cost ?? item.cost);
      return sum + quantity * unitCost;
    }, 0);
    const estimatedTotal = purchaseItemsSubtotal + numericDeliveryFee + additionalFeesTotal;

    const isEditing = Boolean(editingPurchaseOrder);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <style>
          {`
          @keyframes waiseModalAppear {
            0% { opacity: 0; transform: scale(0.95) translateY(12px); }
            100% { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}
        </style>
        <div
          className={`w-full max-w-4xl rounded-3xl border ${
            isDarkMode ? "bg-[#050c1b] border-white/10 text-white" : "bg-white border-slate-200"
          } shadow-2xl animate-[waiseModalAppear_0.35s_ease]`}
        >
          <div
            className={`rounded-t-3xl px-6 py-5 ${
              isDarkMode
                ? "bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-amber-500/10"
                : "bg-gradient-to-r from-orange-50 via-amber-50 to-white"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                  {isEditing ? "Modifier un bon de commande" : "Nouveau bon de commande"}
                </p>
                <h3 className="text-2xl font-semibold">
                  {isEditing
                    ? editingPurchaseOrder?.orderNumber || "Bon de commande"
                    : "Créer un bon"}
                </h3>
                <p className="text-sm text-white/70">
                  {isEditing
                    ? "Mettez à jour les informations de ce bon."
                    : "Préparez votre bon avant l'envoi au fournisseur."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeNewPurchaseOrderModal}
                className={`rounded-full p-2 ${
                  isDarkMode
                    ? "bg-white/10 hover:bg-white/20"
                    : "bg-slate-100 hover:bg-slate-200"
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="space-y-6 p-6 max-h-[85vh] overflow-y-auto">
            <section
              className={`rounded-2xl p-5 ${
                isDarkMode ? "bg-white/5 border border-white/10" : "bg-white border border-slate-200"
              } space-y-4`}
            >
              <h4 className="text-lg font-semibold">Informations générales</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold block mb-1">Fournisseur *</label>
                  <select
                    value={purchaseOrderForm.supplier_id}
                    onChange={(e) => handlePurchaseOrderFieldChange("supplier_id", e.target.value)}
                    className={`w-full rounded-2xl px-4 py-2 border text-sm ${
                      isDarkMode
                        ? "bg-white/5 border-white/10 text-white"
                        : "bg-white border-slate-200"
                    }`}
                  >
                    <option value="">Sélectionner un fournisseur</option>
                    {supplierOptions.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Statut</label>
                  <select
                    value={purchaseOrderForm.status}
                    onChange={(e) => handlePurchaseOrderFieldChange("status", e.target.value)}
                    className={`w-full rounded-2xl px-4 py-2 border text-sm ${
                      isDarkMode
                        ? "bg-white/5 border-white/10 text-white"
                        : "bg-white border-slate-200"
                    }`}
                  >
                    {PURCHASE_ORDER_STATUSES.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.label}
                      </option>
                    ))}
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Créé le</label>
                  <div
                    className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                      isDarkMode
                        ? "bg-white/5 border-white/10 text-white"
                        : "bg-slate-50 border-slate-200 text-slate-700"
                    }`}
                  >
                    {formattedCreated}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Description</label>
                  <textarea
                    rows={3}
                    value={purchaseOrderForm.description}
                    onChange={(e) => handlePurchaseOrderFieldChange("description", e.target.value)}
                    className={`w-full rounded-2xl px-4 py-2 border text-sm ${
                      isDarkMode
                        ? "bg-white/5 border-white/10 text-white"
                        : "bg-white border-slate-200"
                    }`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-semibold block mb-1">Date prévue</label>
                    <input
                      type="date"
                      value={purchaseOrderForm.scheduled_date}
                      onChange={(e) => handlePurchaseOrderFieldChange("scheduled_date", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2 border text-sm ${
                        isDarkMode
                          ? "bg-white/5 border-white/10 text-white"
                          : "bg-white border-slate-200"
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold block mb-1">Heure prévue</label>
                    <input
                      type="time"
                      value={purchaseOrderForm.scheduled_time}
                      onChange={(e) => handlePurchaseOrderFieldChange("scheduled_time", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2 border text-sm ${
                        isDarkMode
                          ? "bg-white/5 border-white/10 text-white"
                          : "bg-white border-slate-200"
                      }`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-semibold block mb-1">Date d'arrivée</label>
                    <input
                      type="date"
                      value={purchaseOrderForm.arrival_date}
                      onChange={(e) => handlePurchaseOrderFieldChange("arrival_date", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2 border text-sm ${
                        isDarkMode
                          ? "bg-white/5 border-white/10 text-white"
                          : "bg-white border-slate-200"
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold block mb-1">Heure d'arrivée</label>
                    <input
                      type="time"
                      value={purchaseOrderForm.arrival_time}
                      onChange={(e) => handlePurchaseOrderFieldChange("arrival_time", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2 border text-sm ${
                        isDarkMode
                          ? "bg-white/5 border-white/10 text-white"
                          : "bg-white border-slate-200"
                      }`}
                    />
                  </div>
                </div>
              </div>
            </section>

            {purchaseOrderForm.supplier_id && (
              <section
                className={`rounded-2xl p-5 space-y-4 ${
                  isDarkMode ? "bg-white/5 border border-white/10" : "bg-white border border-slate-200"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="text-lg font-semibold">Articles du bon</h4>
                    <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                      Sélectionnez les articles liés au fournisseur.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowSupplierCatalog((prev) => !prev)}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold border border-white/10 bg-white/5"
                  >
                    <Plus className="w-4 h-4" />
                    {showSupplierCatalog ? "Masquer la liste" : "Ajouter des articles"}
                  </button>
                </div>

                {showSupplierCatalog && (
                  <div
                    className={`rounded-2xl border border-dashed ${
                      isDarkMode ? "border-white/15 bg-white/5" : "border-amber-200/60 bg-amber-50/30"
                    } p-4 space-y-3`}
                  >
                    {supplierCatalogState.loading ? (
                      <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                        Chargement des articles…
                      </p>
                    ) : supplierCatalogState.error ? (
                      <p className="text-sm text-rose-400">{supplierCatalogState.error}</p>
                    ) : supplierCatalogState.items.length ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-1">
                        {supplierCatalogState.items.map((catalogItem) => {
                          const isSelected = purchaseItems.some((item) => item.id === catalogItem.id);
                          const typeLabel =
                            catalogItem.type === "consumable"
                              ? "Consommable"
                              : catalogItem.type === "ingredient"
                                ? "Ingrédient"
                                : "Article";
                          return (
                            <div
                              key={catalogItem.id}
                              className={`rounded-2xl border px-4 py-3 ${
                                isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                              } flex items-center justify-between gap-3`}
                            >
                              <div>
                                <p className="text-sm font-semibold">{catalogItem.name}</p>
                                <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                                  {typeLabel} • {catalogItem.category || "Sans catégorie"}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleAttachSupplierItem(catalogItem)}
                                disabled={isSelected}
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                  isSelected
                                    ? "bg-emerald-500/20 text-emerald-200 cursor-not-allowed"
                                    : "bg-amber-400/20 text-amber-300 hover:bg-amber-400/30"
                                }`}
                              >
                                {isSelected ? "Ajouté" : "Ajouter"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                        Ce fournisseur n'a aucun article relié pour le moment.
                      </p>
                    )}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
                      <tr>
                        {["Article", "Lieu de stockage", "Coût d'achat", "Qté", "Total", "Remise"].map(
                          (column) => (
                            <th
                              key={column}
                              className={`px-4 py-3 text-left text-xs uppercase tracking-wide ${
                                isDarkMode ? "text-white/60" : "text-slate-500"
                              }`}
                            >
                              {column}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseItems.length === 0 ? (
                        <tr>
                          <td colSpan={6} className={`px-4 py-10 text-center ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                            Aucun article sélectionné pour ce fournisseur.
                          </td>
                        </tr>
                      ) : (
                        purchaseItems.map((item) => {
                          const quantityValue = item.quantity ?? "";
                          const unitCost = parseDecimalInput(item.unit_cost ?? item.cost);
                          const lineQuantity = parseDecimalInput(quantityValue);
                          const lineTotal = lineQuantity * unitCost;
                          const packagingQty = Math.max(1, Number(item.packaging_qty) || 1);
                          const hasPackagingOptions = (item.packaging_options || []).length > 0;
                          return (
                            <tr key={item.id} className="border-t border-white/5">
                              <td className="px-4 py-3">
                                <div className="font-semibold">{item.name}</div>
                                {hasPackagingOptions && (
                                  <select
                                    value={item.packaging_id || ""}
                                    onChange={(e) =>
                                      handlePurchaseItemChange(item.id, "packaging_id", e.target.value)
                                    }
                                    className={`mt-2 w-full rounded-xl px-3 py-1 border text-xs ${
                                      isDarkMode
                                        ? "bg-white/5 border-white/10 text-white"
                                        : "bg-white border-slate-200"
                                    }`}
                                  >
                                    {(item.packaging_options || []).map((packaging) => (
                                      <option key={packaging.id} value={packaging.id}>
                                        {packaging.packaging_unit} · {packaging.packaging_qty} pièces
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {item.unit_label && (
                                  <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                                    Unité : {item.unit_label}
                                  </p>
                                )}
                              </td>
                              <td className={`px-4 py-3 text-sm ${
                                isDarkMode ? "text-white/70" : "text-slate-600"
                              }`}>{item.storage || "—"}</td>
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className={`w-28 rounded-xl px-3 py-1 border ${
                                    isDarkMode
                                      ? "bg-white/5 border-white/10 text-white"
                                      : "bg-white border-slate-200"
                                  }`}
                                  value={item.unit_cost ?? ""}
                                  onChange={(e) =>
                                    handlePurchaseItemChange(item.id, "unit_cost", e.target.value)
                                  }
                                />
                                {item.unit_price_changed && (
                                  <p className="mt-1 text-[11px] font-semibold text-amber-300">modifié</p>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className={`w-24 rounded-xl px-3 py-1 border ${
                                    isDarkMode
                                      ? "bg-white/5 border-white/10 text-white"
                                      : "bg-white border-slate-200"
                                  }`}
                                  value={quantityValue}
                                  onChange={(e) =>
                                    handlePurchaseItemChange(item.id, "quantity", e.target.value)
                                  }
                                />
                                {packagingQty > 1 && (
                                  <p className={`mt-1 text-[11px] ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                                    = {numberFormatter.format(lineQuantity * packagingQty)} pièces
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3 font-semibold">
                                {currencyFormatter.format(lineTotal)}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className={`text-xs font-semibold px-3 py-1 rounded-full border ${
                                      isDarkMode
                                        ? "border-white/20 text-white/70"
                                        : "border-slate-200 text-slate-600"
                                    }`}
                                  >
                                    Ajouter une remise
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePurchaseItem(item.id)}
                                    className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
                                    title="Retirer cet article"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <section
              className={`rounded-2xl p-5 space-y-4 ${
                isDarkMode ? "bg-white/5 border border-white/10" : "bg-white border border-slate-200"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-lg font-semibold">Récapitulatif</h4>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/10 bg-white/5"
                    onClick={() => handleAddAdditionalCharge()}
                  >
                    Autres frais
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/10 bg-white/5"
                  >
                    Frais de livraison
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className={isDarkMode ? "text-white/60" : "text-slate-500"}>Sous-total</span>
                  <span className="font-semibold">
                    {currencyFormatter.format(purchaseItemsSubtotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <label className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Frais de livraison
                  </label>
                  <input
                    type="number"
                    value={deliveryFee}
                    onChange={(e) => setDeliveryFee(e.target.value)}
                    className={`w-40 rounded-xl px-3 py-2 border text-sm ${
                      isDarkMode
                        ? "bg-white/5 border-white/10 text-white"
                      : "bg-white border-slate-200"
                    }`}
                    placeholder="0"
                  />
                </div>
                {additionalCharges.map((charge) => (
                  <div key={charge.id} className="flex items-center gap-3">
                    <input
                      type="text"
                      placeholder="Intitulé du frais"
                      value={charge.label}
                      onChange={(e) => handleAdditionalChargeChange(charge.id, "label", e.target.value)}
                      className={`flex-1 rounded-xl px-3 py-2 border text-sm ${
                        isDarkMode
                          ? "bg-white/5 border-white/10 text-white"
                          : "bg-white border-slate-200"
                      }`}
                    />
                    <input
                      type="number"
                      placeholder="Montant"
                      value={charge.amount}
                      onChange={(e) => handleAdditionalChargeChange(charge.id, "amount", e.target.value)}
                      className={`w-40 rounded-xl px-3 py-2 border text-sm ${
                        isDarkMode
                          ? "bg-white/5 border-white/10 text-white"
                          : "bg-white border-slate-200"
                      }`}
                    />
                  </div>
                ))}
                <div className="flex items-center justify-between text-base font-semibold pt-4 border-t border-white/10">
                  <span>Total estimé</span>
                  <span>{currencyFormatter.format(estimatedTotal)}</span>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={closeNewPurchaseOrderModal}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode
                    ? "border border-white/10 text-white/80"
                    : "border border-slate-200 text-slate-600"
                }`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSavePurchaseOrder}
                disabled={isSavingPurchaseOrder}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
              >
                {isSavingPurchaseOrder
                  ? "Enregistrement…"
                  : isEditing
                    ? "Mettre à jour"
                    : "Créer le bon"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStorageModal = () => {
    if (!storageModalOrder) return null;
    const inputDisabled = storageMode !== "custom" || storageModalLoading;
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div
          className={`w-full max-w-3xl rounded-3xl border ${
            isDarkMode ? "bg-[#050c1b] border-white/10 text-white" : "bg-white border-slate-200"
          } shadow-2xl`}
        >
          <div
            className={`rounded-t-3xl px-6 py-4 flex items-center justify-between ${
              isDarkMode ? "bg-gradient-to-r from-cyan-500/10 to-emerald-500/10" : "bg-cyan-50"
            }`}
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Stockage des articles</p>
              <h3 className="text-xl font-semibold mt-1">Bon {storageModalOrder.orderNumber}</h3>
              <p className={`text-sm ${isDarkMode ? "text-white/70" : "text-slate-600"}`}>
                Choisissez comment les articles sont stockés physiquement.
              </p>
            </div>
            <button
              type="button"
              onClick={closeStorageModal}
              className={`rounded-full p-2 ${
                isDarkMode ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"
              }`}
            >
              <X size={16} />
            </button>
          </div>
          <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="space-y-2">
              <label className="text-sm font-semibold block">Mode de stockage</label>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="radio"
                    className="h-4 w-4"
                    checked={storageMode === "auto"}
                    onChange={() => setStorageMode("auto")}
                  />
                  Stocker comme livré
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="radio"
                    className="h-4 w-4"
                    checked={storageMode === "custom"}
                    onChange={() => setStorageMode("custom")}
                  />
                    Stockage personnalisé
                </label>
              </div>
              <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                Si vous choisissez le mode personnalisé, indiquez la quantité réellement stockée pour chaque article.
              </p>
            </div>
            {storageModalLoading ? (
              <div
                className={`py-10 text-center text-sm ${
                  isDarkMode ? "text-white/60" : "text-slate-500"
                }`}
              >
                Chargement des articles…
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
                    <tr>
                      {["Article", "Quantité commandée", "Stock (stockage)", "Entrepôt"].map((header) => (
                        <th
                          key={header}
                          className={`px-4 py-3 text-left text-xs uppercase tracking-wide ${
                            isDarkMode ? "text-white/60" : "text-slate-500"
                          }`}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {storageItemsForm.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className={`px-4 py-6 text-center text-sm ${
                            isDarkMode ? "text-white/60" : "text-slate-500"
                          }`}
                        >
                          Aucun article pour ce bon.
                        </td>
                      </tr>
                    ) : (
                      storageItemsForm.map((item) => {
                        const lotsTotal = (item.lots || []).reduce(
                          (sum, lot) => sum + (Number(lot.quantity) || 0),
                          0,
                        );
                        const targetQty = Number(parseDecimalInput(item.inputValue)) || 0;
                        const lotsMismatch =
                          item.lotsEnabled && item.lots.length > 0 && Math.abs(lotsTotal - targetQty) > 0.001;
                        return (
                          <React.Fragment key={item.stock_item_id}>
                            <tr className={isDarkMode ? "border-b border-white/5" : "border-b border-slate-100"}>
                              <td className="px-4 py-3">
                                <div className="font-semibold">{item.name}</div>
                                <p className="text-xs text-white/50">
                                  Conditionnement : {item.packaging_unit || item.unit_symbol || "—"}
                                  {item.packaging_qty > 1 ? ` · ${item.packaging_qty} pièces` : ""}
                                </p>
                              </td>
                              <td className="px-4 py-3">
                                {numberFormatter.format(item.quantity_ordered)} {item.packaging_unit || item.unit_symbol || ""}
                                {item.packaging_qty > 1 && (
                                  <p className="text-xs text-white/50">
                                    = {numberFormatter.format(item.pieces_ordered)} pièces
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  disabled={inputDisabled}
                                  value={item.inputValue}
                                  onChange={(e) => handleStorageQuantityChange(item.stock_item_id, e.target.value)}
                                  className={`w-32 rounded-xl px-3 py-1 border ${
                                    isDarkMode
                                      ? "bg-white/5 border-white/10 text-white disabled:text-white/40"
                                      : "bg-white border-slate-200 text-slate-800 disabled:text-slate-400"
                                  }`}
                                />
                              </td>
                              <td className="px-4 py-3 space-y-2">
                                <select
                                  value={item.storage_warehouse_id || ""}
                                  onChange={(e) =>
                                    handleStorageWarehouseChange(item.stock_item_id, e.target.value)
                                  }
                                  className={`w-full rounded-xl px-3 py-2 border ${
                                    isDarkMode
                                      ? "bg-white/5 border-white/10 text-white"
                                      : "bg-white border-slate-200 text-slate-800"
                                  }`}
                                >
                                  <option value="">Choisir un entrepôt</option>
                                  {(warehousesState.data || []).map((warehouse) => (
                                    <option key={warehouse.id} value={warehouse.id}>
                                      {warehouse.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => toggleStorageLots(item.stock_item_id)}
                                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                                    item.lotsEnabled
                                      ? "bg-amber-400 text-slate-900"
                                      : isDarkMode
                                        ? "bg-white/5 text-white/70 border border-white/10"
                                        : "bg-slate-100 text-slate-600 border border-slate-200"
                                  }`}
                                >
                                  {item.lotsEnabled ? "Lots / expirations activés" : "Lots / expirations"}
                                </button>
                              </td>
                            </tr>
                            {item.lotsEnabled && (
                              <tr className={isDarkMode ? "border-b border-white/5" : "border-b border-slate-100"}>
                                <td colSpan={4} className="px-4 py-4">
                                  <div className={`rounded-2xl p-4 ${isDarkMode ? "bg-white/5" : "bg-slate-50"} space-y-3`}>
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-sm font-semibold">Lots & expirations</p>
                                        <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                                          Saisissez un ou plusieurs lots (SKU lot optionnel). Total lots = quantité stockée.
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => addStorageLot(item.stock_item_id)}
                                        className="rounded-full bg-emerald-400/90 px-3 py-1 text-xs font-semibold text-slate-900"
                                      >
                                        + Ajouter un lot
                                      </button>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className={isDarkMode ? "text-white/60" : "text-slate-500"}>
                                            <th className="px-2 py-1 text-left">Quantité</th>
                                            <th className="px-2 py-1 text-left">Lot</th>
                                            <th className="px-2 py-1 text-left">SKU lot (optionnel)</th>
                                            <th className="px-2 py-1 text-left">Expiration</th>
                                            <th className="px-2 py-1 text-right"></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {item.lots.map((lot) => (
                                            <tr key={lot.id} className={isDarkMode ? "border-t border-white/10" : "border-t border-slate-200"}>
                                              <td className="px-2 py-2">
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  min="0"
                                                  value={lot.quantity}
                                                  onChange={(e) =>
                                                    handleStorageLotChange(
                                                      item.stock_item_id,
                                                      lot.id,
                                                      "quantity",
                                                      e.target.value,
                                                    )
                                                  }
                                                  className={`w-24 rounded-lg px-2 py-1 border ${
                                                    isDarkMode
                                                      ? "bg-white/5 border-white/10 text-white"
                                                      : "bg-white border-slate-200 text-slate-800"
                                                  }`}
                                                />
                                              </td>
                                              <td className="px-2 py-2">
                                                <input
                                                  type="text"
                                                  value={lot.batch_number}
                                                  onChange={(e) =>
                                                    handleStorageLotChange(
                                                      item.stock_item_id,
                                                      lot.id,
                                                      "batch_number",
                                                      e.target.value,
                                                    )
                                                  }
                                                  placeholder="Auto si vide"
                                                  className={`w-40 rounded-lg px-2 py-1 border ${
                                                    isDarkMode
                                                      ? "bg-white/5 border-white/10 text-white"
                                                      : "bg-white border-slate-200 text-slate-800"
                                                  }`}
                                                />
                                              </td>
                                              <td className="px-2 py-2">
                                                <input
                                                  type="text"
                                                  value={lot.lot_sku}
                                                  onChange={(e) =>
                                                    handleStorageLotChange(
                                                      item.stock_item_id,
                                                      lot.id,
                                                      "lot_sku",
                                                      e.target.value,
                                                    )
                                                  }
                                                  placeholder="Optionnel"
                                                  className={`w-40 rounded-lg px-2 py-1 border ${
                                                    isDarkMode
                                                      ? "bg-white/5 border-white/10 text-white"
                                                      : "bg-white border-slate-200 text-slate-800"
                                                  }`}
                                                />
                                              </td>
                                              <td className="px-2 py-2">
                                                <input
                                                  type="date"
                                                  value={lot.expiry_date || ""}
                                                  onChange={(e) =>
                                                    handleStorageLotChange(
                                                      item.stock_item_id,
                                                      lot.id,
                                                      "expiry_date",
                                                      e.target.value,
                                                    )
                                                  }
                                                  className={`rounded-lg px-2 py-1 border ${
                                                    isDarkMode
                                                      ? "bg-white/5 border-white/10 text-white"
                                                      : "bg-white border-slate-200 text-slate-800"
                                                  }`}
                                                />
                                              </td>
                                              <td className="px-2 py-2 text-right">
                                                {item.lots.length > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => removeStorageLot(item.stock_item_id, lot.id)}
                                                    className="text-red-300 hover:text-red-200"
                                                  >
                                                    Retirer
                                                  </button>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    {lotsMismatch && (
                                      <p className="text-xs text-amber-300">
                                        La somme des lots ({numberFormatter.format(lotsTotal)}) ne correspond pas à la quantité à stocker ({numberFormatter.format(targetQty)}).
                                      </p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
            <button
              type="button"
              onClick={closeStorageModal}
              className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                isDarkMode ? "border border-white/10 text-white/80" : "border border-slate-200 text-slate-600"
              }`}
              disabled={isSavingStorage}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirmStorage}
              disabled={isSavingStorage || storageModalLoading}
              className="rounded-2xl bg-gradient-to-r from-emerald-400 to-teal-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
            >
              {isSavingStorage ? "Enregistrement…" : "Confirmer le stockage"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPurchasePriceConfirmation = () => {
    if (!purchasePriceConfirmation) return null;
    const isPurchaseOrderConfirmation = (purchasePriceConfirmation.mode || "storage") === "purchase_order";
    const isSavingPriceDecision = isSavingStorage || isSavingPurchaseOrder;
    return (
      <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div
          className={`w-full max-w-xl rounded-3xl border ${
            isDarkMode ? "bg-[#050c1b] border-white/10 text-white" : "bg-white border-slate-200"
          } shadow-2xl`}
        >
          <div className="p-6 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-amber-300">Prix d'achat modifié</p>
              <h3 className="text-xl font-semibold mt-1">Mettre à jour les prix de référence ?</h3>
              <p className={`text-sm mt-2 ${isDarkMode ? "text-white/70" : "text-slate-600"}`}>
                {isPurchaseOrderConfirmation
                  ? "Ces prix seront enregistrés sur ce bon dans tous les cas. Choisissez si MOO Sync doit aussi les conserver comme nouveaux prix de référence."
                  : "Ces prix seront utilisés pour cette réception dans tous les cas. Choisissez si MOO Sync doit aussi les conserver comme nouveaux prix de référence."}
              </p>
            </div>
            <div className={`rounded-2xl border ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"} divide-y ${isDarkMode ? "divide-white/10" : "divide-slate-200"}`}>
              {purchasePriceConfirmation.changes.map((change) => (
                <div key={change.stock_item_id} className="px-4 py-3 text-sm">
                  <div className="font-semibold">{change.name}</div>
                  <div className={`${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    {currencyFormatter.format(change.previous)} → {currencyFormatter.format(change.next)}
                    {change.packaging_unit ? ` / ${change.packaging_unit}` : ""}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => handleStoragePriceDecision(false)}
                disabled={isSavingPriceDecision}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode ? "border border-white/10 text-white/80" : "border border-slate-200 text-slate-600"
                }`}
              >
                Non, ce bon seulement
              </button>
              <button
                type="button"
                onClick={() => handleStoragePriceDecision(true)}
                disabled={isSavingPriceDecision}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50"
              >
                Oui, mettre à jour
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRecipeModal = () => {
    if (!recipeTarget) return null;
    const getInventoryKind = (item) =>
      String(item?.linked_inventory_kind || item?.inventory_kind || "").toLowerCase();
    const isRecipeComponentAllowed = (item) => getInventoryKind(item) !== "composed";
    const ingredientOptions = (ingredientsState.data || []).filter(isRecipeComponentAllowed);
    const consumableOptions = (consumablesState.data || []).filter(isRecipeComponentAllowed);
    const isRecipeIngredientFlagged = (item) =>
      item?.is_recipe_ingredient === true ||
      item?.is_recipe_ingredient === 1 ||
      item?.is_recipe_ingredient === "1";
    const resoldOptions = (resoldStockState.data || []).filter(
      (item) => getInventoryKind(item) === "resold" && isRecipeIngredientFlagged(item),
    );
    const ingredientsRows = recipeItems.filter((item) => item.categoryType === "ingredient");
    const consumablesRows = recipeItems.filter((item) => item.categoryType === "consumable");
    const resoldRows = recipeItems.filter((item) => item.categoryType === "resold");
    const recipeInputsDisabled = recipeModalReadOnly || isSavingRecipe || isDeletingRecipe;
    const renderTable = (rows, typeLabel) => (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold">{typeLabel}</h4>
          <div className="flex items-center gap-2">
            <select
              className={`rounded-2xl px-4 py-2 text-sm border ${
                isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
              }`}
              defaultValue=""
              disabled={recipeInputsDisabled}
              onChange={(e) => {
                if (!e.target.value) return;
                handleAddRecipeStockItem(typeLabel === "Consommables" ? "consumable" : typeLabel === "Revendus" ? "resold" : "ingredient", e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">Ajouter…</option>
              {(typeLabel === "Consommables" ? consumableOptions : typeLabel === "Revendus" ? resoldOptions : ingredientOptions).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
              <tr>
                { ["Article", "Quantité", "Rendement %", "Coût"].map((header) => (
                  <th
                    key={`${typeLabel}-${header}`}
                    className={`px-4 py-2 text-left text-xs uppercase tracking-wide ${
                      isDarkMode ? "text-white/60" : "text-slate-500"
                    }`}
                  >
                    {header}
                  </th>
                )) }
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className={`px-4 py-6 text-center text-sm ${
                    isDarkMode ? "text-white/60" : "text-slate-500"
                  }`}>
                    Aucun élément {typeLabel.toLowerCase()} pour cette recette.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const lineCost = computeRecipeLineCost(row);
                  return (
                    <tr key={row.id} className={isDarkMode ? "border-b border-white/5" : "border-b border-slate-100"}>
                      <td className="px-4 py-2">
                        <div className="font-semibold">{row.name}</div>
                        <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                          Coût unitaire : {currencyFormatter.format(row.cost_price || 0)}
                        </p>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={row.quantity}
                          onChange={(e) => handleRecipeItemChange(row.id, "quantity", e.target.value)}
                          disabled={recipeInputsDisabled}
                          className={`w-28 rounded-xl px-3 py-1 border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                        />
                        <p className="text-xs mt-1 text-white/50">{row.unit_symbol}</p>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.1"
                          min="1"
                          value={row.yield_percent}
                          onChange={(e) => handleRecipeItemChange(row.id, "yield_percent", e.target.value)}
                          disabled={recipeInputsDisabled}
                          className={`w-24 rounded-xl px-3 py-1 border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{currencyFormatter.format(lineCost)}</span>
                          <button
                            type="button"
                            onClick={() => removeRecipeItem(row.id)}
                            disabled={recipeInputsDisabled}
                            className="rounded-full border border-white/10 p-1 text-rose-400 hover:border-rose-400/60 disabled:opacity-50"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
    const canDeleteRecipe = recipeHasExisting && Boolean(currentRecipeId);

    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="relative w-full max-w-4xl">
          <div
            className={`relative rounded-3xl border ${
              isDarkMode ? "bg-[#050c1b] border-white/10 text-white" : "bg-white border-slate-200"
            } shadow-2xl`}
          >
            <div
              className={`rounded-t-3xl px-6 py-4 flex items-center justify-between ${
                isDarkMode ? "bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10" : "bg-amber-50"
              }`}
            >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                {recipeModalReadOnly ? "Voir la recette" : "Créer une recette"}
              </p>
              <h3 className="text-xl font-semibold mt-1">{recipeTarget?.label || "Recette"}</h3>
              <p className={`text-xs ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
                Déclarez les ingrédients nécessaires pour produire cet article.
              </p>
            </div>
            <button
              type="button"
              onClick={closeRecipeModal}
              className={`rounded-full p-2 ${
                isDarkMode ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"
              }`}
            >
              <X size={16} />
            </button>
          </div>
            <div className="p-6 space-y-6 max-h-[85vh] overflow-y-auto">
              <section className={`rounded-2xl border ${
                isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
              } p-5 space-y-4`}>
              <h4 className="text-lg font-semibold">A. Informations générales</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold block mb-1">Nom de la recette *</label>
                  <input
                    type="text"
                    value={recipeForm.name}
                    onChange={(e) => handleRecipeFormChange("name", e.target.value)}
                    disabled={recipeInputsDisabled}
                    className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                      isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                    }`}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">SKU interne</label>
                  <input
                    type="text"
                    value={recipeForm.sku}
                    onChange={(e) => handleRecipeFormChange("sku", e.target.value)}
                    disabled={recipeInputsDisabled}
                    className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                      isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                    }`}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold block mb-1">Quantité produite *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={recipeForm.produced_quantity}
                    onChange={(e) => handleRecipeFormChange("produced_quantity", e.target.value)}
                    disabled={recipeInputsDisabled}
                    className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                      isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                    }`}
                  />
                  <p className="text-xs text-white/60 mt-1">Quantité de produit fini pour cette recette.</p>
                </div>
              </div>
            </section>
            <section className={`rounded-2xl border ${
              isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
            } p-5 space-y-4`}>
              <h4 className="text-lg font-semibold">B. Ingrédients, consommables & revendus</h4>
              {renderTable(ingredientsRows, "Ingrédients")}
              {renderTable(consumablesRows, "Consommables")}
              {renderTable(resoldRows, "Revendus")}
            </section>
            <section className={`rounded-2xl border ${
              isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
            } p-5 space-y-3`}>
              <h4 className="text-lg font-semibold">C. Résultats</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className={`rounded-2xl px-4 py-3 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
                  <p className={isDarkMode ? "text-white/60" : "text-slate-500"}>Coût total</p>
                  <p className="text-xl font-bold">{preciseCurrencyFormatter.format(recipeTotals.totalCost || 0)}</p>
                </div>
                <div className={`rounded-2xl px-4 py-3 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
                  <p className={isDarkMode ? "text-white/60" : "text-slate-500"}>Coût unitaire</p>
                  <p className="text-xl font-bold">{preciseCurrencyFormatter.format(recipeTotals.costPerUnit || 0)}</p>
                </div>
              </div>
            </section>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                type="button"
                onClick={closeRecipeModal}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode ? "border border-white/10 text-white/80" : "border border-slate-200 text-slate-600"
                } disabled:opacity-50`}
                disabled={isSavingRecipe || isDeletingRecipe}
              >
                {recipeModalReadOnly ? "Fermer" : "Annuler"}
              </button>
              {!recipeModalReadOnly && canDeleteRecipe && (
                <button
                  type="button"
                  onClick={handleRecipeDelete}
                  disabled={isSavingRecipe || isDeletingRecipe}
                  className="rounded-2xl border border-rose-500/40 px-4 py-2 text-sm font-semibold text-rose-200 hover:border-rose-400/80 disabled:opacity-50"
                >
                  {isDeletingRecipe ? "Suppression…" : "Supprimer"}
                </button>
              )}
              {!recipeModalReadOnly && (
                <button
                  type="button"
                  onClick={handleRecipeSubmit}
                  disabled={isSavingRecipe || isDeletingRecipe}
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
                >
                  {isSavingRecipe ? "Enregistrement…" : "Enregistrer la recette"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSimulatorModal = () => {
    const getInventoryKind = (item) =>
      String(item?.linked_inventory_kind || item?.inventory_kind || "").toLowerCase();
    const isRecipeComponentAllowed = (item) => getInventoryKind(item) !== "composed";
    const ingredientOptions = (ingredientsState.data || []).filter(isRecipeComponentAllowed);
    const consumableOptions = (consumablesState.data || []).filter(isRecipeComponentAllowed);
    const ingredientRows = simulatorItems.filter((item) => item.categoryType !== "consumable");
    const consumableRows = simulatorItems.filter((item) => item.categoryType === "consumable");
    const isExisting = simulatorMode === "existing";
    const results = simulatorResult?.results || [];
    const blocking = simulatorResult?.blocking || [];
    const renderTable = (rows, typeLabel) => (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold">{typeLabel}</h4>
          {!isExisting && (
            <select
              className={`rounded-2xl px-4 py-2 text-sm border ${
                isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
              }`}
              defaultValue=""
              onChange={(e) => {
                if (!e.target.value) return;
                handleAddSimulatorItem(typeLabel === "Consommables" ? "consumable" : "ingredient", e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">Ajouter…</option>
              {(typeLabel === "Consommables" ? consumableOptions : ingredientOptions).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
              <tr>
                {["Article", "Quantité", "Rendement %", "Stock dispo", !isExisting ? "Action" : null]
                  .filter(Boolean)
                  .map((header) => (
                    <th
                      key={`${typeLabel}-${header}`}
                      className={`px-4 py-2 text-left text-xs uppercase tracking-wide ${
                        isDarkMode ? "text-white/60" : "text-slate-500"
                      }`}
                    >
                      {header}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className={`px-4 py-6 text-center text-sm ${
                    isDarkMode ? "text-white/60" : "text-slate-500"
                  }`}>
                    Aucun élément {typeLabel.toLowerCase()} dans la simulation.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const available =
                    (Number(row.stock_quantity) || 0) + (Number(row.storage_stock_quantity) || 0);
                  return (
                    <tr key={row.id} className={isDarkMode ? "border-b border-white/5" : "border-b border-slate-100"}>
                      <td className="px-4 py-2">
                        <div className="font-semibold">{row.name}</div>
                        <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                          {row.unit_symbol || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={row.quantity}
                          onChange={(e) => handleSimulatorItemChange(row.id, "quantity", e.target.value)}
                          disabled={isExisting}
                          className={`w-28 rounded-xl px-3 py-1 border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.1"
                          min="1"
                          value={row.yield_percent}
                          onChange={(e) => handleSimulatorItemChange(row.id, "yield_percent", e.target.value)}
                          disabled={isExisting}
                          className={`w-24 rounded-xl px-3 py-1 border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <span className="font-semibold">
                          {decimalFormatter.format(available)} {row.unit_symbol}
                        </span>
                      </td>
                      {!isExisting && (
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => removeSimulatorItem(row.id)}
                            className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
                          >
                            <X size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );

    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="relative w-full max-w-5xl">
          <div
            className={`relative rounded-3xl border ${
              isDarkMode ? "bg-[#050c1b] border-white/10 text-white" : "bg-white border-slate-200"
            } shadow-2xl`}
          >
            <div
              className={`rounded-t-3xl px-6 py-4 flex items-center justify-between ${
                isDarkMode ? "bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10" : "bg-amber-50"
              }`}
            >
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                  Simulateur de recettes
                </p>
                <h3 className="text-xl font-semibold mt-1">
                  {isExisting ? "Simulation d'une recette existante" : "Simulation libre"}
                </h3>
                <p className={`text-xs ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
                  Vérifiez la faisabilité avant de lancer la production.
                </p>
              </div>
              <button
                type="button"
                onClick={resetSimulatorModal}
                className={`rounded-full p-2 ${
                  isDarkMode ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"
                }`}
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-6 space-y-6 max-h-[85vh] overflow-y-auto">
              <section
                className={`rounded-2xl border ${
                  isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                } p-5 space-y-4`}
              >
                <h4 className="text-lg font-semibold">A. Informations générales</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  {isExisting ? (
                    <>
                      <div className="space-y-1">
                        <label className="text-sm font-semibold block">Recette</label>
                        <select
                          className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                          value={simulatorForm.recipeId}
                          onChange={(e) => handleSimulatorRecipeSelect(e.target.value)}
                        >
                          {(recipesState.items || []).map((recipe) => (
                            <option key={recipe.id} value={recipe.id}>
                              {recipe.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-semibold block mb-1">Quantité produite par recette</label>
                        <input
                          type="number"
                          value={simulatorForm.produced_quantity}
                          disabled
                          className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                        />
                        <p className="text-xs text-white/60 mt-1">
                          Valeur issue de la recette existante (non modifiable ici).
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm font-semibold block mb-1">Nom de la recette</label>
                        <input
                          type="text"
                          value={simulatorForm.name}
                          onChange={(e) => handleSimulatorFormChange("name", e.target.value)}
                          className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold block mb-1">Quantité produite *</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={simulatorForm.produced_quantity}
                          onChange={(e) => handleSimulatorFormChange("produced_quantity", e.target.value)}
                          className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="text-sm font-semibold block mb-1">Quantité à produire *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={simulatorForm.quantity}
                      onChange={(e) => handleSimulatorFormChange("quantity", e.target.value)}
                      className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                        isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                      }`}
                    />
                  </div>
                </div>
              </section>

              <section
                className={`rounded-2xl border ${
                  isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                } p-5 space-y-4`}
              >
                <h4 className="text-lg font-semibold">B. Ingrédients & consommables</h4>
                {renderTable(ingredientRows, "Ingrédients")}
                {renderTable(consumableRows, "Consommables")}
              </section>

              {simulatorResult && (
                <section
                  className={`rounded-2xl border ${
                    isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                  } p-5 space-y-4`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        blocking.length
                          ? "bg-rose-500/20 text-rose-200"
                          : "bg-emerald-500/20 text-emerald-200"
                      }`}
                    >
                      {blocking.length ? <AlertTriangle className="w-5 h-5" /> : <Activity className="w-5 h-5" />}
                    </div>
                    <div>
                      <h4 className="text-base font-semibold">
                        {blocking.length
                          ? "Stock insuffisant pour cette quantité."
                          : "Production possible avec les stocks actuels."}
                      </h4>
                      <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                        {blocking.length
                          ? "Complétez les articles manquants pour produire la quantité demandée."
                          : "Les stocks couvrent la production demandée."}
                      </p>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-white/10">
                    <table className="w-full text-sm">
                      <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
                        <tr>
                          {["Article", "Stock actuel", "Quantité utilisée", "Stock restant"].map((header) => (
                            <th
                              key={header}
                              className={`px-4 py-2 text-left text-xs uppercase tracking-wide ${
                                isDarkMode ? "text-white/60" : "text-slate-500"
                              }`}
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {results.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className={`px-4 py-6 text-center text-sm ${
                                isDarkMode ? "text-white/60" : "text-slate-500"
                              }`}
                            >
                              Aucune donnée de simulation.
                            </td>
                          </tr>
                        ) : (
                          results.map((row, idx) => (
                            <tr key={`${row.name}-${idx}`} className={isDarkMode ? "border-b border-white/5" : "border-b border-slate-100"}>
                              <td className="px-4 py-2 font-semibold">{row.name}</td>
                              <td className="px-4 py-2">
                                {decimalFormatter.format(row.available)} {row.unit}
                              </td>
                              <td className="px-4 py-2">
                                {decimalFormatter.format(row.used)} {row.unit}
                              </td>
                              <td className="px-4 py-2">
                                {decimalFormatter.format(row.remaining)} {row.unit}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {blocking.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Ingrédients bloquants</p>
                      <div className="overflow-x-auto rounded-2xl border border-white/10">
                        <table className="w-full text-sm">
                          <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
                            <tr>
                              {["Article", "Stock actuel", "Besoin", "Manque"].map((header) => (
                                <th
                                  key={`block-${header}`}
                                  className={`px-4 py-2 text-left text-xs uppercase tracking-wide ${
                                    isDarkMode ? "text-white/60" : "text-slate-500"
                                  }`}
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {blocking.map((row, idx) => (
                              <tr key={`block-row-${idx}`} className={isDarkMode ? "border-b border-white/5" : "border-b border-slate-100"}>
                                <td className="px-4 py-2 font-semibold">{row.name}</td>
                                <td className="px-4 py-2">
                                  {decimalFormatter.format(row.available)} {row.unit}
                                </td>
                                <td className="px-4 py-2">
                                  {decimalFormatter.format(row.needed)} {row.unit}
                                </td>
                                <td className="px-4 py-2 text-rose-300">
                                  {decimalFormatter.format(row.missing)} {row.unit}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                type="button"
                onClick={resetSimulatorModal}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode ? "border border-white/10 text-white/80" : "border border-slate-200 text-slate-600"
                }`}
              >
                Fermer
              </button>
              <button
                type="button"
                onClick={handleRunSimulation}
                disabled={!simulatorItems.length}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              >
                Lancer la simulation
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInventory = () => {
    const inventoryCards = [
      {
        id: "products",
        title: "Produits",
        caption: isBoutique ? "Articles suivis pour la boutique" : "Articles & extras avec suivi activé",
        helper: isBoutique ? "Catalogue produits suivi" : "Configuration du catalogue suivi",
        icon: Package,
      },
      {
        id: "resold",
        title: "Produits revendus",
        caption: "Fournisseurs & réassort",
        helper: "Catalogue revendu (auto)",
        icon: Link2,
      },
      {
        id: "ingredients",
        title: "Ingrédients",
        caption: "Base de recettes pour le menu",
        helper: "Gestion des composants & nomenclatures",
        icon: Activity,
      },
      {
        id: "consumables",
        title: "Consommables",
        caption: "Emballages et fournitures",
        helper: "Pilotage des consommables du site",
        icon: Truck,
      },
      {
        id: "overview",
        title: "Aperçu et valeur du stock",
        caption: "Vue synthétique des quantités & valeurs",
        helper: "Indicateurs financiers du stock",
        icon: BarChart3,
      },
      {
        id: "reconciliation",
        title: "Inventaire et rapprochement",
        caption: "Comptages, écarts et validations",
        helper: "Processus d'inventaire sécurisé",
        icon: ClipboardList,
      },
      {
        id: "losses",
        title: "Pertes",
        caption: "Déclarer et consulter les pertes de stock",
        helper: "Casse, expiration, vol, erreur...",
        icon: Trash2,
      },
    ];

    const visibleInventoryCards = isBoutique
      ? inventoryCards.filter((card) => !["ingredients", "consumables", "resold"].includes(card.id))
      : inventoryCards;

    const renderStockItemsSection = (sectionType) => {
      const isIngredientSection = sectionType === "ingredients";
      const state = isIngredientSection ? ingredientsState : consumablesState;
      const normalizedType = isIngredientSection ? "ingredient" : "consumable";
      const emptyMessage = isIngredientSection
        ? "Aucun ingrédient n'a encore été défini."
        : "Aucun consommable n'a encore été défini.";
      const uniqueUnits = new Set(
        state.data.map((item) => item.unit_symbol || item.unit_name || item.unit_id),
      ).size;
      const categoriesCount = new Set(
        state.data.map((item) => (item.category || "Sans catégorie").toLowerCase()),
      ).size;

      return (
        <div className="space-y-6">
          <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setInventorySection(null)}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                    isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                    {isIngredientSection
                      ? "Gestion des composants & nomenclatures"
                      : "Pilotage des consommables du site"}
                  </p>
                  <h3 className="text-lg font-semibold">
                    {isIngredientSection ? "Ingrédients suivis" : "Consommables suivis"}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    {isIngredientSection
                      ? "Ajoutez les composants utilisés dans vos recettes et contrôlez leurs unités."
                      : "Centralisez vos emballages et fournitures pour anticiper les besoins."}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {state.error && <span className="text-sm text-rose-400">{state.error}</span>}
                <button
                  type="button"
                  onClick={() => openStockItemModal(normalizedType)}
                  className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow"
                >
                  <Plus className="w-4 h-4" />
                  {isIngredientSection ? "Ajouter un ingrédient" : "Ajouter un consommable"}
                </button>
                <button
                  type="button"
                  onClick={() => fetchStockItems(normalizedType)}
                  disabled={state.loading}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                    isDarkMode ? "bg-white/5 text-white" : "bg-slate-900 text-white"
                  } disabled:opacity-60`}
                >
                  <RefreshCw className={`w-4 h-4 ${state.loading ? "animate-spin" : ""}`} />
                  Rafraîchir
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <TabStatCard
                icon={Package}
                label={isIngredientSection ? "Ingrédients" : "Consommables"}
                value={state.data.length}
                helper="Référencés"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={Scale}
                label="Unités"
                value={uniqueUnits}
                helper="Unités de mesure utilisées"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={ClipboardList}
                label="Catégories"
                value={categoriesCount}
                helper="Groupes déclarés"
                isDarkMode={isDarkMode}
              />
            </div>

            {state.loading && !state.loaded ? (
              <div className="py-10 text-center text-sm text-slate-500">
                Chargement des {isIngredientSection ? "ingrédients" : "consommables"}…
              </div>
            ) : state.data.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {state.data.map((item) => (
                  <StockItemCard
                    key={item.id}
                    item={item}
                    isDarkMode={isDarkMode}
                    onEdit={() => openStockItemModal(normalizedType, item)}
                  />
                ))}
              </div>
            ) : (
              <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                {emptyMessage}
              </p>
            )}
          </section>
        </div>
      );
    };

    if (!isBoutique && (inventorySection === "ingredients" || inventorySection === "consumables")) {
      return renderStockItemsSection(inventorySection);
    }

    if (!isBoutique && inventorySection === "resold") {
      const resoldItems = (trackedCatalogItems || []).filter(
        (item) => item && item.inventoryKind === "resold",
      );
      const stockByProductId = new Map();
      const stockByVariationId = new Map();
      (resoldStockState.data || []).forEach((stockItem) => {
        const productId = Number(stockItem.product_id);
        if (Number.isFinite(productId) && productId > 0 && !stockByProductId.has(productId)) {
          stockByProductId.set(productId, stockItem);
        }
        const variationId = Number(stockItem.variation_id);
        if (Number.isFinite(variationId) && variationId > 0 && !stockByVariationId.has(variationId)) {
          stockByVariationId.set(variationId, stockItem);
        }
      });
      const entries = resoldItems.map((item) => {
        const variationId = Number(item.variationId);
        const productId = Number(item.productId);
        const stockItem =
          (Number.isFinite(variationId) && variationId > 0
            ? stockByVariationId.get(variationId)
            : null) ||
          stockByProductId.get(productId) ||
          null;
        return {
          productId,
          variationId: Number.isFinite(variationId) && variationId > 0 ? variationId : null,
          name: item.type === "variation"
            ? [item.baseName, item.name].filter(Boolean).join(" - ")
            : item.baseName || item.name || "Produit",
          category: item.category || "Sans catégorie",
          destination: item.destination || "cuisine",
          hasVariations: item.type === "variation",
          supplierIds: Array.isArray(item.supplierIds) ? item.supplierIds : [],
          stockItem,
        };
      }).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "fr", {
          sensitivity: "base",
        }),
      );

      return (
        <div className="space-y-6">
          <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setInventorySection(null)}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                    isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                    Catalogue revendu (auto)
                  </p>
                  <h3 className="text-lg font-semibold">Produits revendus</h3>
                  <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Liste alimentée depuis Admin Produits (articles suivis marqués "revendus").
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={fetchDashboard}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                    isDarkMode ? "bg-white/5 text-white" : "bg-slate-900 text-white"
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  Rafraîchir
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <TabStatCard
                icon={Package}
                label="Produits"
                value={entries.length}
                helper="Revendus suivis"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={Users}
                label="Fournisseurs"
                value={
                  entries.filter((e) => (e.supplierIds || []).length > 0).length
                }
                helper="Produits liés"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={ClipboardList}
                label="Variantes"
                value={entries.filter((e) => e.hasVariations).length}
                helper="Produits variables"
                isDarkMode={isDarkMode}
              />
            </div>

            {resoldStockState.error && (
              <p className="text-sm text-rose-400">{resoldStockState.error}</p>
            )}

            {entries.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {entries.map((entry) => (
                  <ResoldProductCard
                    key={`resold-${entry.productId}-${entry.variationId || "base"}`}
                    entry={entry}
                    stockItem={entry.stockItem}
                    suppliers={suppliersState.data || []}
                    warehouses={warehousesState.data || []}
                    isDarkMode={isDarkMode}
                    onLinkSuppliers={() =>
                      openProductSupplierModal({
                        productId: entry.productId,
                        name: entry.name,
                      })
                    }
                    onSaved={fetchResoldStockItems}
                  />
                ))}
              </div>
            ) : (
              <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                Aucun produit revendu suivi pour le moment.
              </p>
            )}
          </section>
        </div>
      );
    }

    if (inventorySection === "overview") {
      const mutedText = isDarkMode ? "text-white/60" : "text-slate-500";
      const backButtonClass = `inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
        isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
      }`;
      const selectClass = `rounded-xl px-3 py-1.5 text-sm border ${
        isDarkMode
          ? "bg-white/5 border-white/10 text-white"
          : "bg-white border-slate-200 text-slate-900"
      }`;
      const valuation = valuationState.data || {};
      const summary = valuation.summary || {};
      const byWarehouse = valuation.by_warehouse || [];
      const byCategory = valuation.by_category || [];
      const items = valuation.items || [];
      const warehouses = warehousesState.data || [];

      const handleWarehouseFilterChange = (event) => {
        const value = event.target.value;
        setValuationWarehouseFilter(value);
        fetchStockValuation(value || null);
      };

      return (
        <div className="space-y-6">
          <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setInventorySection(null)}
                  className={backButtonClass}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                    Indicateurs financiers du stock
                  </p>
                  <h3 className="text-lg font-semibold">Aperçu et valeur du stock</h3>
                  <p className={`text-sm ${mutedText}`}>
                    Valorisation en temps réel des quantités physiques en stock, au coût moyen
                    pondéré (CMUP).
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={valuationWarehouseFilter}
                  onChange={handleWarehouseFilterChange}
                  className={selectClass}
                >
                  <option value="">Tous les entrepôts</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => fetchStockValuation(valuationWarehouseFilter || null)}
                  className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold bg-amber-500 text-white shadow"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </button>
              </div>
            </div>

            {valuationState.error && (
              <p className="text-sm text-rose-400">{valuationState.error}</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <TabStatCard
                icon={DollarSign}
                label="Valeur totale du stock"
                value={currencyFormatter.format(summary.total_value || 0)}
                helper="Quantité x coût moyen pondéré"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={Package}
                label="Articles valorisés"
                value={numberFormatter.format(summary.total_items || 0)}
                helper="Lignes stock x entrepôt"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={Truck}
                label="Entrepôts"
                value={numberFormatter.format(summary.warehouse_count || 0)}
                helper="Avec du stock valorisé"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={AlertTriangle}
                label="Sans coût configuré"
                value={numberFormatter.format(summary.items_without_cost || 0)}
                helper="Coût manquant : valeur sous-estimée"
                isDarkMode={isDarkMode}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className={`rounded-2xl p-4 ${isDarkMode ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-200"}`}>
                <p className="text-sm font-semibold mb-3">Valeur par entrepôt</p>
                {byWarehouse.length ? (
                  <div className="space-y-2">
                    {byWarehouse.map((row) => (
                      <div
                        key={row.warehouse_id}
                        className="flex items-center justify-between text-sm"
                      >
                        <div>
                          <p className="font-medium">{row.warehouse_name}</p>
                          <p className={`text-xs ${mutedText}`}>
                            {numberFormatter.format(row.item_count)} articles
                          </p>
                        </div>
                        <p className="font-semibold">
                          {currencyFormatter.format(row.total_value)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${mutedText}`}>Aucune donnée disponible.</p>
                )}
              </div>

              <div className={`rounded-2xl p-4 ${isDarkMode ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-200"}`}>
                <p className="text-sm font-semibold mb-3">Valeur par catégorie</p>
                {byCategory.length ? (
                  <div className="space-y-2">
                    {byCategory.map((row) => (
                      <div
                        key={row.category}
                        className="flex items-center justify-between text-sm"
                      >
                        <div>
                          <p className="font-medium">{row.category}</p>
                          <p className={`text-xs ${mutedText}`}>
                            {numberFormatter.format(row.item_count)} articles
                          </p>
                        </div>
                        <p className="font-semibold">
                          {currencyFormatter.format(row.total_value)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${mutedText}`}>Aucune donnée disponible.</p>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-3">Détail par article</p>
              {valuationState.loading ? (
                <p className={`text-sm ${mutedText}`}>Chargement...</p>
              ) : items.length ? (
                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead className={isDarkMode ? "bg-white/5" : "bg-slate-50"}>
                      <tr className="text-left">
                        <th className="px-4 py-2">Article</th>
                        <th className="px-4 py-2">Catégorie</th>
                        <th className="px-4 py-2">Entrepôt</th>
                        <th className="px-4 py-2 text-right">Quantité</th>
                        <th className="px-4 py-2 text-right">Coût unitaire</th>
                        <th className="px-4 py-2 text-right">Valeur totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr
                          key={`${item.stock_item_id}-${item.warehouse_id}`}
                          className={`border-t ${isDarkMode ? "border-white/10" : "border-slate-200"}`}
                        >
                          <td className="px-4 py-2 font-medium">
                            {item.item_name}
                            {!item.has_cost && (
                              <span className="ml-2 inline-flex rounded-full px-2 py-0.5 text-xs bg-amber-500/15 text-amber-300">
                                Coût manquant
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">{item.category}</td>
                          <td className="px-4 py-2">{item.warehouse_name}</td>
                          <td className="px-4 py-2 text-right">
                            {numberFormatter.format(item.quantity)} {item.unit_symbol || ""}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {preciseCurrencyFormatter.format(item.unit_cost)}
                          </td>
                          <td className="px-4 py-2 text-right font-semibold">
                            {preciseCurrencyFormatter.format(item.total_value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={`text-sm ${mutedText}`}>
                  Aucun article valorisé pour le moment.
                </p>
              )}
            </div>
          </section>
        </div>
      );
    }

    if (inventorySection === "losses") {
      const mutedText = isDarkMode ? "text-white/60" : "text-slate-500";
      const backButtonClass = `inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
        isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
      }`;
      const selectClass = `rounded-xl px-3 py-1.5 text-sm border ${
        isDarkMode
          ? "bg-white/5 border-white/10 text-white"
          : "bg-white border-slate-200 text-slate-900"
      }`;
      const inputClass = `w-full rounded-xl px-3 py-2 text-sm border ${
        isDarkMode
          ? "bg-white/5 border-white/10 text-white placeholder:text-white/30"
          : "bg-white border-slate-200 text-slate-900"
      }`;
      const warehouses = warehousesState.data || [];
      const valuationItems = valuationState.data?.items || [];
      const lossTypes = lossTypesState.data || [];
      const lossCauses = lossCausesState.data || [];
      const losses = lossesState.data || [];
      const lossValueOf = (row) =>
        Number(row.total_cost ?? (row.quantity || 0) * (row.unit_cost || 0)) || 0;
      const totalLossValue = losses.reduce((sum, row) => sum + lossValueOf(row), 0);
      const totalLossQuantity = losses.reduce(
        (sum, row) => sum + (Number(row.quantity) || 0),
        0,
      );

      const handleLossWarehouseFilterChange = (event) => {
        const value = event.target.value;
        setLossWarehouseFilter(value);
        fetchLosses(value || null);
      };

      const updateLossForm = (field, value) => {
        setLossForm((prev) => ({ ...prev, [field]: value }));
      };

      return (
        <div className="space-y-6">
          <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setInventorySection(null)}
                  className={backButtonClass}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                    Casse, expiration, vol, erreur...
                  </p>
                  <h3 className="text-lg font-semibold">Pertes</h3>
                  <p className={`text-sm ${mutedText}`}>
                    Déclarez une perte de stock et consultez l'historique des pertes
                    enregistrées.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <TabStatCard
                icon={Trash2}
                label="Pertes enregistrées"
                value={numberFormatter.format(losses.length)}
                helper="Historique complet"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={DollarSign}
                label="Valeur des pertes"
                value={currencyFormatter.format(totalLossValue)}
                helper="Coût total des pertes"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={AlertTriangle}
                label="Quantité perdue"
                value={numberFormatter.format(totalLossQuantity)}
                helper="Toutes unités confondues"
                isDarkMode={isDarkMode}
              />
            </div>

            <div className={`rounded-2xl p-4 space-y-4 ${isDarkMode ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-200"}`}>
              <p className="text-sm font-semibold">Déclarer une perte</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <select
                  value={lossForm.item_key}
                  onChange={(event) => updateLossForm("item_key", event.target.value)}
                  className={selectClass}
                >
                  <option value="">Article & entrepôt...</option>
                  {valuationItems.map((item) => (
                    <option
                      key={`${item.stock_item_id}::${item.warehouse_id}`}
                      value={`${item.stock_item_id}::${item.warehouse_id}`}
                    >
                      {item.item_name} — {item.warehouse_name} (
                      {numberFormatter.format(item.quantity)} {item.unit_symbol || ""})
                    </option>
                  ))}
                </select>
                <select
                  value={lossForm.loss_type_id}
                  onChange={(event) => updateLossForm("loss_type_id", event.target.value)}
                  className={selectClass}
                >
                  <option value="">Type de perte...</option>
                  {lossTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <select
                  value={lossForm.loss_cause_id}
                  onChange={(event) => updateLossForm("loss_cause_id", event.target.value)}
                  className={selectClass}
                >
                  <option value="">Cause (optionnel)...</option>
                  {lossCauses.map((cause) => (
                    <option key={cause.id} value={cause.id}>
                      {cause.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Quantité perdue"
                  value={lossForm.quantity}
                  onChange={(event) => updateLossForm("quantity", event.target.value)}
                  className={inputClass}
                />
                <input
                  type="text"
                  placeholder="Notes (optionnel)"
                  value={lossForm.notes}
                  onChange={(event) => updateLossForm("notes", event.target.value)}
                  className={`${inputClass} sm:col-span-2 lg:col-span-1`}
                />
                <button
                  type="button"
                  disabled={isSubmittingLoss}
                  onClick={submitLossDeclaration}
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold bg-amber-500 text-white shadow disabled:opacity-60"
                >
                  {isSubmittingLoss ? "Enregistrement..." : "Déclarer la perte"}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold">Historique des pertes</p>
                <div className="flex items-center gap-3">
                  <select
                    value={lossWarehouseFilter}
                    onChange={handleLossWarehouseFilterChange}
                    className={selectClass}
                  >
                    <option value="">Tous les entrepôts</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => fetchLosses(lossWarehouseFilter || null)}
                    className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold bg-amber-500 text-white shadow"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Actualiser
                  </button>
                </div>
              </div>

              {lossesState.error && (
                <p className="text-sm text-rose-400">{lossesState.error}</p>
              )}

              {lossesState.loading ? (
                <p className={`text-sm ${mutedText}`}>Chargement...</p>
              ) : losses.length ? (
                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead className={isDarkMode ? "bg-white/5" : "bg-slate-50"}>
                      <tr className="text-left">
                        <th className="px-4 py-2">Date</th>
                        <th className="px-4 py-2">Article</th>
                        <th className="px-4 py-2">Entrepôt</th>
                        <th className="px-4 py-2 text-right">Quantité</th>
                        <th className="px-4 py-2 text-right">Valeur</th>
                        <th className="px-4 py-2">Type</th>
                        <th className="px-4 py-2">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {losses.map((row) => (
                        <tr
                          key={row.id}
                          className={`border-t ${isDarkMode ? "border-white/10" : "border-slate-200"}`}
                        >
                          <td className="px-4 py-2">
                            {row.created_at
                              ? new Date(row.created_at).toLocaleString("fr-FR")
                              : "—"}
                          </td>
                          <td className="px-4 py-2 font-medium">{row.item_name}</td>
                          <td className="px-4 py-2">{row.warehouse_name}</td>
                          <td className="px-4 py-2 text-right">
                            {numberFormatter.format(row.quantity)}
                          </td>
                          <td className="px-4 py-2 text-right font-semibold">
                            {preciseCurrencyFormatter.format(lossValueOf(row))}
                          </td>
                          <td className="px-4 py-2">{row.reason || "—"}</td>
                          <td className="px-4 py-2">{row.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={`text-sm ${mutedText}`}>Aucune perte enregistrée pour le moment.</p>
              )}
            </div>
          </section>
        </div>
      );
    }

    if (inventorySection === "reconciliation") {
      const inventories = inventoriesState.data || [];
      const statusLabels = {
        in_progress: "En cours",
        completed: "Terminé",
        cancelled: "Annulé",
      };
      const statusStyles = {
        in_progress: "bg-amber-400/20 text-amber-300",
        completed: "bg-emerald-400/20 text-emerald-300",
        cancelled: "bg-rose-400/20 text-rose-300",
      };
      const mutedText = isDarkMode ? "text-white/60" : "text-slate-500";
      const backButtonClass = `inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
        isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
      }`;
      const inputClass = `rounded-xl px-3 py-1.5 text-sm border ${
        isDarkMode
          ? "bg-white/5 border-white/10 text-white placeholder-white/40"
          : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
      }`;

      if (reconciliationView === "count") {
        const items = countableItemsState.data || [];
        const countedMap = new Map(
          (activeInventoryDetail?.items || []).map((it) => [it.stock_item_id, it]),
        );
        const countedCount = items.filter((it) => countedMap.has(it.stock_item_id)).length;

        return (
          <div className="space-y-6">
            <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setReconciliationView("list")}
                    className={backButtonClass}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour
                  </button>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                      Comptage en cours
                    </p>
                    <h3 className="text-lg font-semibold">
                      {activeInventoryDetail?.name || "Inventaire"}
                    </h3>
                    <p className={`text-sm ${mutedText}`}>
                      {activeInventoryDetail?.warehouse_name || ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleCancelInventory}
                    disabled={isCancellingInventory || isFinalizingInventory}
                    className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-rose-500/10 text-rose-400 shadow disabled:opacity-50"
                  >
                    {isCancellingInventory ? "Annulation..." : "Annuler l'inventaire"}
                  </button>
                  <button
                    type="button"
                    onClick={handleFinalizeInventory}
                    disabled={isFinalizingInventory || isCancellingInventory || countedCount === 0}
                    className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-emerald-500 text-white shadow disabled:opacity-50"
                  >
                    {isFinalizingInventory ? "Finalisation..." : "Finaliser l'inventaire"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <TabStatCard
                  icon={Package}
                  label="Articles"
                  value={items.length}
                  helper="Dans cet entrepôt"
                  isDarkMode={isDarkMode}
                />
                <TabStatCard
                  icon={ClipboardList}
                  label="Comptés"
                  value={countedCount}
                  helper={`Sur ${items.length}`}
                  isDarkMode={isDarkMode}
                />
                <TabStatCard
                  icon={AlertTriangle}
                  label="Restants"
                  value={Math.max(items.length - countedCount, 0)}
                  helper="À compter"
                  isDarkMode={isDarkMode}
                />
              </div>

              <p className={`text-xs ${mutedText}`}>
                Comptage à l'aveugle : les quantités théoriques ne sont pas affichées pendant la
                saisie, pour ne pas influencer le comptage physique.
              </p>

              {countableItemsState.error && (
                <p className="text-sm text-rose-400">{countableItemsState.error}</p>
              )}

              {items.length ? (
                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
                      <tr>
                        {["Article", "Unité", "Quantité comptée", "Notes", "Statut", ""].map(
                          (header) => (
                            <th
                              key={header}
                              className="text-left px-4 py-3 text-xs uppercase tracking-wide font-semibold"
                            >
                              {header}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const counted = countedMap.get(item.stock_item_id);
                        const inputState = countInputs[item.stock_item_id] || {};
                        const currentValue =
                          inputState.value !== undefined
                            ? inputState.value
                            : counted
                              ? counted.counted_quantity
                              : "";
                        const currentNotes =
                          inputState.notes !== undefined
                            ? inputState.notes
                            : counted
                              ? counted.notes || ""
                              : "";
                        return (
                          <tr
                            key={item.stock_item_id}
                            className={isDarkMode ? "border-t border-white/5" : "border-t border-slate-100"}
                          >
                            <td className="px-4 py-3 font-medium">{item.item_name}</td>
                            <td className="px-4 py-3">{item.unit_symbol || "-"}</td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                step="0.001"
                                min="0"
                                value={currentValue}
                                onChange={(e) =>
                                  setCountInputs((prev) => ({
                                    ...prev,
                                    [item.stock_item_id]: {
                                      ...(prev[item.stock_item_id] || {}),
                                      value: e.target.value,
                                      notes: currentNotes,
                                      saved: false,
                                    },
                                  }))
                                }
                                className={`w-28 ${inputClass}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={currentNotes}
                                onChange={(e) =>
                                  setCountInputs((prev) => ({
                                    ...prev,
                                    [item.stock_item_id]: {
                                      ...(prev[item.stock_item_id] || {}),
                                      value: currentValue,
                                      notes: e.target.value,
                                      saved: false,
                                    },
                                  }))
                                }
                                placeholder="Optionnel"
                                className={`w-36 ${inputClass}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              {counted ? (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-400/20 text-emerald-300">
                                  Compté
                                </span>
                              ) : (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-400/20 text-slate-300">
                                  En attente
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="inline-flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleSaveCount(item.stock_item_id, currentValue, currentNotes)
                                  }
                                  disabled={inputState.saving}
                                  className="inline-flex items-center gap-1 text-amber-300 text-xs font-semibold disabled:opacity-50"
                                >
                                  {inputState.saving ? "..." : "Enregistrer"}
                                </button>
                                {counted && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteCountedItem(item.stock_item_id)}
                                    className="inline-flex items-center text-rose-400 hover:text-rose-300"
                                    title="Retirer ce comptage"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={`text-sm ${mutedText}`}>
                  {countableItemsState.loading
                    ? "Chargement..."
                    : "Aucun article suivi dans cet entrepôt."}
                </p>
              )}
            </section>
          </div>
        );
      }

      if (reconciliationView === "detail") {
        const detail = activeInventoryDetail;
        const detailItems = detail?.items || [];
        const totalValueDiff = detailItems.reduce(
          (sum, it) => sum + (Number(it.value_difference) || 0),
          0,
        );
        const totalItemsCount = Number(detail?.total_items) || 0;
        const countedItemsCount = Number(detail?.counted_items) || detailItems.length;
        const uncountedCount = Math.max(totalItemsCount - countedItemsCount, 0);

        return (
          <div className="space-y-6">
            <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setReconciliationView("list")}
                    className={backButtonClass}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour à l'historique
                  </button>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                      Inventaire terminé
                    </p>
                    <h3 className="text-lg font-semibold">{detail?.name || "Inventaire"}</h3>
                    <p className={`text-sm ${mutedText}`}>
                      {detail?.warehouse_name || ""}
                      {detail?.completed_at
                        ? ` · ${new Date(detail.completed_at).toLocaleString("fr-FR")}`
                        : ""}
                    </p>
                  </div>
                </div>
              </div>

              {activeInventoryLoading ? (
                <p className={`text-sm ${mutedText}`}>Chargement...</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <TabStatCard
                      icon={Package}
                      label="Articles comptés"
                      value={countedItemsCount}
                      helper={`Sur ${totalItemsCount} dans l'entrepôt`}
                      isDarkMode={isDarkMode}
                    />
                    <TabStatCard
                      icon={AlertTriangle}
                      label="Écarts"
                      value={detail?.discrepancies || 0}
                      helper="Ajustements appliqués"
                      isDarkMode={isDarkMode}
                    />
                    <TabStatCard
                      icon={BarChart3}
                      label="Valeur des écarts"
                      value={preciseCurrencyFormatter.format(totalValueDiff)}
                      helper="Cumulée"
                      isDarkMode={isDarkMode}
                    />
                  </div>

                  {uncountedCount > 0 && (
                    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-300">
                      {uncountedCount} article{uncountedCount > 1 ? "s" : ""} non compté
                      {uncountedCount > 1 ? "s" : ""} lors de cet inventaire.
                    </div>
                  )}

                  {detailItems.length ? (
                    <div className="overflow-x-auto rounded-2xl border border-white/10">
                      <table className="w-full text-sm">
                        <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
                          <tr>
                            {[
                              "Article",
                              "Théorique",
                              "Compté",
                              "Écart",
                              "Valeur écart",
                              "Compté par",
                            ].map((header) => (
                              <th
                                key={header}
                                className="text-left px-4 py-3 text-xs uppercase tracking-wide font-semibold"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {detailItems.map((it) => (
                            <tr
                              key={it.id}
                              className={isDarkMode ? "border-t border-white/5" : "border-t border-slate-100"}
                            >
                              <td className="px-4 py-3 font-medium">{it.item_name}</td>
                              <td className="px-4 py-3">
                                {numberFormatter.format(Number(it.expected_quantity) || 0)}{" "}
                                {it.unit_symbol}
                              </td>
                              <td className="px-4 py-3">
                                {numberFormatter.format(Number(it.counted_quantity) || 0)}{" "}
                                {it.unit_symbol}
                              </td>
                              <td
                                className={`px-4 py-3 font-semibold ${
                                  Number(it.difference) === 0
                                    ? ""
                                    : Number(it.difference) > 0
                                      ? "text-emerald-400"
                                      : "text-rose-400"
                                }`}
                              >
                                {Number(it.difference) > 0 ? "+" : ""}
                                {numberFormatter.format(Number(it.difference) || 0)}
                              </td>
                              <td className="px-4 py-3">
                                {preciseCurrencyFormatter.format(it.value_difference)}
                              </td>
                              <td className="px-4 py-3">{it.counted_by_name || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className={`text-sm ${mutedText}`}>Aucun article compté.</p>
                  )}
                </>
              )}
            </section>
          </div>
        );
      }

      return (
        <div className="space-y-6">
          <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setInventorySection(null)}
                  className={backButtonClass}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                    Inventaire & rapprochement
                  </p>
                  <h3 className="text-lg font-semibold">Historique des inventaires</h3>
                  <p className={`text-sm ${mutedText}`}>
                    Comptages physiques et écarts par entrepôt.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={fetchInventories}
                className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                  isDarkMode ? "bg-white/5 text-white" : "bg-slate-900 text-white"
                }`}
              >
                <RefreshCw className="w-4 h-4" />
                Rafraîchir
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <TabStatCard
                icon={ClipboardList}
                label="Inventaires"
                value={inventories.length}
                helper="Tous statuts"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={Activity}
                label="En cours"
                value={inventories.filter((i) => i.status === "in_progress").length}
                helper="À finaliser"
                isDarkMode={isDarkMode}
              />
              <TabStatCard
                icon={AlertTriangle}
                label="Écarts détectés"
                value={inventories.reduce((sum, i) => sum + (Number(i.discrepancies) || 0), 0)}
                helper="Cumulés (terminés)"
                isDarkMode={isDarkMode}
              />
            </div>

            <div
              className={`rounded-2xl border p-4 ${
                isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"
              }`}
            >
              <p className="text-sm font-semibold mb-3">Démarrer un nouvel inventaire</p>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={newInventoryWarehouseId}
                  onChange={(e) => setNewInventoryWarehouseId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Choisir un entrepôt...</option>
                  {(warehousesState.data || []).map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleCreateInventory}
                  disabled={isCreatingInventory || !newInventoryWarehouseId}
                  className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  {isCreatingInventory ? "Création..." : "Démarrer le comptage"}
                </button>
              </div>
            </div>

            {inventoriesState.error && (
              <p className="text-sm text-rose-400">{inventoriesState.error}</p>
            )}

            {inventories.length ? (
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className={isDarkMode ? "bg-white/5" : "bg-slate-100"}>
                    <tr>
                      {[
                        "Inventaire",
                        "Entrepôt",
                        "Statut",
                        "Démarré le",
                        "Comptés / Total",
                        "Écarts",
                        "",
                      ].map((header) => (
                        <th
                          key={header}
                          className="text-left px-4 py-3 text-xs uppercase tracking-wide font-semibold"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {inventories.map((inv) => (
                      <tr
                        key={inv.id}
                        className={isDarkMode ? "border-t border-white/5" : "border-t border-slate-100"}
                      >
                        <td className="px-4 py-3 font-medium">{inv.name}</td>
                        <td className="px-4 py-3">{inv.warehouse_name}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              statusStyles[inv.status] || "bg-slate-400/20 text-slate-300"
                            }`}
                          >
                            {statusLabels[inv.status] || inv.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {inv.started_at
                            ? new Date(inv.started_at).toLocaleString("fr-FR")
                            : "-"}
                        </td>
                        <td className="px-4 py-3">
                          {inv.counted_items || 0} / {inv.total_items || 0}
                        </td>
                        <td className="px-4 py-3">{inv.discrepancies || 0}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleOpenInventory(inv)}
                            className="inline-flex items-center gap-1 text-amber-300 text-xs font-semibold"
                          >
                            <Eye className="w-4 h-4" />
                            Ouvrir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={`text-sm ${mutedText}`}>
                {inventoriesState.loading ? "Chargement..." : "Aucun inventaire pour le moment."}
              </p>
            )}
          </section>
        </div>
      );
    }

    if (inventorySection === "catalog") {
      return (
        <div className="space-y-6">
          <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-5`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setInventorySection(null)}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                    isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                    {isBoutique ? "Catalogue produits suivi" : "Configuration du catalogue suivi"}
                  </p>
                  <h3 className="text-lg font-semibold">
                    {isBoutique ? "Produits suivis" : "Produits & extras suivis"}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    {isBoutique
                      ? "Liste des produits suivis pour le stock boutique."
                      : "Sélectionnez un onglet pour afficher les éléments suivis."}
                  </p>
                </div>
              </div>
            </div>

            {!isBoutique && (
              <div
                className={`flex flex-wrap gap-2 rounded-2xl border ${
                  isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                } p-1`}
              >
                {[
                  { id: "products", label: "Produits", count: trackedProductsCount },
                  { id: "extras", label: "Extras", count: trackedExtrasCount },
                  { id: "recipes", label: "Recettes", count: trackedRecipesCount },
                  { id: "simulator", label: "Simulateur de recettes", count: undefined },
                ].map((tab) => {
                  const isActiveTab = catalogTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        setCatalogTab(tab.id);
                        if (tab.id === "recipes" && !recipesState.loaded && !recipesState.loading) {
                          fetchRecipesList();
                        }
                        if (tab.id === "simulator") {
                          if (!recipesState.loaded && !recipesState.loading) {
                            fetchRecipesList();
                          }
                          if (!ingredientsState.loaded && !ingredientsState.loading) {
                            fetchStockItems("ingredient");
                          }
                          if (!consumablesState.loaded && !consumablesState.loading) {
                            fetchStockItems("consumable");
                          }
                        }
                      }}
                      className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                        isActiveTab
                          ? "bg-amber-400 text-slate-900 shadow-lg"
                          : isDarkMode
                            ? "text-white/70"
                            : "text-slate-700"
                      }`}
                    >
                      <span>{tab.label}</span>
                      {tab.count !== undefined && tab.count !== null && (
                        <span
                          className={`text-xs font-semibold ${
                            isActiveTab
                              ? "text-slate-900/70"
                              : isDarkMode
                                ? "text-white/60"
                                : "text-slate-500"
                          }`}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

              {catalogTab === "products" ? (
                trackedProductsCount > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {trackedCatalogItems.map((item) => {
                      const isVariation = item.type === "variation";
                      const title = isVariation ? item.baseName : item.name;
                      const subtitle = isVariation
                        ? item.variantLabel || item.name
                        : item.category || "Sans catégorie";
                      const hasRecipe = Boolean(item.hasRecipe);
                      // Pour les produits sans recette (vendus directement : "resold"/"consumable"),
                      // le backend ne calcule jamais saleCapacity/recipeUnitCost (ces champs sont
                      // dérivés des recettes). On replie alors sur stockQuantity/stockValue, déjà
                      // fournis sur l'item, pour ne pas afficher un "—" trompeur.
                      const effectiveSaleCapacity =
                        item.saleCapacity !== null && item.saleCapacity !== undefined
                          ? item.saleCapacity
                          : item.stockQuantity !== null && item.stockQuantity !== undefined
                            ? item.stockQuantity
                            : null;
                      const effectiveValue =
                        item.recipeUnitCost !== null && item.recipeUnitCost !== undefined
                          ? item.recipeUnitCost
                          : item.stockValue !== null && item.stockValue !== undefined
                            ? item.stockValue
                            : null;
                      const saleCapacityValue =
                        effectiveSaleCapacity !== null
                          ? decimalFormatter.format(effectiveSaleCapacity)
                          : "—";
                      const recipeCostDisplay =
                        effectiveValue !== null
                          ? preciseCurrencyFormatter.format(effectiveValue)
                          : "—";
                      return (
                        <div
                          key={item.id}
                          className={`rounded-2xl border p-4 ${
                            isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                                {item.category || "Sans catégorie"}
                              </p>
                              <h4 className="text-lg font-semibold">{title}</h4>
                              {isVariation && (
                                <p className={`text-xs ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
                                  {subtitle}
                                </p>
                              )}
                              {!isVariation && (
                                <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                                  {item.destination === "bar"
                                    ? "Bar"
                                    : item.destination === "cuisine"
                                      ? "Cuisine"
                                      : item.destination === "none" || !item.destination
                                        ? "Aucune destination"
                                        : item.destination_name || item.destination} · Produit simple
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                  isVariation
                                    ? "bg-indigo-400/20 text-indigo-200"
                                    : "bg-emerald-400/20 text-emerald-200"
                                }`}
                              >
                                {isVariation ? "Déclinaison" : "Produit"}
                              </span>
                              {isBoutique && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openProductSupplierModal({
                                      productId: item.productId || item.id,
                                      name: item.baseName || item.name,
                                    })
                                  }
                                  className={`rounded-full p-2 transition ${
                                    isDarkMode
                                      ? "border border-white/10 text-white/80 hover:border-amber-400/60"
                                      : "border border-slate-200 text-slate-600 hover:border-amber-500/60"
                                  }`}
                                  title="Lier à des fournisseurs"
                                >
                                  <Link2 size={16} />
                                </button>
                              )}
                              {!isBoutique &&
                                item.inventoryKind !== "resold" &&
                                item.inventoryKind !== "consumable" && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openRecipeModal({ ...item, productId: item.productId || item.id }, "product")
                                  }
                                  className={
                                    hasRecipe
                                      ? "rounded-full p-2 transition bg-gradient-to-r from-amber-300 to-orange-400 text-slate-900 shadow-lg"
                                      : `rounded-full p-2 transition ${
                                          isDarkMode
                                            ? "border border-white/10 text-white/80 hover:border-amber-400/60"
                                            : "border border-slate-200 text-slate-600 hover:border-amber-500/60"
                                        }`
                                  }
                                  title={hasRecipe ? "Modifier la recette" : "Créer une recette"}
                                >
                                  <ChefHat size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                            <div>
                              <p className={`${isDarkMode ? "text-white/60" : "text-slate-500"} text-xs`}>
                                Capacité de vente
                              </p>
                              <p className="font-semibold">{saleCapacityValue}</p>
                            </div>
                            <div>
                              <p className={`${isDarkMode ? "text-white/60" : "text-slate-500"} text-xs`}>Valeur</p>
                              <p className="font-semibold">{recipeCostDisplay}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Aucun produit suivi pour le moment.
                  </p>
                )
              ) : catalogTab === "extras" ? (
                trackedExtrasCount > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {trackedExtras.map((extra) => {
                      const hasRecipe = Boolean(extra.hasRecipe);
                      const recipeButtonClasses = hasRecipe
                        ? "rounded-full p-2 transition bg-gradient-to-r from-amber-300 to-orange-400 text-slate-900 shadow-lg"
                        : `rounded-full p-2 transition ${
                            isDarkMode
                              ? "border border-white/10 text-white/80 hover:border-amber-400/60"
                              : "border border-slate-200 text-slate-600 hover:border-amber-500/60"
                          }`;
                      return (
                        <div
                          key={extra.id}
                          className={`rounded-2xl border p-4 ${
                            isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                                {extra.category || "Extras"}
                              </p>
                              <h4 className="text-lg font-semibold">{extra.name}</h4>
                              {extra.description && (
                                <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                                  {extra.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                  extra.isActive
                                    ? "bg-emerald-400/20 text-emerald-200"
                                    : "bg-rose-500/20 text-rose-200"
                                }`}
                              >
                                {extra.isActive ? "Actif" : "Inactif"}
                              </span>
                              {extra.inventoryKind !== "resold" &&
                              extra.inventoryKind !== "consumable" && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openRecipeModal({ ...extra, extraId: extra.extraId || extra.id }, "extra")
                                  }
                                  className={recipeButtonClasses}
                                  title={hasRecipe ? "Modifier la recette" : "Créer une recette"}
                                >
                                  <ChefHat size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 text-sm flex items-center justify-between">
                            <span className={isDarkMode ? "text-white/60" : "text-slate-500"}>Prix de référence</span>
                            <span className="font-semibold">
                              {currencyFormatter.format(extra.basePrice || 0)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Vous n'avez aucun extra tracké.
                  </p>
                )
              ) : catalogTab === "simulator" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    className={`rounded-2xl border p-5 space-y-3 ${
                      isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                          Simulation rapide
                        </p>
                        <h4 className="text-lg font-semibold">Simuler avec une recette existante</h4>
                        <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                          Choisissez une recette et testez la production immédiate.
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center justify-center rounded-2xl p-3 ${
                          isDarkMode ? "bg-white/5 text-white" : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        <ChefHat className="w-5 h-5" />
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!recipesState.loaded && !recipesState.loading) {
                          fetchRecipesList();
                        }
                        const firstRecipe = (recipesState.items || [])[0];
                        if (!firstRecipe) {
                          showToast("info", "Aucune recette disponible pour la simulation.");
                          return;
                        }
                        openSimulatorWithRecipe(firstRecipe);
                      }}
                      className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 shadow"
                    >
                      <ChefHat className="w-4 h-4" />
                      Simuler une recette
                    </button>
                  </div>
                  <div
                    className={`rounded-2xl border p-5 space-y-3 ${
                      isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                          Scénario libre
                        </p>
                        <h4 className="text-lg font-semibold">Simuler avec une nouvelle recette</h4>
                        <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                          Composez une recette ad hoc sans l'enregistrer.
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center justify-center rounded-2xl p-3 ${
                          isDarkMode ? "bg-white/5 text-white" : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        <Plus className="w-5 h-5" />
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!ingredientsState.loaded && !ingredientsState.loading) {
                          fetchStockItems("ingredient");
                        }
                        if (!consumablesState.loaded && !consumablesState.loading) {
                          fetchStockItems("consumable");
                        }
                        openSimulatorNew();
                      }}
                      className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 shadow"
                    >
                      <Activity className="w-4 h-4" />
                      Lancer une simulation
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      {recipesState.error && (
                        <span className="text-rose-400">{recipesState.error}</span>
                      )}
                      {recipesState.loading && <span className={isDarkMode ? "text-white/60" : "text-slate-500"}>Chargement des recettes…</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={fetchRecipesList}
                        disabled={recipesState.loading}
                        className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                          isDarkMode ? "bg-white/5 text-white" : "bg-slate-900 text-white"
                        } disabled:opacity-60`}
                      >
                        <RefreshCw className={`w-4 h-4 ${recipesState.loading ? "animate-spin" : ""}`} />
                        Rafraîchir
                      </button>
                    </div>
                  </div>
                  {recipesState.loading && !recipesState.loaded ? (
                    <div className="py-10 text-center text-sm text-slate-500">Chargement des recettes…</div>
                  ) : trackedRecipesCount > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {trackedRecipes.map((recipe) => {
                        const produced = recipe.produced_quantity || 0;
                        const costUnit = recipe.cost_per_serving !== null && recipe.cost_per_serving !== undefined
                          ? preciseCurrencyFormatter.format(recipe.cost_per_serving)
                          : "—";
                        const stockConso = recipe.stock_conso !== null && recipe.stock_conso !== undefined
                          ? decimalFormatter.format(recipe.stock_conso)
                          : "—";
                        const disableActions = deletingRecipeId === recipe.id;
                        const ingredientCount = recipe.ingredient_count || 0;
                        const consumableCount = recipe.consumable_count || 0;
                        return (
                          <div
                            key={recipe.id}
                            className={`rounded-2xl border p-4 ${
                              isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                            } space-y-3`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Recette</p>
                                <h4 className="text-lg font-semibold">{recipe.name}</h4>
                                <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                                  Produit associé : {recipe.product_name || "—"}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    openRecipeModal(
                                      { id: recipe.product_id, productId: recipe.product_id, name: recipe.product_name },
                                      "product",
                                      { readOnly: true },
                                    )
                                  }
                                  disabled={disableActions}
                                  className={`rounded-full p-2 ${
                                    isDarkMode ? "border border-white/10" : "border border-slate-200"
                                  } hover:border-amber-400/60 disabled:opacity-50`}
                                  title="Voir la recette"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    openRecipeModal(
                                      { id: recipe.product_id, productId: recipe.product_id, name: recipe.product_name, hasRecipe: true },
                                      "product",
                                    )
                                  }
                                  disabled={disableActions}
                                  className={`rounded-full p-2 ${
                                    isDarkMode ? "border border-white/10" : "border border-slate-200"
                                  } hover:border-amber-400/60 disabled:opacity-50`}
                                  title="Modifier la recette"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRecipeCardDelete(recipe)}
                                  disabled={disableActions}
                                  className="rounded-full p-2 border border-white/10 text-rose-300 hover:border-rose-400/70 disabled:opacity-50"
                                  title="Supprimer la recette"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className={isDarkMode ? "text-white/60 text-xs" : "text-slate-500 text-xs"}>Coût unitaire</p>
                                <p className="font-semibold">{costUnit}</p>
                              </div>
                              <div>
                                <p className={isDarkMode ? "text-white/60 text-xs" : "text-slate-500 text-xs"}>Quantité produite</p>
                                <p className="font-semibold">{decimalFormatter.format(produced)}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-xs">
                              <div className={`rounded-xl px-3 py-2 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
                                <p className={isDarkMode ? "text-white/50" : "text-slate-500"}>Ingrédients</p>
                                <p className="font-semibold">{ingredientCount}</p>
                              </div>
                              <div className={`rounded-xl px-3 py-2 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
                                <p className={isDarkMode ? "text-white/50" : "text-slate-500"}>Consommables</p>
                                <p className="font-semibold">{consumableCount}</p>
                              </div>
                              <div className={`rounded-xl px-3 py-2 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
                                <p className={isDarkMode ? "text-white/50" : "text-slate-500"}>Stock Conso</p>
                                <p className="font-semibold">{stockConso}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                      Aucune recette enregistrée pour l'instant.
                    </p>
                  )}
                </div>
              )}
          </section>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                Module Inventaire
              </p>
              <h2 className="text-xl font-semibold">Produits & Inventaire</h2>
              <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                Structure des fonctionnalités Waise. Cliquez à venir.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleInventoryCards.map((card) => {
              const interactiveSections = {
                products: "catalog",
                resold: "resold",
                ingredients: "ingredients",
                consumables: "consumables",
                overview: "overview",
                reconciliation: "reconciliation",
                losses: "losses",
              };
              const targetSection = interactiveSections[card.id];
              const isInteractive = Boolean(targetSection);
              const badgeLabel = isInteractive ? "Disponible" : "Bientôt disponible";
              const handleNavigation = () => {
                if (!isInteractive) return;
                if (targetSection === "catalog") {
                  setInventorySection("catalog");
                  setCatalogTab("products");
                } else {
                  setInventorySection(targetSection);
                }
              };
              return (
                <div
                  key={card.id}
                  role={isInteractive ? "button" : undefined}
                  tabIndex={isInteractive ? 0 : -1}
                  onClick={handleNavigation}
                  onKeyDown={(event) => {
                    if (!isInteractive) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleNavigation();
                    }
                  }}
                  className={`rounded-2xl border p-5 transition ${
                    isDarkMode
                      ? "border-white/10 bg-white/5 hover:border-white/20"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  } ${isInteractive ? "cursor-pointer" : "cursor-not-allowed opacity-80"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                        {card.helper}
                      </p>
                      <h3 className="text-lg font-semibold">{card.title}</h3>
                      <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                        {card.caption}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center justify-center rounded-2xl p-3 ${
                        isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      <card.icon className="w-5 h-5" />
                    </span>
                  </div>
                  <div
                    className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                      isInteractive
                        ? isDarkMode
                          ? "bg-emerald-500/10 text-emerald-200"
                          : "bg-emerald-50 text-emerald-700"
                        : isDarkMode
                          ? "bg-white/5 text-white/70"
                          : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {badgeLabel}
                    {!isInteractive && <AlertTriangle className="w-4 h-4" />}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    );
  };

  const renderWarehouses = () => (
    <div className="space-y-6">
      <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Réseau de stockage</p>
            <h2 className="text-lg font-semibold">Entrepôts & zones contrôlées</h2>
          </div>
          <div className="flex items-center gap-3">
            {warehousesState.error && (
              <span className="text-sm text-rose-400">{warehousesState.error}</span>
            )}
            <button
              type="button"
              onClick={() => openWarehouseModal()}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow`}
            >
              <Plus className="w-4 h-4" />
              Nouvel entrepôt
            </button>
            <button
              type="button"
              onClick={fetchWarehouses}
              disabled={warehousesState.loading}
              className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                isDarkMode ? "bg-white/5 text-white" : "bg-slate-900 text-white"
              } disabled:opacity-60`}
            >
              <RefreshCw className={`w-4 h-4 ${warehousesState.loading ? "animate-spin" : ""}`} />
              Mettre à jour
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <TabStatCard
            icon={Warehouse}
            label="Entrepôts actifs"
            value={warehouseStats.total}
            helper="Zones suivies"
            isDarkMode={isDarkMode}
          />
          <TabStatCard
            icon={ThermometerSun}
            label="Espaces froids"
            value={warehouseStats.cold}
            helper="Chambres froides & congélateurs"
            isDarkMode={isDarkMode}
          />
          <TabStatCard
            icon={Scale}
            label="Capacité volume"
            value={`${numberFormatter.format(Math.round(warehouseStats.volume))} m³`}
            helper="Volume théorique"
            isDarkMode={isDarkMode}
          />
          <TabStatCard
            icon={Activity}
            label="Capacité poids"
            value={`${numberFormatter.format(Math.round(warehouseStats.weight))} kg`}
            helper="Charge admissible"
            isDarkMode={isDarkMode}
          />
        </div>

        {warehousesState.loading && !warehousesState.data.length ? (
          <div className="py-10 text-center text-sm text-slate-500">
            Récupération des entrepôts…
          </div>
        ) : warehousesState.data.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {warehousesState.data.map((warehouse) => (
              <WarehouseCard
                key={warehouse.id}
                warehouse={warehouse}
                isDarkMode={isDarkMode}
                onEdit={() => openWarehouseModal(warehouse)}
                onDelete={() => handleDeleteWarehouse(warehouse)}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Aucun entrepôt actif configuré pour le moment.</p>
        )}
      </section>
    </div>
  );

  const renderSuppliers = () => (
    <div className="space-y-6">
      <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Partenaires</p>
            <h2 className="text-lg font-semibold">Fournisseurs & filières</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {suppliersState.error && (
              <span className="text-sm text-rose-400">{suppliersState.error}</span>
            )}
            <button
              type="button"
              onClick={() => openSupplierModal()}
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow"
            >
              <Plus className="w-4 h-4" />
              Nouveau fournisseur
            </button>
            <button
              type="button"
              onClick={fetchSuppliers}
              disabled={suppliersState.loading}
              className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                isDarkMode ? "bg-white/5 text-white" : "bg-slate-900 text-white"
              } disabled:opacity-60`}
            >
              <RefreshCw className={`w-4 h-4 ${suppliersState.loading ? "animate-spin" : ""}`} />
              Mettre à jour
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TabStatCard
            icon={Users}
            label="Fournisseurs actifs"
            value={supplierStats.total}
            helper="Référencés"
            isDarkMode={isDarkMode}
          />
          <TabStatCard
            icon={Truck}
            label="Délai moyen"
            value={`${supplierStats.avgPayment || 0} j`}
            helper="Conditions de paiement"
            isDarkMode={isDarkMode}
          />
          <TabStatCard
            icon={Star}
            label="Satisfaction moyenne"
            value={
              supplierStats.avgRating
                ? `${supplierStats.avgRating.toFixed(1)} / 5`
                : "—"
            }
            helper={`${supplierStats.cities} villes desservies`}
            isDarkMode={isDarkMode}
          />
        </div>

        {suppliersState.loading && !suppliersState.data.length ? (
          <div className="py-10 text-center text-sm text-slate-500">
            Chargement des fournisseurs…
          </div>
        ) : suppliersState.data.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {suppliersState.data.map((supplier) => (
              <SupplierCard
                key={supplier.id}
                supplier={supplier}
                isDarkMode={isDarkMode}
                onEdit={() => openSupplierModal(supplier)}
                onToggle={() => handleToggleSupplier(supplier)}
                onDelete={() => handleDeleteSupplier(supplier)}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            Aucun fournisseur n'a encore été enregistré dans Waise.
          </p>
        )}
      </section>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
        {settingsState.loading && !settingsState.data ? (
          <div className="py-10 text-center text-sm text-slate-500">
            Synchronisation des paramètres…
          </div>
        ) : settingsSection === "alerts" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSettingsSection(null)}
                className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                  isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Surveillance du stock</p>
                <h3 className="text-lg font-semibold">Configuration des alertes</h3>
                <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                  Activez/désactivez les alertes et ajustez les seuils globaux.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[
                {
                  id: "lowStock",
                  title: "Seuil d'alerte atteint",
                  description: "\"Votre stock de X est en dessous du seuil minimum.\" (individuel ou global)",
                  enabled: alertSettings.enableLowStock,
                  onToggle: async (state) => {
                    const nextState = { ...alertSettings, enableLowStock: state };
                    setAlertSettings(nextState);
                    const ok = await persistAlerts(nextState);
                    if (!ok) {
                      setAlertSettings(alertSettings);
                    }
                  },
                  extraAction: () => setShowMinThresholdModal(true),
                },
                {
                  id: "saleCapacity",
                  title: "Capacité de vente atteinte",
                  description: "Alerte quand la capacité de vente atteint le seuil global.",
                  enabled: alertSettings.enableSaleCapacity,
                  onToggle: async (state) => {
                    const nextState = { ...alertSettings, enableSaleCapacity: state };
                    setAlertSettings(nextState);
                    const ok = await persistAlerts(nextState);
                    if (!ok) {
                      setAlertSettings(alertSettings);
                    }
                  },
                  extraAction: () => setShowSaleCapacityModal(true),
                },
                {
                  id: "nearZero",
                  title: "Capacité proche de zéro",
                  description: "\"Votre produit X ne peut plus être produit qu’une seule fois.\"",
                  enabled: alertSettings.enableNearZeroCapacity,
                  onToggle: async (state) => {
                    const nextState = { ...alertSettings, enableNearZeroCapacity: state };
                    setAlertSettings(nextState);
                    const ok = await persistAlerts(nextState);
                    if (!ok) {
                      setAlertSettings(alertSettings);
                    }
                  },
                },
                {
                  id: "recipeZero",
                  title: "Recette inutilisable",
                  description: "\"La recette X ne peut plus être produite.\" (Stock Conso = 0)",
                  enabled: alertSettings.enableRecipeUnusable,
                  onToggle: async (state) => {
                    const nextState = { ...alertSettings, enableRecipeUnusable: state };
                    setAlertSettings(nextState);
                    const ok = await persistAlerts(nextState);
                    if (!ok) {
                      setAlertSettings(alertSettings);
                    }
                  },
                },
                {
                  id: "negativeMargin",
                  title: "Perte éventuelle (marge négative)",
                  description: "Alerte si le prix de vente ≤ coût unitaire de production.",
                  enabled: alertSettings.enableNegativeMargin,
                  onToggle: async (state) => {
                    const nextState = { ...alertSettings, enableNegativeMargin: state };
                    setAlertSettings(nextState);
                    const ok = await persistAlerts(nextState);
                    if (!ok) {
                      setAlertSettings(alertSettings);
                    }
                  },
                },
              ].map((card) => {
                const isOn = card.enabled;
                return (
                  <div
                    key={card.id}
                    className={`rounded-2xl border ${
                      isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                    } p-4 space-y-3 shadow-sm`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Alerte</p>
                        <h4 className="text-base font-semibold">{card.title}</h4>
                        <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                          {card.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {card.extraAction && (
                          <button
                            type="button"
                            onClick={card.extraAction}
                            className={`rounded-full p-2 ${
                              isDarkMode ? "border border-white/10 hover:border-amber-400/60" : "border border-slate-200 hover:border-amber-400/60"
                            }`}
                            title="Définir le seuil"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => card.onToggle(!isOn)}
                          className={`rounded-full border p-2 transition ${
                            isOn
                              ? "border-emerald-400/60 bg-emerald-500/10 text-emerald-300"
                              : isDarkMode
                                ? "border-white/10 text-white/70 hover:border-amber-400/60"
                                : "border-slate-200 text-slate-600 hover:border-amber-400/60"
                          }`}
                          title={isOn ? "Désactiver" : "Activer"}
                        >
                          <Power size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div
                className={`rounded-2xl border ${
                  isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                } p-4 space-y-3 shadow-sm`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Alerte</p>
                    <h4 className="text-base font-semibold">Produits dormants</h4>
                    <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                      Produit actif sans vente depuis un nombre de jours configurable.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDormantDaysModal(true)}
                    className={`rounded-full p-2 ${
                      isDarkMode
                        ? "border border-white/10 hover:border-amber-400/60"
                        : "border border-slate-200 hover:border-amber-400/60"
                    }`}
                    title="Définir le seuil"
                  >
                    <Edit size={16} />
                  </button>
                </div>
                <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                  Seuil actuel: {numberFormatter.format(alertSettings.dormantProductDays || 10)} jours
                </p>
              </div>
            </div>

          </div>
        ) : settingsSection === "overview" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSettingsSection(null)}
                className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${
                  isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800"
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Paramètres généraux</p>
                <h3 className="text-lg font-semibold">Vue d'ensemble</h3>
                <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                  Choisissez la période par défaut appliquée aux blocs d'activité.
                </p>
              </div>
            </div>

            <div
              className={`rounded-2xl border ${
                isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
              } p-5 space-y-4`}
            >
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-1">
                  Période par défaut
                </p>
                <h4 className="text-base font-semibold">Filtres de la vue d'ensemble</h4>
                <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                  Cette valeur initialise les filtres Revendus et Cuisine.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={overviewDefaultPeriodInput}
                  onChange={(e) =>
                    setOverviewDefaultPeriodInput(normalizeOverviewPeriod(e.target.value))
                  }
                  className={`rounded-2xl border px-4 py-2 text-sm ${
                    isDarkMode
                      ? "bg-white/5 border-white/10 text-white"
                      : "bg-white border-slate-200 text-slate-700"
                  }`}
                >
                  {OVERVIEW_PERIOD_OPTIONS.filter((option) => option.id !== "custom").map(
                    (option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ),
                  )}
                </select>
                <button
                  type="button"
                  onClick={() => persistOverviewDefaults(overviewDefaultPeriodInput)}
                  disabled={isSavingSettings || overviewDefaultPeriodInput === overviewDefaultPeriod}
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setSettingsSection("alerts")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSettingsSection("alerts");
                }
              }}
              className={`rounded-2xl border p-5 transition ${
                isDarkMode ? "border-white/10 bg-white/5 hover:border-white/20" : "border-slate-200 bg-white hover:border-slate-300"
              } cursor-pointer shadow-sm`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Paramètres</p>
                  <h3 className="text-lg font-semibold">Surveillance du stock</h3>
                  <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Gérer les alertes globales de Waise.
                  </p>
                </div>
                <span className={`inline-flex items-center justify-center rounded-2xl p-3 ${
                  isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-700"
                }`}>
                  <AlertTriangle className="w-5 h-5" />
                </span>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-emerald-500/10 text-emerald-200">
                Configurable
              </div>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setSettingsSection("overview")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSettingsSection("overview");
                }
              }}
              className={`rounded-2xl border p-5 transition ${
                isDarkMode
                  ? "border-white/10 bg-white/5 hover:border-white/20"
                  : "border-slate-200 bg-white hover:border-slate-300"
              } cursor-pointer shadow-sm`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Paramètres</p>
                  <h3 className="text-lg font-semibold">Vue d'ensemble</h3>
                  <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Définir la période par défaut des blocs d'activité.
                  </p>
                </div>
                <span className={`inline-flex items-center justify-center rounded-2xl p-3 ${
                  isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-700"
                }`}>
                  <ClipboardList className="w-5 h-5" />
                </span>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-emerald-500/10 text-emerald-200">
                Configurable
              </div>
            </div>
          </div>
        )}
      </section>

      {showMinThresholdModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className={`w-full max-w-md rounded-3xl border ${isDarkMode ? "bg-[#0f172a] border-white/10 text-white" : "bg-white border-slate-200"}`}>
            <div className={`rounded-t-3xl px-6 py-4 ${isDarkMode ? "bg-gradient-to-r from-amber-500/10 to-orange-500/10" : "bg-amber-50"}`}>
              <h3 className="text-lg font-semibold">Seuil minimum global</h3>
              <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>Appliqué quand aucun seuil individuel n'est défini.</p>
            </div>
            <div className="p-6 space-y-4">
              <label className="text-sm font-semibold block">Valeur (unités)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={minThresholdInput}
              onChange={(e) => setMinThresholdInput(Number(e.target.value))}
              className={`w-full rounded-2xl px-4 py-2 text-sm border ${isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
            />
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowMinThresholdModal(false)}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${isDarkMode ? "border border-white/10 text-white/80" : "border border-slate-200 text-slate-700"}`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={async () => {
                  const nextState = { ...alertSettings, defaultMinThreshold: minThresholdInput };
                  setAlertSettings(nextState);
                  setShowMinThresholdModal(false);
                  const ok = await persistAlerts(nextState);
                  if (!ok) {
                    setAlertSettings(alertSettings);
                  }
                }}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900"
              >
                Définir
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {showSaleCapacityModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className={`w-full max-w-md rounded-3xl border ${isDarkMode ? "bg-[#0f172a] border-white/10 text-white" : "bg-white border-slate-200"}`}>
            <div className={`rounded-t-3xl px-6 py-4 ${isDarkMode ? "bg-gradient-to-r from-amber-500/10 to-orange-500/10" : "bg-amber-50"}`}>
              <h3 className="text-lg font-semibold">Seuil global de capacité de vente</h3>
              <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>Alerte quand un produit atteint cette capacité.</p>
            </div>
            <div className="p-6 space-y-4">
              <label className="text-sm font-semibold block">Valeur (unités)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={saleCapacityInput}
              onChange={(e) => setSaleCapacityInput(Number(e.target.value))}
              className={`w-full rounded-2xl px-4 py-2 text-sm border ${isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
            />
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowSaleCapacityModal(false)}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${isDarkMode ? "border border-white/10 text-white/80" : "border border-slate-200 text-slate-700"}`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={async () => {
                  const nextState = { ...alertSettings, saleCapacityThreshold: saleCapacityInput };
                  setAlertSettings(nextState);
                  setShowSaleCapacityModal(false);
                  const ok = await persistAlerts(nextState);
                  if (!ok) {
                    setAlertSettings(alertSettings);
                  }
                }}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900"
              >
                Définir
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {showDormantDaysModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            className={`w-full max-w-md rounded-3xl border ${
              isDarkMode
                ? "bg-[#0f172a] border-white/10 text-white"
                : "bg-white border-slate-200"
            }`}
          >
            <div
              className={`rounded-t-3xl px-6 py-4 ${
                isDarkMode
                  ? "bg-gradient-to-r from-amber-500/10 to-orange-500/10"
                  : "bg-amber-50"
              }`}
            >
              <h3 className="text-lg font-semibold">Seuil produits dormants</h3>
              <p
                className={`text-xs ${
                  isDarkMode ? "text-white/60" : "text-slate-500"
                }`}
              >
                Nombre de jours sans vente avant classement dormant.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <label className="text-sm font-semibold block">Valeur (jours)</label>
              <input
                type="number"
                min="1"
                step="1"
                value={dormantDaysInput}
                onChange={(e) =>
                  setDormantDaysInput(
                    Math.max(1, Number(e.target.value) || 1),
                  )
                }
                className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                  isDarkMode
                    ? "bg-white/5 border-white/10 text-white"
                    : "bg-white border-slate-200"
                }`}
              />
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDormantDaysModal(false)}
                  className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                    isDarkMode
                      ? "border border-white/10 text-white/80"
                      : "border border-slate-200 text-slate-700"
                  }`}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const nextState = {
                      ...alertSettings,
                      dormantProductDays: Math.max(
                        1,
                        Number(dormantDaysInput) || 10,
                      ),
                    };
                    setAlertSettings(nextState);
                    setShowDormantDaysModal(false);
                    const ok = await persistAlerts(nextState);
                    if (!ok) {
                      setAlertSettings(alertSettings);
                    }
                  }}
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900"
                >
                  Définir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStockItemModal = () => {
    const fieldBase =
      "w-full rounded-2xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300/40";
    const tone = isDarkMode
      ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
      : "bg-white border border-slate-200 text-slate-900 placeholder-slate-400";
    const inputClasses = `${fieldBase} ${tone}`;
    const textareaClasses = `${inputClasses} resize-none`;
    const isIngredientModal = stockModalType === "ingredient";

    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div
          className={`w-full max-w-2xl rounded-3xl border ${
            isDarkMode ? "bg-[#0f172a] border-white/10 text-white" : "bg-white border-slate-200"
          }`}
        >
          <div
            className={`rounded-t-3xl px-6 py-4 flex items-center justify-between ${
              isDarkMode ? "bg-gradient-to-r from-amber-500/10 to-orange-500/10" : "bg-amber-50"
            }`}
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                {editingStockItem ? "Modifier" : "Ajouter"} {isIngredientModal ? "un ingrédient" : "un consommable"}
              </p>
              <h3 className="text-xl font-semibold mt-1">
                {stockItemForm.name.trim() || (isIngredientModal ? "Nouvel ingrédient" : "Nouveau consommable")}
              </h3>
            </div>
            <button
              type="button"
              onClick={closeStockItemModal}
              className={`rounded-full p-2 ${
                isDarkMode ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"
              }`}
            >
              <X size={16} />
            </button>
          </div>
          <form onSubmit={handleStockItemSubmit} className="p-6 space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold block mb-1">Nom *</label>
                <input
                  type="text"
                  value={stockItemForm.name}
                  onChange={(e) => handleStockItemInputChange("name", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">SKU interne</label>
                <input
                  type="text"
                  value={stockItemForm.sku}
                  onChange={(e) => handleStockItemInputChange("sku", e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold block mb-1">Catégorie</label>
                <select
                  value={
                    stockItemForm.category_mode === "custom"
                      ? "__new_category__"
                      : stockItemForm.category || ""
                  }
                  onChange={(e) => handleCategorySelection(e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {stockCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                  {stockItemForm.category &&
                    stockItemForm.category_mode === "existing" &&
                    !stockCategories.includes(stockItemForm.category) && (
                      <option value={stockItemForm.category}>{stockItemForm.category}</option>
                    )}
                  <option value="__new_category__">+ Nouvelle catégorie…</option>
                </select>
                {stockItemForm.category_mode === "custom" && (
                  <input
                    type="text"
                    value={stockItemForm.custom_category}
                    onChange={(e) => handleStockItemInputChange("custom_category", e.target.value)}
                    className={`${inputClasses} mt-2`}
                    placeholder={isIngredientModal ? "Ex: Légumes racines" : "Ex: Emballages recyclables"}
                  />
                )}
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Unité d'achat *</label>
                <select
                  value={stockItemForm.unit_id}
                  onChange={(e) => handleStockItemInputChange("unit_id", e.target.value)}
                  className={inputClasses}
                  disabled={unitsState.loading && !unitsState.data.length}
                >
                  <option value="">Sélectionnez une unité</option>
                  {unitsState.data.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name} ({unit.symbol})
                    </option>
                  ))}
                </select>
                {unitsState.loading && (
                  <p className="text-xs text-slate-500 mt-1">Chargement des unités…</p>
                )}
                {unitsState.error && (
                  <p className="text-xs text-rose-400 mt-1">{unitsState.error}</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-semibold">Unité de stockage principale</label>
                  <span
                    className={`text-[10px] uppercase tracking-[0.2em] ${
                      isDarkMode ? "text-white/50" : "text-slate-400"
                    }`}
                  >
                    Stock réel
                  </span>
                </div>
                <select
                  value={stockItemForm.storage_unit_id}
                  onChange={(e) => handleStockItemInputChange("storage_unit_id", e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Identique à l'unité d'achat</option>
                  {unitsState.data.map((unit) => (
                    <option key={`storage-${unit.id}`} value={unit.id}>
                      {unit.name} ({unit.symbol})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-semibold">Unité de consommation</label>
                  <span
                    className={`text-[10px] uppercase tracking-[0.2em] ${
                      isDarkMode ? "text-white/50" : "text-slate-400"
                    }`}
                  >
                    Recettes
                  </span>
                </div>
                <select
                  value={stockItemForm.consumption_unit_id}
                  onChange={(e) => handleStockItemInputChange("consumption_unit_id", e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Identique à l'unité d'achat</option>
                  {unitsState.data.map((unit) => (
                    <option key={`consumption-${unit.id}`} value={unit.id}>
                      {unit.name} ({unit.symbol})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <label className="text-sm font-semibold block mb-1">Fournisseurs</label>
                  <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Reliez cet article aux fournisseurs concernés.
                  </p>
                </div>
                <label className={`inline-flex items-center gap-2 text-xs font-semibold ${isDarkMode ? "text-white/70" : "text-slate-600"}`}>
                  <input
                    type="checkbox"
                    checked={stockItemForm.has_multiple_suppliers}
                    onChange={(e) => handleSupplierModeToggle(e.target.checked)}
                    className="h-4 w-4 rounded border-white/20 text-amber-400 focus:ring-amber-400/40"
                  />
                  Plusieurs fournisseurs
                </label>
              </div>
              {stockItemForm.has_multiple_suppliers ? (
                <div className="flex flex-wrap gap-2">
                  {(suppliersState.data || []).length === 0 ? (
                    <span className={`text-sm ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                      Aucun fournisseur enregistré pour le moment.
                    </span>
                  ) : (
                    suppliersState.data.map((supplier) => {
                      const isSelected = stockItemForm.supplier_ids.includes(String(supplier.id));
                      return (
                        <button
                          key={supplier.id}
                          type="button"
                          onClick={() => toggleLinkedSupplier(String(supplier.id))}
                          className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                            isSelected
                              ? isDarkMode
                                ? "bg-emerald-400/20 border-emerald-300 text-emerald-100"
                                : "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : isDarkMode
                                ? "border-white/10 text-white/70"
                                : "border-slate-200 text-slate-600"
                          }`}
                        >
                          {supplier.name}
                        </button>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  <label className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                    Fournisseur principal
                  </label>
                  {(suppliersState.data || []).length === 0 ? (
                    <span className={`text-sm ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                      Aucun fournisseur enregistré pour le moment.
                    </span>
                  ) : (
                    <select
                      value={stockItemForm.supplier_id}
                      onChange={(e) => handleStockItemInputChange("supplier_id", e.target.value)}
                      className={inputClasses}
                    >
                      <option value="">Sélectionnez un fournisseur</option>
                      {(suppliersState.data || []).map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1">
                Entrepôt de suivi <span className="text-rose-500">*</span>
              </label>
              {warehousesState.data?.length ? (
                <select
                  value={stockItemForm.warehouse_id}
                  onChange={(e) => handleStockItemInputChange("warehouse_id", e.target.value)}
                  className={inputClasses}
                  required
                >
                  <option value="">Sélectionnez un entrepôt</option>
                  {warehousesState.data.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className={`text-sm ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                  Aucun entrepôt n'est disponible pour le moment. Créez-en un dans Paramètres &gt; Entrepôts.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1">Description</label>
              <textarea
                rows={3}
                value={stockItemForm.description}
                onChange={(e) => handleStockItemInputChange("description", e.target.value)}
                className={textareaClasses}
                placeholder="Notes sur l'origine, la recette ou le conditionnement"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <label className="text-sm font-semibold block mb-1">Stock min.</label>
                <input
                  type="number"
                  min="0"
                  value={stockItemForm.minimum_stock}
                  onChange={(e) => handleStockItemInputChange("minimum_stock", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Seuil alerte</label>
                <input
                  type="number"
                  min="0"
                  value={stockItemForm.reorder_point}
                  onChange={(e) => handleStockItemInputChange("reorder_point", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Qté réappro.</label>
                <input
                  type="number"
                  min="0"
                  value={stockItemForm.reorder_quantity}
                  onChange={(e) => handleStockItemInputChange("reorder_quantity", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Coût unitaire</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={stockItemForm.cost_price}
                  onChange={(e) => handleStockItemInputChange("cost_price", e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className={`inline-flex items-center gap-2 text-sm font-semibold ${
                isDarkMode ? "text-white" : "text-slate-700"
              }`}>
                <input
                  type="checkbox"
                  checked={stockItemForm.is_perishable}
                  onChange={(e) => handleStockItemInputChange("is_perishable", e.target.checked)}
                  className="h-4 w-4 rounded border-white/20 text-amber-400 focus:ring-amber-400/40"
                />
                Produit périssable
              </label>
              {stockItemForm.is_perishable && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`text-xs font-semibold block mb-1 ${
                      isDarkMode ? "text-white/70" : "text-slate-600"
                    }`}>
                      Temps de péremption
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={stockItemForm.shelf_life_value}
                      onChange={(e) => handleStockItemInputChange("shelf_life_value", e.target.value)}
                      className={inputClasses}
                      placeholder="Ex: 10"
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-semibold block mb-1 ${
                      isDarkMode ? "text-white/70" : "text-slate-600"
                    }`}>
                      Unité
                    </label>
                    <select
                      value={stockItemForm.shelf_life_unit}
                      onChange={(e) => handleStockItemInputChange("shelf_life_unit", e.target.value)}
                      className={inputClasses}
                    >
                      <option value="days">Jours</option>
                      <option value="months">Mois</option>
                      <option value="years">Années</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={closeStockItemModal}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode
                    ? "border border-white/10 text-white/80"
                    : "border border-slate-200 text-slate-600"
                }`}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSavingStockItem}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              >
                {isSavingStockItem ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderWarehouseModal = () => {
    const modalClasses = isDarkMode
      ? "bg-[#070f1f] border-white/10 text-white"
      : "bg-white border-slate-200";
    const inputClasses = isDarkMode
      ? "w-full rounded-2xl px-4 py-2 text-sm bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
      : "w-full rounded-2xl px-4 py-2 text-sm bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-300/40";
    const isMainWarehouse =
      editingWarehouse?.is_main_pos_store === 1 ||
      isMainWarehouseName(editingWarehouse?.name) ||
      isMainWarehouseName(warehouseForm.name);
    const typeValue = isMainWarehouse
      ? "primary"
      : warehouseForm.type && warehouseForm.type !== "primary"
        ? warehouseForm.type
        : "secondary";
    const typeOptions = isMainWarehouse
      ? WAREHOUSE_TYPE_OPTIONS.filter((option) => option.value === "primary")
      : WAREHOUSE_TYPE_OPTIONS.filter((option) => option.value !== "primary");
    const locationValue = warehouseForm.location_mode === "custom" ? "__custom_location__" : warehouseForm.location || "";
    const associatedWarehouses = (warehousesState.data || []).filter(
      (w) => !editingWarehouse || w.id !== editingWarehouse.id,
    );

    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className={`w-full max-w-3xl rounded-3xl border ${modalClasses} shadow-2xl`}>
          <div
            className={`rounded-t-3xl px-6 py-4 flex items-center justify-between ${
              isDarkMode ? "bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-amber-500/10" : "bg-emerald-50"
            }`}
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                {editingWarehouse ? "Modifier un entrepôt" : "Nouvel entrepôt"}
              </p>
              <h3 className="text-xl font-semibold mt-1">
                {warehouseForm.name.trim() || "Entrepôt"}
              </h3>
            </div>
            <button
              type="button"
              onClick={closeWarehouseModal}
              className={`rounded-full p-2 ${
                isDarkMode ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"
              }`}
            >
              <X size={16} />
            </button>
          </div>
          <form onSubmit={handleWarehouseSubmit} className="p-6 space-y-5">
            <section className={`rounded-2xl border ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"} p-5 space-y-4`}>
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold">Infos générales</h4>
                <label className="flex items-center gap-2 text-xs font-semibold">
                  <input
                    type="checkbox"
                    checked={warehouseForm.is_active}
                    onChange={(e) => handleWarehouseInputChange("is_active", e.target.checked)}
                    className="h-4 w-4 rounded border-white/20 text-emerald-400 focus:ring-emerald-400/40"
                  />
                  Activer cet entrepôt
                </label>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold block mb-1">Nom *</label>
                  <input
                    type="text"
                    value={warehouseForm.name}
                    onChange={(e) => handleWarehouseInputChange("name", e.target.value)}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Code</label>
                  <input
                    type="text"
                    value={warehouseForm.code}
                    onChange={(e) => handleWarehouseInputChange("code", e.target.value)}
                    className={inputClasses}
                    placeholder="EX: ENT-PRINCIPAL"
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold block mb-1">Type d'entrepôt</label>
                  <select
                    value={typeValue}
                    onChange={(e) => handleWarehouseTypeChange(e.target.value)}
                    className={inputClasses}
                    disabled={isMainWarehouse}
                  >
                    {typeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {isMainWarehouse && (
                    <p className="mt-2 text-xs text-amber-200">
                      Entrepôt principal : le type est verrouillé.
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Emplacement</label>
                  <select
                    value={locationValue}
                    onChange={(e) => handleWarehouseLocationChange(e.target.value)}
                    className={inputClasses}
                  >
                    <option value="">Sélectionnez un emplacement</option>
                    {warehouseLocations.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                    <option value="__custom_location__">+ Nouvel emplacement</option>
                  </select>
                  {warehouseForm.location_mode === "custom" && (
                    <input
                      type="text"
                      value={warehouseForm.custom_location}
                      onChange={(e) => handleWarehouseInputChange("custom_location", e.target.value)}
                      className={`${inputClasses} mt-2`}
                      placeholder="Ex: Bâtiment B - Sous-sol"
                    />
                  )}
                </div>
              </div>
            </section>

            <section className={`rounded-2xl border ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"} p-5 space-y-4`}>
              <h4 className="text-lg font-semibold">Logistiques avancées</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold block mb-1">Température de stockage (°C)</label>
                  <input
                    type="number"
                    value={warehouseForm.temperature_target}
                    onChange={(e) => handleWarehouseInputChange("temperature_target", e.target.value)}
                    className={inputClasses}
                    placeholder="Ex: 4"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Capacité maximale (m³)</label>
                  <input
                    type="number"
                    value={warehouseForm.capacity_max}
                    onChange={(e) => handleWarehouseInputChange("capacity_max", e.target.value)}
                    className={inputClasses}
                    placeholder="Ex: 150"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Entrepôt associé</label>
                {associatedWarehouses.length ? (
                  <select
                    value={warehouseForm.parent_id}
                    onChange={(e) => handleWarehouseInputChange("parent_id", e.target.value)}
                    className={inputClasses}
                  >
                    <option value="">Aucun</option>
                    {associatedWarehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className={`text-sm ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                    Aucun autre entrepôt disponible.
                  </p>
                )}
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={closeWarehouseModal}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode
                    ? "border border-white/10 text-white/80"
                    : "border border-slate-200 text-slate-600"
                }`}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSavingWarehouse}
                className="rounded-2xl bg-gradient-to-r from-emerald-400 to-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              >
                {isSavingWarehouse ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderSupplierModal = () => {
    const fieldBase =
      "w-full rounded-2xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300/40";
    const tone = isDarkMode
      ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
      : "bg-white border border-slate-200 text-slate-900 placeholder-slate-400";
    const inputClasses = `${fieldBase} ${tone}`;
    const textareaClasses = `${inputClasses} resize-none`;
    const isCompany = supplierForm.entity_type === "company";
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div
          className={`w-full max-w-3xl rounded-3xl border ${
            isDarkMode ? "bg-[#0f172a] border-white/10 text-white" : "bg-white border-slate-200"
          }`}
        >
          <div
            className={`rounded-t-3xl px-6 py-4 flex items-center justify-between ${
              isDarkMode ? "bg-gradient-to-r from-emerald-500/10 to-cyan-500/10" : "bg-emerald-50"
            }`}
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-400">
                {editingSupplier ? "Modifier" : "Nouveau"} fournisseur
              </p>
              <h3 className="text-xl font-semibold mt-1">
                {isCompany
                  ? supplierForm.company_name || "Entreprise"
                  : `${supplierForm.first_name} ${supplierForm.last_name}`.trim() || "Fournisseur"}
              </h3>
            </div>
            <button
              type="button"
              onClick={closeSupplierModal}
              className={`rounded-full p-2 ${
                isDarkMode ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"
              }`}
            >
              <X size={16} />
            </button>
          </div>
          <form onSubmit={handleSupplierSubmit} className="p-6 space-y-5">
            <div className="flex gap-2">
              {[
                { id: "person", label: "Personne" },
                { id: "company", label: "Entreprise" },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSupplierInputChange("entity_type", option.id)}
                  className={`flex-1 rounded-2xl px-4 py-2 text-sm font-semibold ${
                    supplierForm.entity_type === option.id
                      ? "bg-emerald-400 text-slate-900 shadow"
                      : isDarkMode
                        ? "border border-white/10 text-white/70"
                        : "border border-slate-200 text-slate-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {supplierForm.entity_type === "person" ? (
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="text-sm font-semibold block mb-1">Civilité *</label>
                  <select
                    value={supplierForm.civility}
                    onChange={(e) => handleSupplierInputChange("civility", e.target.value)}
                    className={inputClasses}
                  >
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                    <option value="Mlle">Mlle</option>
                  </select>
                </div>
                <div className="md:col-span-1">
                  <label className="text-sm font-semibold block mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={supplierForm.first_name}
                    onChange={(e) => handleSupplierInputChange("first_name", e.target.value)}
                    className={inputClasses}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="text-sm font-semibold block mb-1">Nom *</label>
                  <input
                    type="text"
                    value={supplierForm.last_name}
                    onChange={(e) => handleSupplierInputChange("last_name", e.target.value)}
                    className={inputClasses}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold block mb-1">
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    value={supplierForm.company_name}
                    onChange={(e) => handleSupplierInputChange("company_name", e.target.value)}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold block mb-1">Responsable</label>
                  <input
                    type="text"
                    value={supplierForm.manager_name}
                    onChange={(e) => handleSupplierInputChange("manager_name", e.target.value)}
                    className={inputClasses}
                  />
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold block mb-1">Type</label>
                <select
                  value={supplierForm.supplier_category}
                  onChange={(e) => handleSupplierInputChange("supplier_category", e.target.value)}
                  className={inputClasses}
                >
                  {supplierTypeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Commune / Ville</label>
                <input
                  type="text"
                  value={supplierForm.city}
                  onChange={(e) => handleSupplierInputChange("city", e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold block mb-1">Adresse</label>
                <input
                  type="text"
                  value={supplierForm.address}
                  onChange={(e) => handleSupplierInputChange("address", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Téléphone *</label>
                <input
                  type="tel"
                  value={supplierForm.phone}
                  onChange={(e) => handleSupplierInputChange("phone", e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold block mb-1">Email</label>
                <input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => handleSupplierInputChange("email", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Mode de paiement</label>
                <select
                  value={supplierForm.payment_mode}
                  onChange={(e) => handleSupplierInputChange("payment_mode", e.target.value)}
                  className={inputClasses}
                >
                  {paymentModeOptions.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold block mb-1">N° de compte / IBAN</label>
                <input
                  type="text"
                  value={supplierForm.account_number}
                  onChange={(e) => handleSupplierInputChange("account_number", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">N° contribuable</label>
                <input
                  type="text"
                  value={supplierForm.tax_number}
                  onChange={(e) => handleSupplierInputChange("tax_number", e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1">Descriptif</label>
              <textarea
                value={supplierForm.description}
                onChange={(e) => handleSupplierInputChange("description", e.target.value)}
                rows={3}
                className={textareaClasses}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold block mb-1">Délai de paiement (jours)</label>
                <input
                  type="number"
                  min="0"
                  value={supplierForm.payment_terms}
                  onChange={(e) => handleSupplierInputChange("payment_terms", e.target.value)}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1">Satisfaction (1-5)</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={supplierForm.rating}
                  onChange={(e) => handleSupplierInputChange("rating", e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={supplierForm.is_active}
                onChange={(e) => handleSupplierInputChange("is_active", e.target.checked)}
                className="h-4 w-4 rounded border-white/20 text-emerald-400 focus:ring-emerald-400/40"
              />
              Activer ce fournisseur
            </label>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={closeSupplierModal}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode
                    ? "border border-white/10 text-white/80"
                    : "border border-slate-200 text-slate-600"
                }`}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSavingSupplier}
                className="rounded-2xl bg-gradient-to-r from-emerald-400 to-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              >
                {isSavingSupplier ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderProductSupplierModal = () => {
    if (!productSupplierTarget) return null;
    const suppliers = suppliersState.data || [];
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div
          className={`w-full max-w-2xl rounded-3xl border ${
            isDarkMode ? "bg-[#0f172a] border-white/10 text-white" : "bg-white border-slate-200"
          }`}
        >
          <div
            className={`rounded-t-3xl px-6 py-4 flex items-center justify-between ${
              isDarkMode ? "bg-gradient-to-r from-amber-500/10 to-orange-500/10" : "bg-amber-50"
            }`}
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Catalogue boutique</p>
              <h3 className="text-xl font-semibold mt-1">
                Lier des fournisseurs
              </h3>
              <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
                {productSupplierTarget.name}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowProductSupplierModal(false)}
              className={`rounded-full p-2 ${
                isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {productSupplierLoading ? (
              <p className="text-sm text-slate-400">Chargement des fournisseurs…</p>
            ) : suppliers.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-6 text-center">
                <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-600"}`}>
                  Aucun fournisseur enregistré pour le moment.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowProductSupplierModal(false);
                    setActiveTab("suppliers");
                  }}
                  className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-400 to-lime-400 px-4 py-2 text-sm font-semibold text-slate-900"
                >
                  Ajouter un fournisseur
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {suppliers.map((supplier) => {
                  const isSelected = productSupplierSelection.includes(String(supplier.id));
                  return (
                    <button
                      key={supplier.id}
                      type="button"
                      onClick={() => toggleProductSupplierSelection(String(supplier.id))}
                      className={`flex items-center justify-between rounded-2xl px-4 py-3 text-left border transition ${
                        isSelected
                          ? "border-amber-400 bg-amber-400/10 text-white"
                          : isDarkMode
                            ? "border-white/10 bg-white/5 text-white/70"
                            : "border-slate-200 bg-white text-slate-700"
                      }`}
                    >
                      <div>
                        <p className="font-semibold">{supplier.name}</p>
                        <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                          {supplier.city || supplier.country || "Fournisseur"}
                        </p>
                      </div>
                      <div
                        className={`h-6 w-6 rounded-full border flex items-center justify-center ${
                          isSelected ? "border-amber-300 bg-amber-400 text-slate-900" : "border-white/20"
                        }`}
                      >
                        {isSelected ? "✓" : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
            <button
              type="button"
              onClick={() => setShowProductSupplierModal(false)}
              className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                isDarkMode ? "border border-white/10 text-white/80" : "border border-slate-200 text-slate-600"
              }`}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSaveProductSuppliers}
              disabled={productSupplierSaving || productSupplierLoading}
              className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
            >
              {productSupplierSaving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? "bg-gradient-to-b from-[#01030b] via-[#050c1d] to-[#01030b] text-slate-100"
          : "bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-300">
              Module Waise
            </p>
            <h1 className="text-3xl font-bold">Console de stock</h1>
            {dashboard?.serviceDay?.label && (
              <p className={isDarkMode ? "text-white/60" : "text-slate-500"}>
                Journée de service : {dashboard.serviceDay.label}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {error && <span className="text-sm text-rose-400">{error}</span>}
            <button
              type="button"
              onClick={fetchDashboard}
              disabled={loading || isRefreshingDashboard}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${
                  loading || isRefreshingDashboard ? "animate-spin" : ""
                }`}
              />
              Actualiser
            </button>
          </div>
        </header>

        <nav className="flex flex-wrap gap-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg"
                  : isDarkMode
                    ? "text-white/70 bg-white/5"
                    : "text-slate-600 bg-white"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>

        {loading ? (
          <div className={`${cardBg(isDarkMode)} rounded-3xl p-10 text-center`}>
            <div className="mx-auto h-12 w-12 border-4 border-amber-400 border-b-transparent rounded-full animate-spin" />
            <p className="mt-4 text-sm text-slate-500">
              Chargement des informations d'inventaire…
            </p>
          </div>
        ) : (
          <div>
            {activeTab === "overview" && renderOverview()}
            {activeTab === "inventory" && renderInventory()}
            {activeTab === "consumption" && renderConsumption()}
            {activeTab === "entries" && renderEntries()}
            {activeTab === "suppliers" && renderSuppliers()}
            {activeTab === "warehouses" && renderWarehouses()}
            {activeTab === "settings" && renderSettings()}
          </div>
        )}
      </div>
      {showStockItemModal && renderStockItemModal()}
      {showWarehouseModal && renderWarehouseModal()}
      {showPurchaseOrderModal && renderPurchaseOrderModal()}
      {showStorageModal && renderStorageModal()}
      {renderPurchasePriceConfirmation()}
      {showSimulatorModal && renderSimulatorModal()}
      {showRecipeModal && renderRecipeModal()}
      {showSupplierModal && renderSupplierModal()}
      {showProductSupplierModal && renderProductSupplierModal()}
    </div>
  );
};

const cardBg = (isDark) =>
  isDark ? "bg-[#0b162c] border border-white/10" : "bg-white border border-slate-100";

const SummaryCard = ({ label, value, subValue, caption, icon: Icon, isDarkMode }) => (
  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 shadow-lg`}>
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
          {label}
        </p>
        <div className="mt-1 flex items-end gap-2">
          <p className="text-2xl font-bold">{value}</p>
          {subValue ? (
            <p className={`text-sm font-medium ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>
              {subValue}
            </p>
          ) : null}
        </div>
      </div>
      <div className={`p-3 rounded-2xl ${isDarkMode ? "bg-white/5" : "bg-slate-100"}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <p className={`text-xs ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>{caption}</p>
  </div>
);

const MetricCard = ({
  label,
  value,
  helper,
  subHelper,
  badge,
  badgeTone = "default",
  rawValue = null,
  negativeTone = false,
  isDarkMode,
}) => {
  const badgeClass =
    badgeTone === "warning"
      ? "bg-amber-500/15 text-amber-300"
      : badgeTone === "success"
        ? "bg-emerald-500/15 text-emerald-300"
        : isDarkMode
          ? "bg-white/10 text-white/80"
          : "bg-slate-100 text-slate-700";
  const isNegative = negativeTone && Number(rawValue) < 0;
  const valueClass = isNegative ? "text-amber-300" : "";

  return (
    <div
      className={`rounded-2xl border p-4 ${
        isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      }`}
    >
      <p className={`text-xs ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valueClass}`}>{value}</p>
      {helper ? (
        <p className={`text-xs mt-1 ${isDarkMode ? "text-white/75" : "text-slate-600"}`}>
          {helper}
        </p>
      ) : null}
      {subHelper ? (
        <p className={`text-xs mt-1 ${isDarkMode ? "text-white/55" : "text-slate-500"}`}>
          {subHelper}
        </p>
      ) : null}
      {badge ? (
        <span className={`inline-flex mt-3 rounded-full px-3 py-1 text-xs font-semibold ${badgeClass}`}>
          {badge}
        </span>
      ) : null}
    </div>
  );
};

const PeriodFilterControl = ({
  value,
  onChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  isDarkMode,
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className={`text-sm ${isDarkMode ? "text-white/70" : "text-slate-500"}`}>Période:</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`rounded-2xl border px-3 py-2 text-sm font-semibold ${
        isDarkMode ? "bg-white/5 border-white/15 text-white" : "bg-white border-slate-200 text-slate-700"
      }`}
    >
      {OVERVIEW_PERIOD_OPTIONS.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
    {value === "custom" ? (
      <>
        <input
          type="date"
          value={startDate}
          onChange={(event) => onStartDateChange(event.target.value)}
          className={`rounded-2xl border px-3 py-2 text-sm ${
            isDarkMode ? "bg-white/5 border-white/15 text-white" : "bg-white border-slate-200 text-slate-700"
          }`}
        />
        <input
          type="date"
          value={endDate}
          onChange={(event) => onEndDateChange(event.target.value)}
          className={`rounded-2xl border px-3 py-2 text-sm ${
            isDarkMode ? "bg-white/5 border-white/15 text-white" : "bg-white border-slate-200 text-slate-700"
          }`}
        />
      </>
    ) : null}
  </div>
);

const TabStatCard = ({ icon: Icon, label, value, helper, isDarkMode }) => (
  <div
    className={`rounded-2xl border p-4 ${
      isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`rounded-2xl p-3 ${isDarkMode ? "bg-white/5" : "bg-slate-100"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{label}</p>
        <p className="text-xl font-semibold">{value}</p>
        {helper && (
          <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>{helper}</p>
        )}
      </div>
    </div>
  </div>
);

const WarehouseCard = ({ warehouse, isDarkMode, onEdit, onDelete }) => {
  const typeLabels = {
    primary: "Site principal",
    secondary: "Dépôt secondaire",
    cold_storage: "Chambre froide",
    dry_storage: "Zone sèche",
    freezer: "Congélateur",
  };

  const typeLabel = typeLabels[warehouse?.type] || "Entrepôt";
  const hasTemperature =
    warehouse?.temperature_min !== null &&
    warehouse?.temperature_min !== undefined &&
    warehouse?.temperature_max !== null &&
    warehouse?.temperature_max !== undefined;
  const hasHumidity =
    warehouse?.humidity_min !== null &&
    warehouse?.humidity_min !== undefined &&
    warehouse?.humidity_max !== null &&
    warehouse?.humidity_max !== undefined;

  const itemsManaged = warehouse.items_managed ?? 0;
  const stockValue = warehouse.stock_value ?? 0;
  const normalizedWarehouseName = String(warehouse?.name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
  const isMainWarehouse =
    warehouse?.is_main_pos_store === 1 ||
    warehouse?.type === "primary" ||
    normalizedWarehouseName === "entrepot principal";

  return (
    <div
      className={`rounded-2xl border p-5 ${
        isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      } space-y-3`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{typeLabel}</p>
          <h3 className="text-lg font-semibold">{warehouse.name}</h3>
          {warehouse.location && (
            <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"} flex items-center gap-1`}>
              <MapPin className="w-3 h-3" />
              {warehouse.location}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              warehouse.is_active
                ? "bg-emerald-500/15 text-emerald-300"
                : "bg-rose-500/20 text-rose-200"
            }`}
          >
            {warehouse.is_active ? "Actif" : "Inactif"}
          </span>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
            title="Modifier cet entrepôt"
          >
            <Edit size={16} />
          </button>
          {!isMainWarehouse && (
            <button
              type="button"
              onClick={onDelete}
              className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
              title="Supprimer cet entrepot"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
      {warehouse.manager_name && (
        <p className="text-sm text-slate-500">
          Responsable : <span className="font-semibold">{warehouse.manager_name}</span>
        </p>
      )}
      <div className="flex flex-wrap gap-2 text-xs">
        <span
          className={`px-3 py-1 rounded-full border ${
            isDarkMode ? "border-white/10 text-white/70" : "border-slate-200 text-slate-600"
          }`}
        >
          Code : {warehouse.code || "Non défini"}
        </span>
        <span
          className={`px-3 py-1 rounded-full border ${
            isDarkMode ? "border-white/10 text-white/70" : "border-slate-200 text-slate-600"
          }`}
        >
          Type : {typeLabel}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Articles gérés</p>
          <p className="font-semibold">{numberFormatter.format(itemsManaged)}</p>
        </div>
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Valeur du stock</p>
          <p className="font-semibold">{preciseCurrencyFormatter.format(stockValue)}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs text-slate-500">Capacité volume</p>
          <p className="font-semibold">
            {warehouse?.capacity_volume
              ? `${numberFormatter.format(Number(warehouse.capacity_volume))} m³`
              : "—"}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Capacité poids</p>
          <p className="font-semibold">
            {warehouse?.capacity_weight
              ? `${numberFormatter.format(Number(warehouse.capacity_weight))} kg`
              : "—"}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        {hasTemperature && (
          <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-1">
            <ThermometerSun className="w-3 h-3" />
            {warehouse.temperature_min}°C → {warehouse.temperature_max}°C
          </span>
        )}
        {hasHumidity && (
          <span className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-1">
            <Droplets className="w-3 h-3" />
            {warehouse.humidity_min}% → {warehouse.humidity_max}%
          </span>
        )}
      </div>
    </div>
  );
};

const ResoldProductCard = ({
  entry,
  stockItem,
  suppliers,
  warehouses,
  isDarkMode,
  onLinkSuppliers,
  onSaved,
}) => {
  const buildDraftRow = (row = {}, index = 0) => {
    const knownOption = PACKAGING_UNIT_OPTIONS.find(
      (option) =>
        option.value === row.packaging_unit ||
        option.label.toLowerCase() === String(row.packaging_unit || "").toLowerCase(),
    );
    const isOther = row.packaging_unit && !knownOption;
    return {
      localId: row.id || `new-${Date.now()}-${index}`,
      id: row.id || null,
      unitValue: isOther ? "other" : knownOption?.value || "carton",
      customUnit: isOther ? row.packaging_unit : "",
      packagingQty: String(row.packaging_qty ?? 1),
      purchasePrice:
        row.purchase_price !== null && row.purchase_price !== undefined
          ? String(row.purchase_price)
          : "",
      supplierId: row.supplier_id ? String(row.supplier_id) : "",
      isDefault: Boolean(row.is_default),
    };
  };

  const initialPackaging = Array.isArray(stockItem?.packaging) ? stockItem.packaging : [];
  const [draftRows, setDraftRows] = useState(() =>
    initialPackaging.length ? initialPackaging.map(buildDraftRow) : [],
  );
  const [alertThreshold, setAlertThreshold] = useState(
    stockItem?.reorder_point ?? stockItem?.minimum_stock ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [warehouseLinkOpen, setWarehouseLinkOpen] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(
    stockItem?.warehouse_id ? String(stockItem.warehouse_id) : "",
  );
  const [savingWarehouse, setSavingWarehouse] = useState(false);

  useEffect(() => {
    const nextPackaging = Array.isArray(stockItem?.packaging) ? stockItem.packaging : [];
    setDraftRows(nextPackaging.length ? nextPackaging.map(buildDraftRow) : []);
    setAlertThreshold(stockItem?.reorder_point ?? stockItem?.minimum_stock ?? "");
    setSelectedWarehouseId(stockItem?.warehouse_id ? String(stockItem.warehouse_id) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockItem?.id, stockItem?.updated_at, JSON.stringify(stockItem?.packaging || [])]);

  const handleLinkWarehouse = async () => {
    if (!selectedWarehouseId) {
      showToast("error", "Selectionnez un entrepot.");
      return;
    }
    setSavingWarehouse(true);
    try {
      let stockItemId = stockItem?.id;
      if (!stockItemId) {
        const ensureResponse = await fetch("/api/waise/stock-items/ensure-catalog-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_id: entry.productId,
            variation_id: entry.variationId,
            inventory_kind: "resold",
          }),
        });
        if (!ensureResponse.ok) {
          const error = await ensureResponse.json().catch(() => ({}));
          throw new Error(error.error || "Impossible de creer l'article Waise lie.");
        }
        const ensured = await ensureResponse.json();
        stockItemId = ensured.stock_item_id || ensured.id;
      }
      if (!stockItemId) {
        throw new Error("Article Waise introuvable pour ce produit.");
      }
      const response = await fetch(`/api/waise/stock-items/${stockItemId}/warehouse`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ warehouse_id: Number(selectedWarehouseId) }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de lier l'entrepot.");
      }
      showToast("success", "Entrepot lie au produit.");
      setWarehouseLinkOpen(false);
      await onSaved?.();
    } catch (error) {
      console.error("[waise] Erreur liaison entrepot:", error);
      showToast("error", error.message || "Erreur lors de la liaison de l'entrepot.");
    } finally {
      setSavingWarehouse(false);
    }
  };

  const unitLabel = stockItem?.unit_symbol || stockItem?.unit_name || "pièce";
  const currentStock = Number(
    stockItem?.storage_stock_quantity ??
      stockItem?.stock_level_quantity ??
      stockItem?.purchase_stock_quantity ??
      0,
  );
  const weightedCost = Number(stockItem?.weighted_avg_cost ?? stockItem?.cost_price ?? 0);

  const setRowValue = (localId, field, value) => {
    setDraftRows((prev) =>
      prev.map((row) => (row.localId === localId ? { ...row, [field]: value } : row)),
    );
  };

  const addPackagingRow = () => {
    setDraftRows((prev) => [
      ...prev,
      {
        localId: `new-${Date.now()}`,
        id: null,
        unitValue: "carton",
        customUnit: "",
        packagingQty: "1",
        purchasePrice: "",
        supplierId: "",
        isDefault: prev.length === 0,
      },
    ]);
  };

  const removePackagingRow = (localId) => {
    setDraftRows((prev) => {
      const next = prev.filter((row) => row.localId !== localId);
      if (next.length && !next.some((row) => row.isDefault)) {
        next[0] = { ...next[0], isDefault: true };
      }
      return next;
    });
  };

  const setDefaultPackaging = (localId) => {
    setDraftRows((prev) =>
      prev.map((row) => ({ ...row, isDefault: row.localId === localId })),
    );
  };

  const parseDraftNumber = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const normalizeRowsForSave = () =>
    draftRows.map((row) => {
      const option = PACKAGING_UNIT_OPTIONS.find((item) => item.value === row.unitValue);
      const packagingUnit =
        row.unitValue === "other" ? row.customUnit.trim() : option?.label || row.unitValue;
      return {
        packaging_unit: packagingUnit,
        packaging_qty: parseDraftNumber(row.packagingQty),
        purchase_price: parseDraftNumber(row.purchasePrice),
        supplier_id: row.supplierId ? Number(row.supplierId) : null,
        is_default: row.isDefault ? 1 : 0,
      };
    });

  const handleSave = async () => {
    if (!stockItem?.id) {
      showToast("error", "Article Waise introuvable pour ce produit.");
      return;
    }
    if (draftRows.length === 0) {
      showToast("error", "Au moins un conditionnement est requis.");
      return;
    }
    const rows = normalizeRowsForSave();
    for (const row of rows) {
      if (!row.packaging_unit) {
        showToast("error", "Renseignez l'unité du conditionnement.");
        return;
      }
      if (!Number.isFinite(row.packaging_qty) || row.packaging_qty <= 0) {
        showToast("error", "La quantité par unité doit être supérieure à 0.");
        return;
      }
      if (
        row.purchase_price !== null &&
        (!Number.isFinite(row.purchase_price) || row.purchase_price <= 0)
      ) {
        showToast("error", "Le prix d'achat par unité doit être supérieur à 0.");
        return;
      }
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/waise/stock-items/${stockItem.id}/packaging`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          alert_threshold: alertThreshold,
          packaging: rows,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible d'enregistrer le conditionnement.");
      }
      showToast("success", "Conditionnements enregistrés.");
      await onSaved?.();
    } catch (error) {
      console.error("❌ Erreur conditionnements:", error);
      showToast("error", error.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border p-4 space-y-4 ${
        isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{entry.category}</p>
          <h4 className="text-lg font-semibold">{entry.name}</h4>
          <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
            {entry.destination === "bar"
              ? "Bar"
              : entry.destination === "cuisine"
                ? "Cuisine"
                : entry.destination === "none" || !entry.destination
                  ? "Aucune destination"
                  : entry.destination_name || entry.destination}
            {entry.hasVariations ? " · Variable" : " · Simple"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onLinkSuppliers}
            className={`rounded-full p-2 transition ${
              isDarkMode
                ? "border border-white/10 text-white/80 hover:border-amber-400/60"
                : "border border-slate-200 text-slate-600 hover:border-amber-500/60"
            }`}
            title="Lier à des fournisseurs"
          >
            <Link2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => setWarehouseLinkOpen((prev) => !prev)}
            className={`rounded-full p-2 transition ${
              isDarkMode
                ? "border border-white/10 text-white/80 hover:border-amber-400/60"
                : "border border-slate-200 text-slate-600 hover:border-amber-500/60"
            }`}
            title="Lier à un entrepôt"
          >
            <Warehouse size={16} />
          </button>
        </div>
      </div>

      <div className={`rounded-2xl p-3 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={`text-xs uppercase tracking-[0.2em] ${isDarkMode ? "text-white/40" : "text-slate-400"}`}>
              Entrepôt de suivi
            </p>
            <p className="text-sm font-semibold">
              {stockItem?.warehouse_name || "Aucun entrepôt lié"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setWarehouseLinkOpen((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
            disabled={false}
          >
            <Warehouse size={16} />
            {stockItem?.warehouse_name ? "Changer l'entrepôt" : "Lier à un entrepôt"}
          </button>
        </div>
      </div>

      {warehouseLinkOpen && (
        <div className={`rounded-2xl p-3 space-y-3 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">Entrepôt</p>
            <span className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
              Actuel : {stockItem?.warehouse_name || "Non défini"}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
              className={`rounded-xl px-3 py-2 border text-sm ${
                isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
              }`}
            >
              <option value="">Sélectionner un entrepôt…</option>
              {(warehouses || []).map((warehouse) => (
                <option key={warehouse.id} value={String(warehouse.id)}>
                  {warehouse.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleLinkWarehouse}
              disabled={savingWarehouse || !selectedWarehouseId}
              className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
            >
              {savingWarehouse ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button
              type="button"
              onClick={() => setWarehouseLinkOpen(false)}
              className={`rounded-xl px-3 py-2 text-sm ${
                isDarkMode ? "text-white/60 hover:text-white" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className={`rounded-2xl p-3 space-y-3 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold">Stock & coût</p>
          <span className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
            Lecture Waise
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>CMUP</p>
            <p className="font-semibold">{currencyFormatter.format(weightedCost)}</p>
          </div>
          <div>
            <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Stock actuel</p>
            <p className="font-semibold">
              {decimalFormatter.format(currentStock)} {unitLabel}
            </p>
          </div>
          <div>
            <label className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
              Seuil d'alerte
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={alertThreshold ?? ""}
              onChange={(e) => setAlertThreshold(e.target.value.replace(/[^0-9.,]/g, ""))}
              className={`mt-1 w-full rounded-xl px-3 py-2 border text-sm ${
                isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
              }`}
              placeholder="0"
            />
          </div>
        </div>
      </div>

      <div className={`rounded-2xl p-3 space-y-3 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Conditionnement</p>
            <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
              Fournisseurs liés: {(entry.supplierIds || []).length}
            </p>
          </div>
          <button
            type="button"
            onClick={addPackagingRow}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold bg-amber-400/20 text-amber-300"
          >
            <Plus className="w-3.5 h-3.5" />
            Ajouter un conditionnement
          </button>
        </div>

        {draftRows.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
            Aucun conditionnement défini.
          </p>
        ) : (
          <div className="space-y-3">
            {draftRows.map((row) => {
              const qty = parseDraftNumber(row.packagingQty) || 0;
              const price = parseDraftNumber(row.purchasePrice);
              const piecePrice = qty > 0 && Number.isFinite(price) ? price / qty : null;
              return (
                <div
                  key={row.localId}
                  className={`rounded-xl border p-3 space-y-3 ${
                    isDarkMode ? "border-white/10 bg-black/10" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setDefaultPackaging(row.localId)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                        row.isDefault
                          ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200"
                          : isDarkMode
                            ? "border-white/10 text-white/60"
                            : "border-slate-200 text-slate-500"
                      }`}
                    >
                      {row.isDefault ? "Par défaut" : "Définir par défaut"}
                    </button>
                    <button
                      type="button"
                      onClick={() => removePackagingRow(row.localId)}
                      className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
                      title="Supprimer ce conditionnement"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="text-xs font-semibold">
                      Unité
                      <select
                        value={row.unitValue}
                        onChange={(e) => setRowValue(row.localId, "unitValue", e.target.value)}
                        className={`mt-1 w-full rounded-xl px-3 py-2 border text-sm ${
                          isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                        }`}
                      >
                        {PACKAGING_UNIT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {row.unitValue === "other" && (
                      <label className="text-xs font-semibold">
                        Unité libre
                        <input
                          type="text"
                          value={row.customUnit}
                          onChange={(e) => setRowValue(row.localId, "customUnit", e.target.value)}
                          className={`mt-1 w-full rounded-xl px-3 py-2 border text-sm ${
                            isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                          }`}
                          placeholder="Ex: Bac"
                        />
                      </label>
                    )}
                    <label className="text-xs font-semibold">
                      Qté par unité
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={row.packagingQty}
                        onChange={(e) =>
                          setRowValue(row.localId, "packagingQty", e.target.value.replace(/[^0-9]/g, ""))
                        }
                        className={`mt-1 w-full rounded-xl px-3 py-2 border text-sm ${
                          isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                        }`}
                      />
                    </label>
                    <label className="text-xs font-semibold">
                      Prix d'achat par unité
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={row.purchasePrice}
                        onChange={(e) =>
                          setRowValue(row.localId, "purchasePrice", e.target.value.replace(/[^0-9.,]/g, ""))
                        }
                        className={`mt-1 w-full rounded-xl px-3 py-2 border text-sm ${
                          isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                        }`}
                        placeholder="0"
                      />
                      <span className={`mt-1 block ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
                        {piecePrice !== null ? `= ${currencyFormatter.format(piecePrice)} / pièce` : "= — / pièce"}
                      </span>
                    </label>
                    <label className="text-xs font-semibold">
                      Fournisseur associé
                      <select
                        value={row.supplierId}
                        onChange={(e) => setRowValue(row.localId, "supplierId", e.target.value)}
                        className={`mt-1 w-full rounded-xl px-3 py-2 border text-sm ${
                          isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"
                        }`}
                      >
                        <option value="">Aucun</option>
                        {(suppliers || []).map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name || supplier.company_name || `Fournisseur ${supplier.id}`}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !stockItem?.id}
            className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

const StockItemCard = ({ item, isDarkMode, onEdit }) => {
  const unitLabel = item.unit_symbol || item.unit_name || "";
  const storageUnitLabel = item.storage_unit_symbol || unitLabel || "";
  const consumptionUnitLabel = item.consumption_unit_symbol || unitLabel || "";
  const categoryLabel = item.category || (item.type === "consumable" ? "Consommable" : "Ingrédient");
  const description = item.description?.trim();
  const minStock = item.minimum_stock !== null && item.minimum_stock !== undefined ? item.minimum_stock : null;
  const reorderPoint = item.reorder_point !== null && item.reorder_point !== undefined ? item.reorder_point : null;
  const reorderQuantity =
    item.reorder_quantity !== null && item.reorder_quantity !== undefined ? item.reorder_quantity : null;
  const costValue = item.cost_price !== null && item.cost_price !== undefined ? item.cost_price : null;
  // purchase_price_per_unit = prix de référence saisi sur le bon de commande (avant CMUP)
  const purchaseRefPrice = item.purchase_price_per_unit !== null && item.purchase_price_per_unit !== undefined
    ? Number(item.purchase_price_per_unit)
    : null;
  const purchaseStock =
    item.purchase_stock_quantity !== null && item.purchase_stock_quantity !== undefined
      ? Number(item.purchase_stock_quantity)
      : 0;
  const storageStock =
    item.storage_stock_quantity !== null && item.storage_stock_quantity !== undefined
      ? Number(item.storage_stock_quantity)
      : 0;

  return (
    <div
      className={`rounded-2xl border p-5 space-y-4 ${
        isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{categoryLabel}</p>
          <h4 className="text-lg font-semibold">{item.name}</h4>
          {description && (
            <p className={`text-sm ${isDarkMode ? "text-white/60" : "text-slate-500"} line-clamp-2`}>
              {description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
          title="Modifier cet article"
        >
          <Edit size={16} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span
          className={`px-3 py-1 rounded-full border ${
            isDarkMode ? "border-white/10 text-white/70" : "border-slate-200 text-slate-600"
          }`}
        >
          Unité : {unitLabel || "—"}
        </span>
        {item.sku && (
          <span
            className={`px-3 py-1 rounded-full border ${
              isDarkMode ? "border-white/10 text-white/70" : "border-slate-200 text-slate-600"
            }`}
          >
            SKU {item.sku}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Stock min.</p>
          <p className="font-semibold">
            {minStock !== null ? `${numberFormatter.format(minStock)} ${unitLabel}` : "—"}
          </p>
        </div>
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Seuil alerte</p>
          <p className="font-semibold">
            {reorderPoint !== null ? `${numberFormatter.format(reorderPoint)} ${unitLabel}` : "—"}
          </p>
        </div>
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Réappro.</p>
          <p className="font-semibold">
            {reorderQuantity !== null ? `${numberFormatter.format(reorderQuantity)} ${unitLabel}` : "—"}
          </p>
        </div>
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>CMUP</p>
          <p className="font-semibold">
            {costValue !== null ? currencyFormatter.format(costValue) : "Prix non renseigné"}
          </p>
          {purchaseRefPrice !== null && purchaseRefPrice !== costValue && (
            <p className={`text-xs mt-0.5 ${isDarkMode ? "text-white/40" : "text-slate-400"}`}>
              Réf. : {currencyFormatter.format(purchaseRefPrice)}
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        {[
          { label: "En commande", value: purchaseStock, unit: unitLabel },
          { label: "En stock", value: storageStock, unit: storageUnitLabel || unitLabel },
          { label: "Consommé (30j)", value: Number(item.consumption_quantity) || 0, unit: storageUnitLabel || unitLabel },
          {
            label: "Valeur",
            value: storageStock * (costValue !== null ? Number(costValue) : 0),
            unit: "",
            isCurrency: true,
          },
        ].map((stockOverview) => (
          <div
            key={stockOverview.label}
            className={`rounded-xl px-3 py-2 ${isDarkMode ? "bg-white/5" : "bg-slate-50"}`}
          >
            <p className={isDarkMode ? "text-white/50" : "text-slate-500"}>{stockOverview.label}</p>
            <p className="font-semibold">
              {stockOverview.isCurrency
                ? currencyFormatter.format(stockOverview.value || 0)
                : `${numberFormatter.format(stockOverview.value || 0)} ${stockOverview.unit || ""}`}
            </p>
          </div>
        ))}
      </div>
      <div className="space-y-1 text-xs">
        <p className={isDarkMode ? "text-white/60" : "text-slate-500"}>
          Fournisseur : {item.supplier_name || "Non renseigné"}
        </p>
        <p className={isDarkMode ? "text-white/60" : "text-slate-500"}>
          Entrepôt : {item.warehouse_name || "Non défini"}
        </p>
      </div>
    </div>
  );
};

const SupplierCard = ({ supplier, isDarkMode, onEdit, onToggle, onDelete }) => {
  const isCompany =
    supplier?.entity_type === "company" || (supplier?.company_name && !supplier?.first_name);
  const typeLabel =
    supplier.supplier_category === "ingredient"
      ? "Ingrédients"
      : supplier.supplier_category === "consumable"
        ? "Consommables"
        : "Autre";
  const paymentLabel = PAYMENT_MODE_LABELS[supplier.payment_mode] || "—";
  const statusClasses = supplier.is_active
    ? "bg-emerald-400/20 text-emerald-200"
    : "bg-rose-500/20 text-rose-200";
  const pillClasses = `px-3 py-1 rounded-full text-xs font-semibold ${
    isDarkMode ? "bg-white/10 text-white/80" : "bg-slate-100 text-slate-600"
  }`;
  const infoRows = [
    supplier.address && {
      icon: MapPin,
      label: "Adresse",
      value: supplier.address,
    },
    supplier.phone && { icon: Phone, label: "Téléphone", value: supplier.phone },
    supplier.email && { icon: Mail, label: "Email", value: supplier.email },
  ].filter(Boolean);

  const accountLabel = supplier.account_number || supplier.bank_details || "—";
  const paymentTerms = supplier.payment_terms ? `${supplier.payment_terms} j` : "—";
  const contactName =
    supplier.manager_name || supplier.contact_person || `${supplier.first_name || ""} ${supplier.last_name || ""}`.trim();

  return (
    <div
      className={`rounded-3xl border p-5 space-y-4 ${
        isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
            {supplier.city || supplier.country || "Fournisseur"}
          </p>
          <h3 className="text-lg font-semibold">
            {isCompany
              ? supplier.company_name || supplier.name
              : supplier.name || `${supplier.first_name || ""} ${supplier.last_name || ""}`.trim() || "Fournisseur"}
          </h3>
          <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
            {typeLabel} • {isCompany ? "Entreprise" : "Responsable"}
            {contactName ? ` · ${contactName}` : ""}
          </p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClasses}`}>
          {supplier.is_active ? "Actif" : "Inactif"}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className={pillClasses}>{isCompany ? "Entreprise" : "Personne"}</span>
        <span className={pillClasses}>{typeLabel}</span>
        <span className={pillClasses}>{paymentLabel}</span>
        {supplier.tax_number && <span className={pillClasses}>{supplier.tax_number}</span>}
        {supplier.city && (
          <span className={pillClasses}>{supplier.city}</span>
        )}
      </div>

      {infoRows.length ? (
        <div className="grid gap-3 text-sm">
          {infoRows.map((item) => (
            <p
              key={item.label}
              className={`flex items-center gap-2 ${isDarkMode ? "text-white/70" : "text-slate-600"}`}
            >
              <item.icon className="w-4 h-4 text-amber-300" />
              <span className={isDarkMode ? "font-semibold text-white/80" : "font-semibold text-slate-600"}>
                {item.label}:
              </span>
              <span className="text-sm font-normal">{item.value}</span>
            </p>
          ))}
        </div>
      ) : null}

      {supplier.description && (
        <p className={`text-xs ${isDarkMode ? "text-white/60" : "text-slate-500"}`}>
          {supplier.description}
        </p>
      )}

      <div
        className={`grid gap-3 border-t pt-3 text-sm sm:grid-cols-3 ${
          isDarkMode ? "border-white/10" : "border-slate-200"
        }`}
      >
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
            Mode de paiement
          </p>
          <p className="font-semibold">{paymentLabel}</p>
        </div>
        <div>
          <p className={`flex items-center gap-1 text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>
            <CreditCard className="w-3 h-3" />
            Compte
          </p>
          <p className="font-semibold break-all">{accountLabel}</p>
        </div>
        <div>
          <p className={`text-xs ${isDarkMode ? "text-white/50" : "text-slate-500"}`}>Délai</p>
          <p className="font-semibold">{paymentTerms}</p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
          title="Modifier ce fournisseur"
        >
          <Edit size={16} />
        </button>
        <button
          type="button"
          onClick={onToggle}
          className={`rounded-full border border-white/10 p-2 ${
            supplier.is_active
              ? "text-emerald-300 hover:border-emerald-400/60"
              : "text-amber-300 hover:border-amber-400/60"
          }`}
          title={supplier.is_active ? "Désactiver ce fournisseur" : "Activer ce fournisseur"}
        >
          <Power size={16} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
          title="Supprimer ce fournisseur"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const AlertsPanel = ({ alerts, isDarkMode }) => {
  const cards = [
    ...(alerts.criticalStock || []).map((alert) => ({
      title: alert.item_name,
      subtitle: alert.warehouse_name,
      value: `${numberFormatter.format(alert.quantity)} ${alert.unit_symbol || ""}`,
      accent: "from-rose-500/20 to-red-500/20",
      icon: AlertTriangle,
    })),
    ...(alerts.expiringItems || []).map((alert) => ({
      title: alert.item_name,
      subtitle: `Expire dans ${Math.round(alert.days_until_expiry)} j`,
      value: `${numberFormatter.format(alert.remaining_quantity || 0)} ${alert.unit_symbol || ""}`,
      accent: "from-yellow-400/20 to-amber-400/20",
      icon: ShieldAlert,
    })),
  ].slice(0, 6);

  if (!cards.length) {
    return null;
  }

  return (
    <section className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4`}>
      {cards.map((card, index) => (
        <div
          key={`${card.title}-${index}`}
          className={`rounded-2xl border bg-gradient-to-r ${card.accent} ${
            isDarkMode ? "border-white/10" : "border-slate-100"
          } p-4 shadow-inner backdrop-blur`}
        >
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-black/10">
              <card.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold">{card.title}</p>
              <p className="text-xs opacity-80">{card.subtitle}</p>
            </div>
          </div>
          <p className="mt-3 text-lg font-bold">{card.value}</p>
        </div>
      ))}
    </section>
  );
};

const MenuProductRow = ({ product, isDarkMode }) => {
  const statusMap = {
    rupture: { label: "Rupture", color: "bg-rose-500/15 text-rose-400" },
    faible: { label: "Faible", color: "bg-amber-500/15 text-amber-400" },
    ok: { label: "OK", color: "bg-emerald-500/15 text-emerald-400" },
    non_suivi: { label: "Non suivi", color: "bg-slate-500/15 text-slate-400" },
  };
  const badge = statusMap[product.status] || statusMap.non_suivi;
  return (
    <tr className={isDarkMode ? "border-white/10" : "border-slate-100"}>
      <td className="py-3">
        <div className="font-semibold">{product.name}</div>
        <p className="text-xs text-slate-500">{product.category || ""}</p>
      </td>
      <td className="py-3">
        {product.stockQuantity !== null
          ? `${numberFormatter.format(product.stockQuantity)} ${
              product.unitSymbol || ""
            }`
          : "Non suivi"}
      </td>
      <td className="py-3 text-right">
        {numberFormatter.format(product.salesQuantity || 0)}
      </td>
      <td className="py-3 text-right">
        {currencyFormatter.format(product.salesRevenue || 0)}
      </td>
      <td className="py-3">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
          {badge.label}
        </span>
      </td>
    </tr>
  );
};

const ConsumptionPanel = ({ consumption, isDarkMode, expanded = false }) => {
  if (!consumption) {
    return (
      <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg`}>
        <p className="text-sm text-slate-500">Aucune donnée disponible.</p>
      </section>
    );
  }

  return (
    <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
            Consommation
          </p>
          <h2 className="text-lg font-semibold">Impact des ventes</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <ConsumptionCard title="Aujourd'hui" data={consumption.today} />
        <ConsumptionCard title="7 jours" data={consumption.week} />
        <ConsumptionCard title="30 jours" data={consumption.month} />
      </div>
      {expanded && consumption.topItems?.length ? (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
            Articles les plus consommés
          </p>
          {consumption.topItems.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between rounded-2xl px-4 py-3 ${
                isDarkMode ? "bg-white/5" : "bg-white"
              }`}
            >
              <div>
                <p className="font-semibold">{item.name}</p>
                <p className="text-xs text-slate-500">{item.category || ""}</p>
              </div>
              <div className="text-right text-sm">
                <p>
                  {numberFormatter.format(item.quantity || 0)} {item.unitSymbol || ""}
                </p>
                <p className="text-xs text-slate-500">
                  {currencyFormatter.format(item.value || 0)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

const ConsumptionCard = ({ title, data }) => (
  <div className="rounded-2xl bg-gradient-to-br from-slate-900/5 to-slate-900/0 border border-white/10 p-4">
    <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{title}</p>
    <p className="text-2xl font-bold mt-2">
      {currencyFormatter.format(data?.value || 0)}
    </p>
    <p className="text-xs text-slate-500">
      {numberFormatter.format(data?.quantity || 0)} unités
    </p>
  </div>
);

const EntriesPanel = ({ entries, isDarkMode, expanded = false }) => {
  if (!entries) {
    return (
      <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg`}>
        <p className="text-sm text-slate-500">Aucune donnée disponible.</p>
      </section>
    );
  }

  return (
    <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
            Entrées de stock
          </p>
          <h2 className="text-lg font-semibold">Livraisons & bons de commande</h2>
        </div>
        <span className="text-xs text-slate-500">
          {entries.pending} commande(s) en attente
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <ConsumptionCard title="Aujourd'hui" data={entries.today} />
        <ConsumptionCard title="7 jours" data={entries.week} />
        <ConsumptionCard title="30 jours" data={entries.month} />
      </div>
      {expanded && entries.purchaseOrders?.length ? (
        <div className="space-y-2">
          {entries.purchaseOrders.map((order) => (
            <div
              key={order.id}
              className={`rounded-2xl px-4 py-3 flex items-center justify-between text-sm ${
                isDarkMode ? "bg-white/5" : "bg-white"
              }`}
            >
              <div>
                <p className="font-semibold">{order.orderNumber}</p>
                <p className="text-xs text-slate-500">
                  {order.supplierName || "Fournisseur inconnu"}
                </p>
              </div>
              <div className="text-right">
                <p>{currencyFormatter.format(order.totalAmount || 0)}</p>
                <p className="text-xs text-slate-500">
                  {numberFormatter.format(order.quantityReceived || 0)} reçus
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

const FlowTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload.reduce((acc, entry) => {
    acc[entry.dataKey] = entry.value;
    return acc;
  }, {});
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 text-white shadow-xl">
      <p className="text-sm font-semibold mb-2">{label}</p>
      <p className="text-xs">Consommation : {currencyFormatter.format(data.consumptionValue || 0)}</p>
      <p className="text-xs">Entrées : {currencyFormatter.format(data.entriesValue || 0)}</p>
    </div>
  );
};

export default WaiseInterface;
